// @ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import { MessageCircle, Keyboard, BarChart2, Settings, Menu, Mic, FileText, Clock, Calendar, TrendingUp, FolderOpen, Mail, Key, Users, Smartphone, Brain, CheckCircle, User, DollarSign, Bell, Shield, Link, Moon, Sun, Globe, LogOut, ChevronRight, Home, Zap, Edit, Trash2, AlertTriangle, Volume2, Lock, ClipboardList, Eye, Star, XCircle, RefreshCw, Lock as LockIcon, Plus, X, HelpCircle, MessageCircle as MessageCircleIcon, Search, Check, Download, Send, Camera, Copy, Rocket, Target, Server, Laptop, Monitor, Building2, Flag, Inbox, BookOpen, Palette } from "lucide-react";
import { BriefcaseIcon, FaceSmileIcon, ChatBubbleOvalLeftIcon, DocumentTextIcon, HeartIcon, BoltIcon, GlobeAltIcon, MoonIcon } from "@heroicons/react/24/outline";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { api, getToken, getConversations, approveReply, dismissReply, getAutoRespond, setGlobalAutoRespond, setContactAutoRespond, getTemplates, createTemplate, updateTemplate, deleteTemplate, useTemplate, getGroups, createGroup, updateGroup, deleteGroup, listGroupMembers, addGroupMember, removeGroupMember, evaluateGroup, listContacts, getAway, setAway } from "./services/api";

const DARK_COLORS = { primary:"#0A84FF", success:"#30D158", danger:"#FF453A", warning:"#FF9F0A", bg:"#000000", card:"#1C1C1E", text:"#FFFFFF", subtext:"#8E8E93", border:"#38383A" };
const LIGHT_COLORS = { primary:"#007AFF", success:"#34C759", danger:"#FF3B30", warning:"#FF9500", bg:"#F2F2F7", card:"#FFFFFF", text:"#000000", subtext:"#8E8E93", border:"#E5E5EA" };
const isDark = () => { try { return localStorage.getItem("jedichatai_dark_mode") === "true"; } catch(e) { return false; } };
let COLORS = isDark() ? DARK_COLORS : LIGHT_COLORS;

// Global dark mode event
const DARK_MODE_EVENT = "jedichatai_darkmode_change";
const setDarkModeGlobal = (dark: boolean) => {
  localStorage.setItem("jedichatai_dark_mode", dark ? "true" : "false");
  COLORS = dark ? DARK_COLORS : LIGHT_COLORS;
  window.dispatchEvent(new CustomEvent(DARK_MODE_EVENT, { detail: dark }));
};

// Hook that triggers re-render on dark mode change
const useDarkModeState = () => {
  const [dark, setDark] = React.useState(isDark());
  React.useEffect(() => {
    const handler = (e: any) => { setDark(e.detail); };
    window.addEventListener(DARK_MODE_EVENT, handler);
    return () => window.removeEventListener(DARK_MODE_EVENT, handler);
  }, []);
  return dark;
};

// Apply dark mode to document
// Haptic feedback
const haptic = (duration = 10) => { if ("vibrate" in navigator) navigator.vibrate(duration); };

const applyDarkMode = (dark: boolean) => {
  const colors = dark ? DARK_COLORS : LIGHT_COLORS;
  COLORS = colors;
  try {
    if (document.documentElement) {
      document.documentElement.style.setProperty("--bg", colors.bg);
      document.documentElement.style.setProperty("--card", colors.card);
      document.documentElement.style.setProperty("--text", colors.text);
      document.documentElement.style.setProperty("--subtext", colors.subtext);
      document.documentElement.style.setProperty("--border", colors.border);
      document.documentElement.style.setProperty("--primary", colors.primary);
    }
    if (document.body) {
      document.body.style.background = colors.bg;
      document.body.style.color = colors.text;
    }
    localStorage.setItem("jedichatai_dark_mode", dark ? "true" : "false");
  } catch(e) {}
};
// Initialize on load
// Dark mode applied after DOM ready - see App component useEffect

// Dark mode hook
const useDarkMode = () => {
  const [dark, setDark] = useState(isDark());
  const toggle = () => { const nd = !dark; setDark(nd); applyDarkMode(nd); };
  return [dark, toggle] as const;
};
const EMOTION_COLORS = { anger:"#FF3B30", joy:"#34C759", sadness:"#5856D6", fear:"#FF9500",
  surprise:"#007AFF", neutral:"#8E8E93", love:"#FF2D55", excitement:"#FF9F0A", disgust:"#AF52DE" };
function EmotionDot({ emotion, size }) {
  const d = size || 8;
  return <span style={{display:"inline-block",width:d,height:d,borderRadius:"50%",
    background: EMOTION_COLORS[emotion] || "#8E8E93", marginRight:6, verticalAlign:"middle"}}/>;
}

// Skeleton loader component
function Skeleton({ width="100%", height=16, borderRadius=8, style={} }: any) {
  return (
    <div style={{width,height,borderRadius,background:`linear-gradient(90deg, ${COLORS.border} 25%, ${COLORS.card} 50%, ${COLORS.border} 75%)`,backgroundSize:"200% 100%",animation:"shimmer 1.5s infinite",...style}}/>
  );
}

// Shimmer animation injected safely after mount
const injectShimmer = () => {
  if (typeof document !== "undefined" && document.head) {
    const existing = document.getElementById("jc-shimmer");
    if (!existing) {
      const style = document.createElement("style");
      style.id = "jc-shimmer";
      style.textContent = "@keyframes shimmer { 0% { background-position: -200% 0 } 100% { background-position: 200% 0 } }";
      document.head.appendChild(style);
    }
  }
};

// Conversation skeleton
function ConversationSkeleton() {
  return (
    <div style={{padding:"12px 16px",borderBottom:"1px solid "+COLORS.border,display:"flex",alignItems:"center",gap:12}}>
      <Skeleton width={48} height={48} borderRadius={24}/>
      <div style={{flex:1}}>
        <Skeleton width="60%" height={14} style={{marginBottom:8}}/>
        <Skeleton width="80%" height={12}/>
      </div>
      <Skeleton width={40} height={12}/>
    </div>
  );
}

async function requestNotificationPermission() {
  if (!("Notification" in window)) return false;
  if (Notification.permission === "granted") return true;
  const permission = await Notification.requestPermission();
  return permission === "granted";
}

// Register service worker
// Service worker registered safely after mount
const registerSW = () => {
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker.register("/sw.js").catch(() => {});
    });
  }
};

// PWA install prompt
let deferredPrompt: any = null;
// PWA install prompt - registered safely
const registerInstallPrompt = () => {
  window.addEventListener("beforeinstallprompt", (e: any) => {
    e.preventDefault();
    deferredPrompt = e;
    setTimeout(() => {
      const existing = document.getElementById("jc-install-btn");
      if (!existing && deferredPrompt) {
        const btn = document.createElement("div");
        btn.id = "jc-install-btn";
        btn.innerHTML = `<div style="position:fixed;bottom:80px;left:50%;transform:translateX(-50%);background:#007AFF;color:#fff;padding:12px 24px;border-radius:50px;font-size:14px;font-weight:700;cursor:pointer;z-index:9998;box-shadow:0 4px 20px rgba(0,122,255,0.4);white-space:nowrap">
          📲 Add JediChatAI to Home Screen
        </div>`;
        btn.onclick = async () => {
          if (deferredPrompt) {
            deferredPrompt.prompt();
            await deferredPrompt.userChoice;
            deferredPrompt = null;
            btn.remove();
          }
        };
        document.body.appendChild(btn);
      }
    }, 10000);
  });
};

function showBrowserNotification(title: string, body: string, risk: string = "low") {
  if (Notification.permission !== "granted") return;
  const icon = "logo.png";
  const notification = new Notification(title, {
    body,
    icon,
    badge: icon,
    tag: "jedichatai-message",
  });
  notification.onclick = () => {
    window.focus();
    notification.close();
  };
  if (risk === "high" || risk === "critical") {
    setTimeout(() => notification.close(), 10000);
  } else {
    setTimeout(() => notification.close(), 5000);
  }
}

function getRiskColor(level) { return level==="high"||level==="critical"?COLORS.danger:level==="medium"?COLORS.warning:COLORS.success; }



function VerifyEmailPage({ email, onVerified, onBack }) {
  const [otp, setOtp] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [resending, setResending] = React.useState(false);

  const verify = async () => {
    if (!otp || otp.length !== 6) { setError("Please enter the 6-digit code"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(window.location.hostname === "localhost" ? "http://localhost:8000/api/v1/auth/verify-email" : "https://app.jedichatai.com/api/v1/auth/verify-email", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({email, otp})
      });
      const data = await res.json();
      if (data.message === "Email verified successfully") {
        onVerified();
      } else { setError(data.detail || "Invalid code"); }
    } catch(e) { setError("Could not connect to server"); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    setResending(true);
    try {
      await fetch(window.location.hostname === "localhost" ? "http://localhost:8000/api/v1/auth/send-verification" : "https://app.jedichatai.com/api/v1/auth/send-verification", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({email})
      });
      setError("New code sent!");
    } catch(e) { setError("Could not resend code"); }
    finally { setResending(false); }
  };

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF 0%,#5856D6 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:48,marginBottom:8}}>{<Mail size={18}/>}</div>
          <div style={{fontSize:24,fontWeight:800,color:COLORS.primary}}>Verify Email</div>
          <div style={{fontSize:13,color:COLORS.subtext,marginTop:4}}>Enter the 6-digit code sent to</div>
          <div style={{fontSize:13,fontWeight:600,color:COLORS.text}}>{email}</div>
        </div>
        {error && <div style={{background:error==="New code sent!"?"#F0FFF4":"#FFF0F0",border:"1px solid "+(error==="New code sent!"?"#A5D6A7":"#FFD0D0"),borderRadius:10,padding:10,marginBottom:16,fontSize:13,color:error==="New code sent!"?COLORS.success:COLORS.danger}}>{error}</div>}
        <input type="text" placeholder="Enter 6-digit code" value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").substring(0,6))}
          style={{width:"100%",padding:"14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:24,outline:"none",boxSizing:"border-box",textAlign:"center",letterSpacing:8,marginBottom:16}}/>
        <button onClick={verify} disabled={loading}
          style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:16,fontWeight:700,cursor:"pointer",marginBottom:12}}>
          {loading?"Verifying...":"Verify Email"}
        </button>
        <div style={{textAlign:"center",fontSize:13,color:COLORS.subtext}}>
          Didn't receive the code?{" "}
          <span style={{color:COLORS.primary,fontWeight:600,cursor:"pointer"}} onClick={resend}>{resending?"Sending...":"Resend"}</span>
        </div>
        <div style={{textAlign:"center",marginTop:12,fontSize:13,color:COLORS.subtext}}>
          <span style={{color:COLORS.primary,fontWeight:600,cursor:"pointer"}} onClick={onBack}>← Back</span>
        </div>
      </div>
    </div>
  );
}


function OnboardingPage({ onComplete }) {
  const [step, setStep] = React.useState(0);
  const [name, setName] = React.useState(localStorage.getItem("jedichatai_name") || "");

  const steps = [
    {
      icon: <MessageCircle size={48} color="#007AFF"/>,
      title: "Welcome to JediChatAI!",
      desc: "Your AI-powered WhatsApp assistant. Smart replies, emotion detection and fraud protection, all in one place.",
      action: "Get Started"
    },
    {
      icon: <Zap size={48} color="#FF9500"/>,
      title: "AI Replies in Seconds",
      desc: "JediChatAI reads your WhatsApp messages, detects emotions and generates perfect replies automatically.",
      action: "Next"
    },
    {
      icon: <Shield size={48} color="#34C759"/>,
      title: "Stay Protected",
      desc: "Our AI flags risky messages including financial scams, legal threats and manipulation attempts before you reply.",
      action: "Next"
    },
    {
      icon: <Globe size={48} color="#5856D6"/>,
      title: "181 Languages",
      desc: "JediChatAI works in 181 languages. It automatically detects and replies in the same language as the sender.",
      action: "Next"
    },
    {
      icon: <CheckCircle size={48} color="#34C759"/>,
      title: "Connect WhatsApp",
      desc: "Scan a QR code to link your WhatsApp. Takes less than 2 minutes. Your messages stay private and encrypted.",
      action: "Connect WhatsApp Now"
    },
    {
      icon: <Bell size={48} color="#FF3B30"/>,
      title: "Enable Notifications",
      desc: "Get notified when important messages arrive so you never miss anything urgent.",
      action: "Enable & Finish"
    },
  ];

  const handleAction = async () => {
    if (step === steps.length - 1) {
      try { if ("Notification" in window) { await Notification.requestPermission(); } } catch(e) {}
      if (name) localStorage.setItem("jedichatai_name", name);
      onComplete();
    } else if (step === steps.length - 2) {
      // WhatsApp connect step - navigate to connect page
      if (name) localStorage.setItem("jedichatai_name", name);
      setStep(step + 1);
    } else {
      setStep(step + 1);
    }
  };

  const current = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF,#5856D6)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:36,width:"100%",maxWidth:380,textAlign:"center",boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{marginBottom:24}}>
          <div style={{background:"#f0f4f8",borderRadius:100,height:6,marginBottom:20}}>
            <div style={{background:COLORS.primary,height:"100%",borderRadius:100,width:progress+"%",transition:"width 0.4s"}}/>
          </div>
          <div style={{fontSize:12,color:COLORS.subtext}}>{step + 1} of {steps.length}</div>
        </div>
        <div style={{marginBottom:24}}>{current.icon}</div>
        <div style={{fontSize:24,fontWeight:800,color:COLORS.text,marginBottom:12}}>{current.title}</div>
        <div style={{fontSize:15,color:COLORS.subtext,lineHeight:1.7,marginBottom:32}}>{current.desc}</div>
        {step === 0 && (
          <div style={{marginBottom:20}}>
            <input type="text" placeholder="What is your name?" value={name} onChange={e=>setName(e.target.value)}
              style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box",textAlign:"center"}}/>
          </div>
        )}
        <button onClick={handleAction}
          style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:16,fontWeight:700,cursor:"pointer",marginBottom:12}}>
          {current.action}
        </button>
        {step > 0 && (
          <button onClick={()=>setStep(step-1)}
            style={{width:"100%",background:"none",border:"none",color:COLORS.subtext,fontSize:14,cursor:"pointer",padding:"8px"}}>
            Back
          </button>
        )}
        {step === steps.length - 1 && (
          <button onClick={onComplete}
            style={{width:"100%",background:"none",border:"none",color:COLORS.subtext,fontSize:13,cursor:"pointer",marginTop:4}}>
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
}

function SignupPage({ onSignup, onBack }) {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [fullName, setFullName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [showVerify, setShowVerify] = React.useState(false);

  if (showVerify) return <VerifyEmailPage email={email} onVerified={onSignup} onBack={()=>setShowVerify(false)}/>;

  const handleSignup = async () => {
    if (!email || !password || !fullName) { setError("Please fill in all fields"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirmPassword) { setError("Passwords do not match"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(window.location.hostname === "localhost" ? "http://localhost:8000/api/v1/auth/register" : "https://app.jedichatai.com/api/v1/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, full_name: fullName })
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem("jedichatai_token", data.access_token);
        await fetch(window.location.hostname === "localhost" ? "http://localhost:8000/api/v1/auth/send-verification" : "https://app.jedichatai.com/api/v1/auth/send-verification", {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email})});
        setShowVerify(true); setLoading(false); return; requestNotificationPermission();
      } else { setError(data.detail || "Registration failed"); }
    } catch(e) { setError("Could not connect to server"); }
    finally { setLoading(false); }
  };

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF 0%,#5856D6 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <img src="/chat.png" alt="JediChatAI" style={{width:80,height:80,objectFit:"contain",marginBottom:8}}/>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Create Account</div>
          <div style={{fontSize:14,color:COLORS.subtext,marginTop:4}}>Join JediChatAI today</div>
        </div>
        <button onClick={() => {
          const params = new URLSearchParams({
            client_id: "790306978991-flj3kcj6e54og6f1mi2efvpdrg6dtknd.apps.googleusercontent.com",
            redirect_uri: "https://app.jedichatai.com/auth/google",
            response_type: "code",
            scope: "email profile",
          });
          window.location.href = "https://accounts.google.com/o/oauth2/v2/auth?" + params.toString();
        }} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:"#fff",border:"2px solid #e5e5e7",borderRadius:12,padding:"12px 16px",fontSize:15,fontWeight:600,cursor:"pointer",marginBottom:10,color:"#1a1a1a"}}>
          <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>
        <button onClick={() => {
          const appleParams = new URLSearchParams({
            client_id: "com.jedireply.jedichatai.web",
            redirect_uri: "https://app.jedichatai.com/auth/apple/callback",
            response_type: "code",
            response_mode: "form_post",
            scope: "name email",
          });
          window.location.href = "https://appleid.apple.com/auth/authorize?" + appleParams.toString();
        }} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:"#000",border:"none",borderRadius:12,padding:"12px 16px",fontSize:15,fontWeight:600,cursor:"pointer",marginBottom:16,color:"#fff"}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
          Continue with Apple
        </button>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
          <div style={{flex:1,height:1,background:COLORS.border}}/>
          <span style={{fontSize:12,color:COLORS.subtext}}>OR</span>
          <div style={{flex:1,height:1,background:COLORS.border}}/>
        </div>
        {error && <div style={{background:"#FFF0F0",border:"1px solid #FFD0D0",borderRadius:10,padding:10,marginBottom:16,fontSize:13,color:COLORS.danger}}>{error}</div>}
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Full Name</div>
          <input type="text" placeholder="Your full name" value={fullName} onChange={e=>setFullName(e.target.value)}
            style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Email</div>
          <input type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)}
            style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Password</div>
          <input type="password" placeholder="Min 6 characters" value={password} onChange={e=>setPassword(e.target.value)}
            style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div style={{marginBottom:24}}>
          <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Confirm Password</div>
          <input type="password" placeholder="Confirm your password" value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)}
            style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box"}}/>
        </div>
        <button onClick={handleSignup} disabled={loading}
          style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:16,fontWeight:700,cursor:"pointer"}}>
          {loading ? "Creating account..." : "Create Account"}
        </button>
        <div style={{background:"#FFF3E0",borderRadius:10,padding:12,marginTop:16,textAlign:"center"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.warning,marginBottom:4}}>🚧 Early Access Only</div>
          <div style={{fontSize:12,color:COLORS.subtext}}>JediChatAI is currently in early access. Contact jedidiah@jedireply.com to request an invitation.</div>
        </div>
        <div style={{textAlign:"center",marginTop:16,fontSize:13,color:COLORS.subtext}}>
          Already have an account?{" "}
          <span style={{color:COLORS.primary,fontWeight:600,cursor:"pointer"}} onClick={onBack}>Sign In</span>
        </div>
      </div>
    </div>
  );
}

function ForgotPasswordPage({ onBack }) {
  const [email, setEmail] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleReset = async () => {
    if(!email){ setError("Please enter your email"); return; }
    setLoading(true);
    try {
      const res = await fetch("https://app.jedichatai.com/api/v1/auth/send-verification", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({email, purpose:"reset"})
      });
      setSent(true);
    } catch(e){ setError("Failed to send reset email. Try again."); }
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF 0%,#5856D6 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{textAlign:"center",marginBottom:24}}>
          <img src="/chat.png" alt="JediChatAI" style={{width:64,height:64,objectFit:"contain",marginBottom:8}}/>
          <div style={{fontSize:24,fontWeight:800,color:COLORS.primary}}>Reset Password</div>
          <div style={{fontSize:13,color:COLORS.subtext,marginTop:4}}>Enter your email to receive a reset code</div>
        </div>
        {sent ? (
          <div style={{textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:16}}>{<Mail size={18}/>}</div>
            <div style={{fontSize:16,fontWeight:700,marginBottom:8}}>Check your email!</div>
            <div style={{fontSize:13,color:COLORS.subtext,marginBottom:24}}>We sent a verification code to {email}. Use it to reset your password.</div>
            <button onClick={onBack} style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer"}}>Back to Sign In</button>
          </div>
        ) : (
          <>
            {error && <div style={{background:"#FFF0F0",border:"1px solid #FFD0D0",borderRadius:10,padding:10,marginBottom:16,fontSize:13,color:COLORS.danger}}>{error}</div>}
            <div style={{marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:600,marginBottom:6}}>Email</div>
              <input type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)}
                onKeyPress={e=>e.key==="Enter"&&handleReset()}
                style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box"}}/>
            </div>
            <button onClick={handleReset} disabled={loading}
              style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer",marginBottom:16}}>
              {loading?"Sending...":"Send Reset Code"}
            </button>
            <div style={{textAlign:"center",fontSize:13,color:COLORS.subtext}}>
              Remember your password? <span style={{color:COLORS.primary,fontWeight:600,cursor:"pointer"}} onClick={onBack}>Sign In</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LoginPage({ onLogin }) {
  const [showSignup, setShowSignup] = React.useState(false);
  const [showForgot, setShowForgot] = React.useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async () => {
    if (!email || !password) { setError("Please enter email and password"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(window.location.hostname === "localhost" ? "http://localhost:8000/api/v1/auth/login" : "https://app.jedichatai.com/api/v1/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem("jedichatai_token", data.access_token);
        // A business account belongs in the business app, not this one.
        try {
          const meRes = await fetch(API + "/api/v1/auth/me",
            { headers: { Authorization: "Bearer " + data.access_token } });
          const me = await meRes.json();
          if (me.account_type === "business") {
            localStorage.removeItem("jedichatai_token");
            setError("That is a business account. Sign in at business.jedichatai.com");
            setLoading(false); return;
          }
        } catch(e) {}
        localStorage.setItem("jedichatai_email", email);
        onLogin(); requestNotificationPermission();
      } else {
        setError(data.detail || "Invalid credentials");
      }
    } catch(e) {
      setError("Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  if (showSignup) return <SignupPage onSignup={onLogin} onBack={()=>setShowSignup(false)}/>;
  if (showForgot) return <ForgotPasswordPage onBack={()=>setShowForgot(false)}/>;
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF 0%,#5856D6 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <img src="/chat.png" alt="JediChatAI" style={{width:80,height:80,objectFit:"contain",marginBottom:8}}/>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>JediChatAI</div>
          <div style={{fontSize:14,color:COLORS.subtext,marginTop:4}}>AI WhatsApp Assistant</div>
        </div>
        {error && <div style={{background:"#FFF0F0",border:"1px solid #FFD0D0",borderRadius:10,padding:10,marginBottom:16,fontSize:13,color:COLORS.danger}}>{error}</div>}
        <button onClick={() => {
          const params = new URLSearchParams({
            client_id: "790306978991-flj3kcj6e54og6f1mi2efvpdrg6dtknd.apps.googleusercontent.com",
            redirect_uri: "https://app.jedichatai.com/auth/google",
            response_type: "code",
            scope: "email profile",
          });
          window.location.href = "https://accounts.google.com/o/oauth2/v2/auth?" + params.toString();
        }} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:"#fff",border:"2px solid #e5e5e7",borderRadius:12,padding:"12px 16px",fontSize:15,fontWeight:600,cursor:"pointer",marginBottom:10,color:"#1a1a1a"}}>
          <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Continue with Google
        </button>
        <button onClick={() => {
          const appleParams = new URLSearchParams({
            client_id: "com.jedireply.jedichatai.web",
            redirect_uri: "https://app.jedichatai.com/auth/apple/callback",
            response_type: "code",
            response_mode: "form_post",
            scope: "name email",
          });
          window.location.href = "https://appleid.apple.com/auth/authorize?" + appleParams.toString();
        }} style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"center",gap:10,background:"#000",border:"none",borderRadius:12,padding:"12px 16px",fontSize:15,fontWeight:600,cursor:"pointer",marginBottom:16,color:"#fff"}}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
          Continue with Apple
        </button>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16}}>
          <div style={{flex:1,height:1,background:COLORS.border}}/>
          <span style={{fontSize:12,color:COLORS.subtext}}>OR</span>
          <div style={{flex:1,height:1,background:COLORS.border}}/>
        </div>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,color:COLORS.text,marginBottom:6}}>Email</div>
          <input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={e=>setEmail(e.target.value)}
            style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box"}}
          />
        </div>
        <div style={{marginBottom:24}}>
          <div style={{fontSize:13,fontWeight:600,color:COLORS.text,marginBottom:6}}>Password</div>
          <input
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={e=>setPassword(e.target.value)}
            onKeyPress={e=>e.key==="Enter"&&handleLogin()}
            style={{width:"100%",padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box"}}
          />
        </div>
        <button onClick={handleLogin} disabled={loading}
          style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:16,fontWeight:700,cursor:"pointer"}}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
        <div style={{textAlign:"center",marginTop:16,fontSize:13,color:COLORS.subtext}}>
          Don't have an account? <span style={{color:COLORS.primary,fontWeight:600,cursor:"pointer"}} onClick={()=>setShowSignup(true)}>Sign Up</span>
          </div>
          <div style={{textAlign:"center",marginTop:8,fontSize:13}}>
            <span style={{color:COLORS.primary,fontWeight:600,cursor:"pointer"}} onClick={()=>setShowForgot(true)}>Forgot Password?</span>
        </div>
      </div>
    </div>
  );
}

function ConversationsPage() {
  const [convos, setConvos] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState("");
  const [globalAuto, setGlobalAuto] = useState(false);
  const [usage, setUsage] = useState({messages_today:0, daily_limit:50, plan:"free"});
  const [contactAuto, setContactAuto] = useState({});
  const [contactRel, setContactRel] = useState<any>({});
  const [contactNotes, setContactNotes] = useState({});

  // Notes now live on the server so they follow the user across devices and
  // reach the AI. Anything left in this browser is migrated once.
  React.useEffect(() => {
    const token = localStorage.getItem("jedichatai_token");
    if (!token) return;
    fetch(API + "/api/v1/contacts/notes", { headers: { Authorization: "Bearer " + token } })
      .then(r => r.json())
      .then(async d => {
        const server = d.notes || {};
        let local = {};
        try { local = JSON.parse(localStorage.getItem("jedichatai_notes") || "{}"); } catch(e) {}
        for (const [cid, note] of Object.entries(local)) {
          if (!server[cid] && note) {
            await fetch(API + "/api/v1/contacts/notes", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
              body: JSON.stringify({ contact_id: cid, note })
            }).catch(() => {});
            server[cid] = note;
          }
        }
        localStorage.removeItem("jedichatai_notes");
        setContactNotes(server);
      })
      .catch(() => {});
  }, []);
  const [showNoteFor, setShowNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [summaries, setSummaries] = useState({});
  const [loadingSummary, setLoadingSummary] = useState(null);

  const prevConvosRef = React.useRef([]);
  const [newMsgCount, setNewMsgCount] = useState(0);
  const [showNewBadge, setShowNewBadge] = useState(false);

  // Request push notification permission on load
  React.useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      setTimeout(() => { Notification.requestPermission(); }, 3000);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      await getToken();
      const [data, autoSettings] = await Promise.all([getConversations(), getAutoRespond()]);
      setConvos(data.length > 0 ? data : []);
      setGlobalAuto(autoSettings.global);
      api.get("/api/v1/user/plan").then(r => { if (r && r.data) setUsage(r.data); }).catch(()=>{});
      setContactAuto(autoSettings.contacts || {});
      try {
        const cl = await api.get("/api/v1/contacts");
        const relMap: any = {};
        (cl.data.contacts || []).forEach((ct: any) => { relMap[ct.contact_id] = ct.relationship_type || "professional"; });
        setContactRel(relMap);
      } catch(e) {}
      setLastUpdate(new Date().toLocaleTimeString());

      // Show browser notification for new messages
      const prevIds = prevConvosRef.current.map((c: any) => c.contactId);
      const newConvos = data.filter((c: any) => !prevIds.includes(c.contactId));
      if (newConvos.length > 0) {
        setNewMsgCount(n => n + newConvos.length);
        setShowNewBadge(true);
        setTimeout(() => setShowNewBadge(false), 6000);
      }
      newConvos.forEach((conv: any) => {
        const risk = conv.riskLevel || "low";
        const title = risk === "high" || risk === "critical"
          ? "High Risk Message!"
          : "New Message from " + (conv.contactName || "Someone");
        showBrowserNotification(title, conv.originalMessage || "", risk);
      });
      prevConvosRef.current = data;
    } catch(e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 5000); return () => clearInterval(i); }, [load]);

  const handleGlobalToggle = async () => { const v = !globalAuto; setGlobalAuto(v); await setGlobalAutoRespond(v); };
  const handleContactToggle = async (id) => { const v = !contactAuto[id]; setContactAuto(p => ({...p,[id]:v})); await setContactAutoRespond(id,v); };
  const handleRelChange = async (id, name, rel) => {
    setContactRel(p => ({...p,[id]:rel}));
    try { await api.post("/api/v1/contacts/relationship", { contact_id: id, contact_name: name, relationship_type: rel }); } catch(e) {}
  };
  const handleApprove = async (conv) => {
    if(window.confirm("Send this AI reply?\n\n"+conv.aiReply)) {
      try { await approveReply(conv.contactId,conv.aiReply,conv.contactId);
      const h = JSON.parse(localStorage.getItem("jedichatai_history")||"[]");
      h.unshift({contact:conv.contactName||conv.contactId, reply:conv.aiReply, original:conv.originalMessage, date:new Date().toLocaleString()});
      // Auto-learn from approved replies per contact
      try {
        const contactKey = "jedichatai_contact_style_" + (conv.contactId||"").replace(/[^a-z0-9]/gi,"_");
        const existing = JSON.parse(localStorage.getItem(contactKey)||"[]");
        existing.unshift({their_msg: conv.originalMessage, my_reply: conv.aiReply, date: new Date().toLocaleDateString()});
        localStorage.setItem(contactKey, JSON.stringify(existing.slice(0,20)));
      } catch(e) {}
      localStorage.setItem("jedichatai_history", JSON.stringify(h.slice(0,100)));
      const s = JSON.parse(localStorage.getItem("jedichatai_sentiment")||"[]");
      s.push({contact:conv.contactName||conv.contactId, emotion:conv.emotion||"neutral", riskLevel:conv.riskLevel||"low", date:new Date().toLocaleString()});
      localStorage.setItem("jedichatai_sentiment", JSON.stringify(s.slice(0,200)));
      setConvos(p=>p.filter(c=>c.contactId!==conv.contactId)); alert("Sent!"); }
      catch(e) { alert("Failed: "+e.message); }
    }
  };
  const handleDismiss = async (conv) => { try { await dismissReply(conv.contactId); setConvos(p=>p.filter(c=>c.contactId!==conv.contactId)); } catch(e) {} };
  const saveNote = async (id) => {
    const updated = {...contactNotes, [id]: noteText};
    setContactNotes(updated);
    setShowNoteFor(null);
    const token = localStorage.getItem("jedichatai_token");
    await fetch(API + "/api/v1/contacts/notes", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
      body: JSON.stringify({ contact_id: id, note: noteText })
    }).catch(() => {});
  };
  const summarizeConv = async (conv) => {
    if (summaries[conv.contactId]) { setSummaries(p=>({...p,[conv.contactId]:null})); return; }
    setLoadingSummary(conv.contactId);
    try {
      const token = localStorage.getItem("jedichatai_token");
      const url = window.location.hostname === "localhost" ? "http://localhost:8000/api/v1/keyboard/suggest" : "https://app.jedichatai.com/api/v1/keyboard/suggest";
      const res = await fetch(url, {
        method: "POST",
        headers: {"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({message: "Summarize this conversation in 2-3 sentences. Contact: " + (conv.contactName||conv.contactId) + ". Their message: " + conv.originalMessage + ". Emotion: " + conv.emotion + ". Risk: " + conv.riskLevel + ". AI suggested reply: " + (conv.aiReply||"none"), summary_mode: true, style_profile: (() => { try { return JSON.parse(localStorage.getItem("jedichatai_style_profile")||"null"); } catch(e) { return null; } })(), contact_style: (() => { try { const k = "jedichatai_contact_style_" + (conv.contactId||"").replace(/[^a-z0-9]/gi,"_"); return JSON.parse(localStorage.getItem(k)||"null"); } catch(e) { return null; } })()})
      });
      const data = await res.json();
      const summary = data.suggestions?.[0]?.text || "Unable to generate summary.";
      setSummaries(p=>({...p,[conv.contactId]:summary}));
    } catch(e) { setSummaries(p=>({...p,[conv.contactId]:"Could not generate summary."})); }
    finally { setLoadingSummary(null); }
  };

  return (
    <div style={{paddingBottom:80}}>
      {showNewBadge && (
        <div style={{position:"fixed",top:56,left:"50%",transform:"translateX(-50%)",background:COLORS.primary,color:"#fff",borderRadius:20,padding:"8px 20px",fontSize:13,fontWeight:700,zIndex:9999,boxShadow:"0 4px 20px rgba(0,0,0,0.2)",display:"flex",alignItems:"center",gap:8,whiteSpace:"nowrap"}}>
          🔔 {newMsgCount} new message{newMsgCount!==1?"s":""}
          <button onClick={()=>{load();setShowNewBadge(false);setNewMsgCount(0);}} style={{background:"rgba(255,255,255,0.25)",border:"none",color:"#fff",fontWeight:700,cursor:"pointer",borderRadius:6,padding:"2px 8px",marginLeft:4}}>Refresh</button>
        </div>
      )}
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>JediChatAI</div>
          {newMsgCount > 0 && !showNewBadge && <div style={{background:COLORS.danger,color:"#fff",borderRadius:10,padding:"3px 10px",fontSize:12,fontWeight:700}}>{newMsgCount} new</div>}
        </div>
        <div style={{fontSize:13,color:COLORS.subtext,display:"flex",justifyContent:"space-between"}}>
          <span>Live AI Messaging Assistant</span><span>Updated: {lastUpdate}</span>
        </div>
      </div>
      <div style={{background:globalAuto?"#E8F5E9":"#FFF3E0",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid "+COLORS.border}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:globalAuto?COLORS.success:COLORS.warning}}>{globalAuto?"Auto-Respond ON":"Manual Mode"}</div>
          <div style={{fontSize:11,color:COLORS.subtext}}>{globalAuto?"AI replies sent automatically":"You approve every reply"}</div>
        </div>
        <div onClick={handleGlobalToggle} style={{width:50,height:28,borderRadius:14,background:globalAuto?COLORS.success:COLORS.subtext,cursor:"pointer",position:"relative"}}>
          <div style={{width:22,height:22,borderRadius:11,background:"#fff",position:"absolute",top:3,left:globalAuto?25:3}}/>
        </div>
      </div>
      {usage.messages_today >= usage.daily_limit && (
        <div style={{background:"#FFEBEE",borderBottom:"1px solid #FFCDD2",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:12}}>
          <div>
            <div style={{fontSize:14,fontWeight:700,color:"#C62828"}}>Daily limit reached ({usage.messages_today}/{usage.daily_limit})</div>
            <div style={{fontSize:11,color:"#B71C1C"}}>AI auto-replies are paused until tomorrow. Upgrade to keep replying.</div>
          </div>
          <a href="/settings" style={{background:"#C62828",color:"#fff",padding:"8px 16px",borderRadius:20,fontSize:13,fontWeight:700,textDecoration:"none",whiteSpace:"nowrap"}}>Upgrade</a>
        </div>
      )}
      <div style={{display:"flex",gap:8,padding:12}}>
        {[{label:"Pending",value:convos.filter(c=>c.aiReply).length,color:COLORS.warning},{label:"High Risk",value:convos.filter(c=>c.riskLevel==="high").length,color:COLORS.danger},{label:"Total",value:convos.length,color:COLORS.primary}].map(s=>(
          <div key={s.label} style={{flex:1,background:COLORS.card,borderRadius:12,padding:12,textAlign:"center"}}>
            <div style={{fontSize:24,fontWeight:700,color:s.color}}>{s.value}</div>
            <div style={{fontSize:11,color:COLORS.subtext}}>{s.label}</div>
          </div>
        ))}
      </div>
      <div style={{padding:"0 12px 8px"}}>
        <div style={{position:"relative"}}>
          <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",display:"flex",color:COLORS.subtext}}><Search size={16}/></span>
          <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
            placeholder="Search by name or number..."
            style={{width:"100%",padding:"10px 12px 10px 38px",borderRadius:12,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box",background:COLORS.card,color:COLORS.text}}/>
          {searchQuery && (
            <span onClick={()=>setSearchQuery("")} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",fontSize:16,color:COLORS.subtext,cursor:"pointer"}}>✕</span>
          )}
        </div>
      </div>
      <div style={{padding:"0 12px"}}>
        {loading ? <div style={{textAlign:"center",padding:40,color:COLORS.subtext}}>Loading...</div>
        : convos.length===0 ? (
          <div style={{background:COLORS.card,borderRadius:16,padding:32,textAlign:"center"}}>
            <div style={{fontSize:48,marginBottom:12}}>{<MessageCircle size={18}/>}</div>
            <div style={{fontSize:16,fontWeight:600}}>No pending replies</div>
            <div style={{fontSize:13,color:COLORS.subtext,marginTop:8}}>Start WhatsApp service to receive messages</div>
          </div>
        ) : (() => {
            const q = searchQuery.trim().toLowerCase();
            const filtered = q ? convos.filter(c =>
              (c.contactName||"").toLowerCase().includes(q) ||
              (c.contactId||"").toLowerCase().includes(q)
            ) : convos;
            if (filtered.length === 0) {
              return <div style={{background:COLORS.card,borderRadius:16,padding:32,textAlign:"center"}}>
                <div style={{display:"flex",justifyContent:"center",marginBottom:8,color:COLORS.subtext}}><Search size={36}/></div>
                <div style={{fontSize:14,color:COLORS.subtext}}>No contacts match "{searchQuery}"</div>
              </div>;
            }
            return filtered.map(conv=>(
          <div key={conv.contactId} style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:44,height:44,borderRadius:22,background:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:18,fontWeight:700}}>
                {(conv.contactName||"?")[0].toUpperCase()}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:16,fontWeight:600}}>{conv.contactName||conv.contactId}</div>
                <div style={{fontSize:13,color:COLORS.subtext,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{conv.originalMessage}</div>
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end"}}>
                <span style={{padding:"2px 8px",borderRadius:8,fontSize:10,fontWeight:700,border:"1px solid "+getRiskColor(conv.riskLevel),color:getRiskColor(conv.riskLevel),background:getRiskColor(conv.riskLevel)+"20"}}>{(conv.riskLevel||"low").toUpperCase()}</span>
                <span style={{fontSize:12,color:COLORS.subtext}}><EmotionDot emotion={conv.emotion}/>{conv.emotion}</span>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10,padding:"8px 0",borderTop:"1px solid "+COLORS.border}}>
              <span style={{fontSize:12,color:COLORS.subtext}}>Auto-respond for {conv.contactName?.split(" ")[0]}</span>
              <div onClick={()=>handleContactToggle(conv.contactId)} style={{width:40,height:22,borderRadius:11,background:contactAuto[conv.contactId]?COLORS.success:COLORS.subtext,cursor:"pointer",position:"relative"}}>
                <div style={{width:16,height:16,borderRadius:8,background:"#fff",position:"absolute",top:3,left:contactAuto[conv.contactId]?21:3}}/>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"6px 0"}}>
              <span style={{fontSize:12,color:COLORS.subtext}}>Relationship {contactRel[conv.contactId]==="romantic" && ""}</span>
              <select value={contactRel[conv.contactId]||"professional"}
                onChange={e=>handleRelChange(conv.contactId, conv.contactName, e.target.value)}
                style={{fontSize:12,padding:"4px 8px",borderRadius:8,border:"1px solid "+COLORS.border,background:"#fff",color:COLORS.text,cursor:"pointer",outline:"none"}}>
                <option value="professional">Professional</option>
                <option value="friend">Friend</option>
                <option value="family">Family</option>
                <option value="romantic">Romantic</option>
              </select>
            </div>
            <div style={{marginTop:8}}>
              <button onClick={()=>{ setShowNoteFor(showNoteFor===conv.contactId?null:conv.contactId); setNoteText(contactNotes[conv.contactId]||""); }}
                style={{background:"none",border:"1px solid "+COLORS.border,borderRadius:8,padding:"4px 10px",fontSize:12,color:COLORS.subtext,cursor:"pointer"}}>
                📝 {contactNotes[conv.contactId] ? "Edit Note" : "Add Note"}
              </button>
              {contactNotes[conv.contactId] && <span style={{fontSize:11,color:COLORS.subtext,marginLeft:8}}>📌 {contactNotes[conv.contactId].substring(0,30)}{contactNotes[conv.contactId].length>30?"...":""}</span>}
            </div>
            <div style={{marginTop:6}}>
              <button onClick={()=>summarizeConv(conv)}
                style={{background:"none",border:"1px solid "+COLORS.purple||"#5856D6",borderRadius:8,padding:"4px 10px",fontSize:12,color:"#5856D6",cursor:"pointer"}}>
                {loadingSummary===conv.contactId ? "⏳ Summarizing..." : summaries[conv.contactId] ? "Hide Summary" : "AI Summary"}
              </button>
            </div>
            {summaries[conv.contactId] && (
              <div style={{marginTop:8,background:"#F5F0FF",borderRadius:12,padding:12,border:"1px solid #C4B5FD"}}>
                <div style={{fontSize:12,fontWeight:600,color:"#5856D6",marginBottom:4}}>{<Brain size={14}/>} Conversation Summary</div>
                <div style={{fontSize:13,lineHeight:1.6,color:COLORS.text}}>{summaries[conv.contactId]}</div>
              </div>
            )}
            {showNoteFor===conv.contactId && (
              <div style={{marginTop:8,background:"#FFFBEA",borderRadius:12,padding:12,border:"1px solid #FFE082"}}>
                <div style={{fontSize:12,fontWeight:600,color:"#F59E0B",marginBottom:6}}>📝 Note for {conv.contactName}</div>
                <textarea value={noteText} onChange={e=>setNoteText(e.target.value)}
                  placeholder="Add a private note about this contact..."
                  style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid #FFE082",fontSize:13,outline:"none",boxSizing:"border-box",minHeight:60,resize:"vertical",background:"#fff"}}/>
                <div style={{display:"flex",gap:8,marginTop:8}}>
                  <button onClick={()=>saveNote(conv.contactId)} style={{flex:1,background:"#F59E0B",color:"#fff",border:"none",borderRadius:8,padding:"8px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Save Note</button>
                  <button onClick={()=>setShowNoteFor(null)} style={{flex:1,background:"#f0f0f0",color:COLORS.text,border:"none",borderRadius:8,padding:"8px",fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                </div>
              </div>
            )}
            {conv.aiReply && (
              <div style={{marginTop:8,background:"#F0F7FF",borderRadius:12,padding:12}}>
                <div style={{fontSize:12,fontWeight:600,color:COLORS.primary,marginBottom:4}}>{<Zap size={14}/>} AI Reply Ready</div>
                <div style={{fontSize:13,lineHeight:1.5}}>{conv.aiReply}</div>
                {!globalAuto && !contactAuto[conv.contactId] && (
                  <div style={{display:"flex",gap:8,marginTop:8}}>
                    <button onClick={()=>handleApprove(conv)} style={{flex:1,background:COLORS.primary,color:"#fff",border:"none",borderRadius:8,padding:"10px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>{<Check size={14}/>} Send</button>
                    <button onClick={()=>handleDismiss(conv)} style={{flex:1,background:COLORS.danger,color:"#fff",border:"none",borderRadius:8,padding:"10px 16px",fontSize:13,fontWeight:600,cursor:"pointer"}}>❌ Dismiss</button>
                  </div>
                )}
                {(globalAuto||contactAuto[conv.contactId]) && (
                  <div style={{marginTop:8,background:"#E8F5E9",borderRadius:8,padding:8,fontSize:12,color:COLORS.success,fontWeight:600}}>{<Check size={14}/>} Will be sent automatically</div>
                )}
              </div>
            )}
          </div>
        ))})()}
      </div>
    </div>
  );
}



function WhatsAppConnectPage({ onConnected }) {
  const [qrCode, setQrCode] = React.useState("");
  const [status, setStatus] = React.useState("loading");
  const [accountType, setAccountType] = React.useState("");
  const [method, setMethod] = React.useState("");           // "qr" | "phone"
  const [phoneInput, setPhoneInput] = React.useState("");
  const [pairingCode, setPairingCode] = React.useState("");
  const [pairingError, setPairingError] = React.useState("");
  const [codeSeconds, setCodeSeconds] = React.useState(0);
  const [requesting, setRequesting] = React.useState(false);
  const WHATSAPP_URL = "https://app.jedichatai.com/whatsapp";

  // ── Pairing code (Baileys): connect with just a phone, no second device ────
  const requestPairingCode = async () => {
    const clean = phoneInput.replace(/[^0-9]/g, "");
    if (clean.length < 8) { setPairingError("Enter your full number with country code"); return; }
    setRequesting(true); setPairingError(""); setPairingCode("");
    try {
      await api.post("/api/v1/baileys/start", { phone_number: clean });
      // poll for the code
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const res = await api.get("/api/v1/baileys/pairing-code");
          if (res.data?.connected) { clearInterval(poll); onConnected(); return; }
          if (res.data?.pairingCode) {
            clearInterval(poll);
            setPairingCode(res.data.pairingCode);
            setCodeSeconds(60);
            setRequesting(false);
          }
        } catch (e) {}
        if (tries > 12) { clearInterval(poll); setRequesting(false); setPairingError("Could not get a code. Try again."); }
      }, 1500);
    } catch (e) {
      setRequesting(false);
      setPairingError("Could not start the connection. Try again.");
    }
  };

  // countdown + connection polling while a code is showing
  React.useEffect(() => {
    if (!pairingCode) return;
    const tick = setInterval(() => setCodeSeconds(s => (s > 0 ? s - 1 : 0)), 1000);
    const check = setInterval(async () => {
      try {
        const res = await api.get("/api/v1/baileys/status");
        if (res.data?.connected) { clearInterval(tick); clearInterval(check); onConnected(); }
      } catch (e) {}
    }, 3000);
    return () => { clearInterval(tick); clearInterval(check); };
  }, [pairingCode]);

  React.useEffect(() => {
    if (accountType === "personal" && method === "qr") {
      // Start session once then poll
      api.post("/api/v1/whatsapp/session/start", {}).catch(() => {});
      setTimeout(fetchQR, 2000); // wait 2s for session to init
      const interval = setInterval(() => { fetchQR(); checkStatus(); }, 4000);
      return () => clearInterval(interval);
    }
  }, [accountType]);

  const fetchQR = async () => {
    try {
      const userEmail = localStorage.getItem("jedichatai_email") || "default";
      const res = await api.get("/api/v1/whatsapp/qr/" + encodeURIComponent(userEmail)).catch(() => null);
      if (!res) { setStatus("error"); return; }
      const data = res.data;
      if (data.status === "connected") { onConnected(); return; }
      if (data.qr) { setQrCode(data.qr); setStatus("pending"); }
      else { setStatus(data.status || "starting"); }
    } catch(e) { setStatus("error"); }
  };
  const retryFetchQR = async () => {
    setStatus("loading");
    await fetchQR();
  };

  const checkStatus = async () => {
    try {
      const userEmail = localStorage.getItem("jedichatai_email") || "default";
      const res = await api.get("/api/v1/whatsapp/status/" + encodeURIComponent(userEmail));
      if (res.data.status === "connected") onConnected();
    } catch(e) {}
  };

  if (!accountType) return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF 0%,#5856D6 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",textAlign:"center"}}>
        <img src="/logo.png" alt="JediChatAI" style={{width:80,height:80,borderRadius:20,marginBottom:16,objectFit:"contain"}}/>
        <div style={{fontSize:24,fontWeight:800,color:COLORS.primary,marginBottom:8}}>Connect WhatsApp</div>
        <div style={{fontSize:14,color:COLORS.subtext,marginBottom:24}}>Select your WhatsApp account type to get started</div>
        <button onClick={()=>setAccountType("personal")}
          style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer",marginBottom:12}}>
          📱 Personal WhatsApp
        </button>
        <button onClick={()=>setAccountType("business")}
          style={{width:"100%",background:"#5856D6",color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer"}}>
          💼 WhatsApp Business
        </button>
      </div>
    </div>
  );

  if (accountType === "business") return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#5856D6 0%,#007AFF 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",textAlign:"center"}}>
        <div style={{fontSize:48,marginBottom:16}}>{<FileText size={18}/>}</div>
        <div style={{fontSize:24,fontWeight:800,color:"#5856D6",marginBottom:8}}>WhatsApp Business</div>
        <div style={{fontSize:14,color:COLORS.subtext,marginBottom:24}}>WhatsApp Business API requires Meta Business verification. Use Personal WhatsApp to connect your number.</div>
        <button onClick={()=>setAccountType("")}
          style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer"}}>
          ← Go Back
        </button>
      </div>
    </div>
  );

  // ── Choose how to connect ────────────────────────────────────────────────
  if (accountType === "personal" && !method) return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF 0%,#5856D6 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",textAlign:"center"}}>
        <img src="/logo.png" alt="JediChatAI" style={{width:72,height:72,borderRadius:18,marginBottom:12,objectFit:"contain"}}/>
        <div style={{fontSize:22,fontWeight:800,color:COLORS.primary,marginBottom:6}}>How do you want to connect?</div>
        <div style={{fontSize:13,color:COLORS.subtext,marginBottom:22}}>Both link your WhatsApp securely.</div>

        <button onClick={()=>setMethod("phone")}
          style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer",marginBottom:10}}>
          Use my phone number
        </button>
        <div style={{fontSize:11,color:COLORS.subtext,marginBottom:18}}>Only one device needed. No QR code.</div>

        <button onClick={()=>setMethod("qr")}
          style={{width:"100%",background:"#fff",color:COLORS.primary,border:"2px solid "+COLORS.primary,borderRadius:12,padding:"13px",fontSize:15,fontWeight:700,cursor:"pointer",marginBottom:10}}>
          Scan a QR code
        </button>
        <div style={{fontSize:11,color:COLORS.subtext}}>You will need a second screen to scan from.</div>

        <button onClick={()=>setAccountType("")} style={{marginTop:20,background:"none",border:"none",color:COLORS.subtext,cursor:"pointer",fontSize:13}}>Back</button>
      </div>
    </div>
  );

  // ── Pairing code: single device, no QR ───────────────────────────────────
  if (accountType === "personal" && method === "phone") return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF 0%,#5856D6 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",textAlign:"center"}}>
        <div style={{fontSize:22,fontWeight:800,color:COLORS.primary,marginBottom:6}}>Connect with your number</div>

        {!pairingCode ? (
          <>
            <div style={{fontSize:13,color:COLORS.subtext,marginBottom:18}}>Enter the number your WhatsApp uses, including the country code.</div>
            <input
              value={phoneInput}
              onChange={e=>setPhoneInput(e.target.value)}
              placeholder="e.g. 14384641730"
              style={{width:"100%",padding:"13px",fontSize:16,border:"2px solid #E5E7EB",borderRadius:12,marginBottom:12,boxSizing:"border-box",textAlign:"center",letterSpacing:1}}
            />
            {pairingError && <div style={{fontSize:12,color:"#DC2626",marginBottom:10}}>{pairingError}</div>}
            <button onClick={requestPairingCode} disabled={requesting}
              style={{width:"100%",background:requesting?"#9CA3AF":COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:requesting?"default":"pointer"}}>
              {requesting ? "Getting your code..." : "Get my code"}
            </button>
          </>
        ) : (
          <>
            <div style={{fontSize:13,color:COLORS.subtext,marginBottom:14}}>Enter this code in WhatsApp now.</div>
            <div style={{background:"#F0F7FF",borderRadius:14,padding:"20px 12px",marginBottom:6}}>
              <div style={{fontSize:34,fontWeight:800,letterSpacing:6,color:COLORS.primary,fontFamily:"monospace"}}>{pairingCode}</div>
            </div>
            <div style={{fontSize:12,color: codeSeconds>15 ? COLORS.subtext : "#DC2626", marginBottom:16}}>
              {codeSeconds>0 ? "Expires in " + codeSeconds + "s" : "Code expired - get a new one"}
            </div>

            <div style={{background:"#F9FAFB",borderRadius:12,padding:14,marginBottom:16,textAlign:"left"}}>
              <div style={{fontSize:13,fontWeight:700,color:COLORS.primary,marginBottom:8}}>On your phone:</div>
              <div style={{fontSize:12,color:COLORS.text,lineHeight:1.9}}>
                1. Open WhatsApp<br/>
                2. Settings, then Linked Devices<br/>
                3. Tap Link a Device<br/>
                4. Tap "Link with phone number instead"<br/>
                5. Enter the code above
              </div>
            </div>

            <button onClick={()=>{ setPairingCode(""); setCodeSeconds(0); requestPairingCode(); }}
              style={{width:"100%",background:"#fff",color:COLORS.primary,border:"2px solid "+COLORS.primary,borderRadius:12,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              Get a new code
            </button>
          </>
        )}

        <button onClick={()=>{setMethod(""); setPairingCode(""); setPhoneInput(""); setPairingError("");}}
          style={{marginTop:16,background:"none",border:"none",color:COLORS.subtext,cursor:"pointer",fontSize:13}}>Back</button>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#007AFF 0%,#5856D6 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:24,padding:32,width:"100%",maxWidth:380,boxShadow:"0 20px 60px rgba(0,0,0,0.2)",textAlign:"center"}}>
        <img src="/logo.png" alt="JediChatAI" style={{width:80,height:80,borderRadius:20,marginBottom:8,objectFit:"contain"}}/>
        <div style={{fontSize:22,fontWeight:800,color:COLORS.primary,marginBottom:4}}>Connect WhatsApp</div>
        <div style={{fontSize:13,color:COLORS.subtext,marginBottom:16}}>Scan once, connected forever!</div>
        {qrCode ? (
          <>
            <img src={qrCode} alt="WhatsApp QR Code" style={{width:"100%",borderRadius:12,marginBottom:16}}/>
            <div style={{background:"#F0F7FF",borderRadius:12,padding:12,marginBottom:16}}>
              <div style={{fontSize:13,fontWeight:600,color:COLORS.primary,marginBottom:4}}>How to scan:</div>
              <div style={{fontSize:12,color:COLORS.text,textAlign:"left",lineHeight:1.8}}>
                1. Open WhatsApp on your phone<br/>
                2. Tap ⋮ Menu → Linked Devices<br/>
                3. Tap "Link a Device"<br/>
                4. Point camera at QR code above
              </div>
            </div>
            <div style={{fontSize:12,color:COLORS.subtext}}>⏳ Waiting for you to scan...</div>
          </>
        ) : (
          <div style={{padding:32}}>
            <div style={{fontSize:32,marginBottom:8}}>⏳</div>
            <div style={{fontSize:14,color:COLORS.subtext}}>{status === "error" ? "Connection error - retrying..." : status === "starting" ? "Starting WhatsApp service..." : "Generating QR code..."}</div>
          </div>
        )}
        <button onClick={()=>setAccountType("")} style={{marginTop:16,background:"none",border:"none",color:COLORS.subtext,cursor:"pointer",fontSize:13}}>← Change account type</button>
      </div>
    </div>
  );
}

function KeyboardPage() {
  const [inputMessage, setInputMessage] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [suggestions, setSuggestions] = React.useState([]);
  const [liberianMode, setLiberianMode] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [copied, setCopied] = React.useState("");
  const [recentConvos, setRecentConvos] = React.useState([]);
  const [tone, setTone] = React.useState(localStorage.getItem("jedichatai_tone")||"friendly");
  const [showHistory, setShowHistory] = React.useState(false);
  const [analysisResult, setAnalysisResult] = React.useState(null);
  const [analysisLoading, setAnalysisLoading] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("text");
  const API = "https://app.jedichatai.com";
  const token = localStorage.getItem("jedichatai_token");

  const analyzeLink = async (text) => {
    if (!text.trim()) return;
    setAnalysisLoading(true); setAnalysisResult(null);
    try {
      const res = await fetch(API + "/api/v1/analyze/link", {
        method: "POST",
        headers: {"Content-Type": "application/json", "Authorization": "Bearer " + token},
        body: JSON.stringify({text})
      });
      const d = await res.json();
      setAnalysisResult({type: "link", ...d});
      if (d.reply_suggestion) setSuggestions([{text: d.reply_suggestion, tone: "Smart Reply"}]);
    } catch(e) { console.error(e); }
    setAnalysisLoading(false);
  };

  const analyzePDF = async (file) => {
    setAnalysisLoading(true); setAnalysisResult(null);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(",")[1];
        const res = await fetch(API + "/api/v1/analyze/pdf", {
          method: "POST",
          headers: {"Content-Type": "application/json", "Authorization": "Bearer " + token},
          body: JSON.stringify({pdf_base64: base64})
        });
        const d = await res.json();
        setAnalysisResult({type: "pdf", ...d});
        if (d.reply_suggestion) setSuggestions([{text: d.reply_suggestion, tone: "PDF Reply"}]);
        if (d.summary) setInputMessage(d.summary);
        setAnalysisLoading(false);
      };
      reader.readAsDataURL(file);
    } catch(e) { setAnalysisLoading(false); }
  };

  const analyzeImage = async (file) => {
    setAnalysisLoading(true); setAnalysisResult(null);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(",")[1];
        const mediaType = file.type || "image/jpeg";
        const res = await fetch(API + "/api/v1/analyze/image", {
          method: "POST",
          headers: {"Content-Type": "application/json", "Authorization": "Bearer " + token},
          body: JSON.stringify({image_base64: base64, media_type: mediaType})
        });
        const d = await res.json();
        setAnalysisResult({type: "image", ...d});
        if (d.reply_suggestion) setSuggestions([{text: d.reply_suggestion, tone: "Image Reply"}]);
        setAnalysisLoading(false);
      };
      reader.readAsDataURL(file);
    } catch(e) { setAnalysisLoading(false); }
  };

  React.useEffect(() => {
    // Load recent conversations for quick select
    try {
      const convos = JSON.parse(localStorage.getItem("jedichatai_convos") || "[]");
      setRecentConvos(convos.slice(0, 5));
    } catch(e) {}
  }, []);

  const generateReplies = async () => {
    if (!inputMessage.trim()) return;
    setLoading(true); setSuggestions([]);
    try {
      await getToken();
      const styleProfile = (() => { try { return JSON.parse(localStorage.getItem("jedichatai_style_profile")||"null"); } catch(e) { return null; } })();
      const res = await fetch(API + "/api/v1/keyboard/suggest", {
        method: "POST",
        headers: {"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({
          message: inputMessage,
          liberian_mode: liberianMode,
          contact_name: contactName,
          tone: tone,
          style_profile: styleProfile,
          context: contactName ? "Message from " + contactName : ""
        })
      });
      if (res.status === 429) {
        const d = await res.json();
        alert(d.detail || "Daily limit reached. Please upgrade your plan.");
        return;
      }
      const data = await res.json();
      setSuggestions(data.suggestions || []);
      // Store in message history
      if (data.suggestions && data.suggestions.length > 0) {
        try {
          const history = JSON.parse(localStorage.getItem("jedichatai_history") || "[]");
          history.unshift({
            date: new Date().toISOString(),
            message: inputMessage,
            contact: contactName,
            replies: data.suggestions,
          });
          localStorage.setItem("jedichatai_history", JSON.stringify(history.slice(0, 100)));
        } catch(e) {}
      }
    } catch(e) { console.error(e); alert("Error generating replies. Please try again."); }
    finally { setLoading(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {
      const el = document.createElement("textarea");
      el.value = text; document.body.appendChild(el); el.select();
      document.execCommand("copy"); document.body.removeChild(el);
    });
    setCopied(text);
    setTimeout(() => setCopied(""), 2000);
  };

  const tones = [
    {value:"professional", label:"Professional"},
    {value:"friendly", label:"Friendly"},
    {value:"casual", label:"Casual"},
    {value:"formal", label:"Formal"},
    {value:"empathetic", label:"Empathetic"},
  ];

  return (
    <div style={{paddingBottom:80,background:COLORS.bg,minHeight:"100vh"}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:24,fontWeight:800,color:COLORS.primary}}>AI Keyboard</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Smart replies, PDF reader, image detector, link scanner</div>
      </div>

      {/* Tab Selector */}
      <div style={{background:COLORS.card,padding:"12px 16px",borderBottom:"1px solid "+COLORS.border,display:"flex",gap:8,overflowX:"auto"}}>
        {[
          {id:"text", label:"Text Reply", icon:<MessageCircle size={14}/>},
          {id:"link", label:"Link Scanner", icon:<Shield size={14}/>},
          {id:"pdf", label:"PDF Reader", icon:<FileText size={14}/>},
          {id:"image", label:"Image Detect", icon:<Eye size={14}/>},
        ].map(tab=>(
          <button key={tab.id} onClick={()=>{setActiveTab(tab.id);setAnalysisResult(null);setSuggestions([]);}}
            style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",borderRadius:20,border:"none",background:activeTab===tab.id?COLORS.primary:"#f0f0f0",color:activeTab===tab.id?"#fff":COLORS.subtext,fontWeight:activeTab===tab.id?700:500,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      <div style={{padding:16}}>

      {/* LINK SCANNER TAB */}
      {activeTab==="link" && (
        <div>
          <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:8}}>PASTE MESSAGE WITH LINK</div>
            <textarea style={{width:"100%",minHeight:100,padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,resize:"vertical",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:COLORS.bg,color:COLORS.text}}
              placeholder="Paste the message containing a link here..."
              value={inputMessage} onChange={e=>setInputMessage(e.target.value)}/>
            <button onClick={()=>analyzeLink(inputMessage)} disabled={analysisLoading||!inputMessage.trim()}
              style={{width:"100%",marginTop:10,background:analysisLoading?"#ccc":COLORS.danger,color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
              {analysisLoading?"Scanning...":"Scan for Fraud"}
            </button>
          </div>
          {analysisResult?.type==="link" && (
            <div style={{background:COLORS.card,borderRadius:16,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
                <div style={{width:12,height:12,borderRadius:"50%",background:analysisResult.overall_risk==="high"?COLORS.danger:analysisResult.overall_risk==="medium"?"#FF9500":COLORS.success}}/>
                <div style={{fontSize:16,fontWeight:800,color:analysisResult.overall_risk==="high"?COLORS.danger:analysisResult.overall_risk==="medium"?"#FF9500":COLORS.success}}>
                  {analysisResult.overall_risk==="high"?"HIGH RISK - Likely Fraud":analysisResult.overall_risk==="medium"?"MEDIUM RISK - Be Careful":"SAFE LINK"}
                </div>
              </div>
              {analysisResult.results?.map((r,i)=>(
                <div key={i} style={{background:r.risk_level==="high"?"#FFF0F0":r.risk_level==="medium"?"#FFF5E6":"#F0FFF4",borderRadius:12,padding:12,marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:4}}>{r.url}</div>
                  {r.reasons?.map((reason,j)=>(
                    <div key={j} style={{fontSize:11,color:r.risk_level==="high"?COLORS.danger:"#FF9500",marginBottom:2}}>• {reason}</div>
                  ))}
                </div>
              ))}
              {analysisResult.reply_suggestion && (
                <div onClick={()=>{navigator.clipboard.writeText(analysisResult.reply_suggestion);setCopied("link");setTimeout(()=>setCopied(""),2000);}}
                  style={{background:COLORS.bg,borderRadius:12,padding:14,marginTop:8,cursor:"pointer",border:"2px solid "+(copied==="link"?COLORS.success:COLORS.border)}}>
                  <div style={{fontSize:11,fontWeight:700,color:COLORS.primary,marginBottom:4}}>{copied==="link"?"COPIED!":"SUGGESTED REPLY - TAP TO COPY"}</div>
                  <div style={{fontSize:14,color:COLORS.text}}>{analysisResult.reply_suggestion}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* PDF READER TAB */}
      {activeTab==="pdf" && (
        <div>
          <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:8}}>UPLOAD PDF DOCUMENT</div>
            <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,border:"2px dashed "+COLORS.border,borderRadius:12,cursor:"pointer",background:COLORS.bg}}>
              <FileText size={40} color={COLORS.primary} style={{marginBottom:12}}/>
              <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Tap to upload PDF</div>
              <div style={{fontSize:12,color:COLORS.subtext}}>Max 10MB</div>
              <input type="file" accept=".pdf" style={{display:"none"}} onChange={e=>e.target.files[0]&&analyzePDF(e.target.files[0])}/>
            </label>
          </div>
          {analysisLoading && (
            <div style={{background:COLORS.card,borderRadius:16,padding:32,textAlign:"center"}}>
              <FileText size={40} color={COLORS.primary} style={{marginBottom:12}}/>
              <div style={{color:COLORS.subtext}}>Reading PDF and generating reply...</div>
            </div>
          )}
          {analysisResult?.type==="pdf" && (
            <div style={{background:COLORS.card,borderRadius:16,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
              <div style={{fontSize:13,fontWeight:700,marginBottom:8}}>PDF Content ({analysisResult.pages} pages)</div>
              <div style={{background:COLORS.bg,borderRadius:10,padding:12,marginBottom:12,maxHeight:150,overflowY:"auto"}}>
                <div style={{fontSize:12,color:COLORS.subtext,lineHeight:1.6}}>{analysisResult.summary}</div>
              </div>
              {analysisResult.reply_suggestion && (
                <div onClick={()=>{navigator.clipboard.writeText(analysisResult.reply_suggestion);setCopied("pdf");setTimeout(()=>setCopied(""),2000);}}
                  style={{background:COLORS.bg,borderRadius:12,padding:14,cursor:"pointer",border:"2px solid "+(copied==="pdf"?COLORS.success:COLORS.border)}}>
                  <div style={{fontSize:11,fontWeight:700,color:COLORS.primary,marginBottom:4}}>{copied==="pdf"?"COPIED!":"SUGGESTED REPLY - TAP TO COPY"}</div>
                  <div style={{fontSize:14,color:COLORS.text}}>{analysisResult.reply_suggestion}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* IMAGE DETECTOR TAB */}
      {activeTab==="image" && (
        <div>
          <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:8}}>UPLOAD IMAGE TO ANALYZE</div>
            <label style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,border:"2px dashed "+COLORS.border,borderRadius:12,cursor:"pointer",background:COLORS.bg}}>
              <Eye size={40} color={COLORS.primary} style={{marginBottom:12}}/>
              <div style={{fontSize:14,fontWeight:600,marginBottom:4}}>Tap to upload image</div>
              <div style={{fontSize:12,color:COLORS.subtext}}>JPG, PNG, GIF, WEBP</div>
              <input type="file" accept="image/*" style={{display:"none"}} onChange={e=>e.target.files[0]&&analyzeImage(e.target.files[0])}/>
            </label>
          </div>
          {analysisLoading && (
            <div style={{background:COLORS.card,borderRadius:16,padding:32,textAlign:"center"}}>
              <Eye size={40} color={COLORS.primary} style={{marginBottom:12}}/>
              <div style={{color:COLORS.subtext}}>Analyzing image with AI...</div>
            </div>
          )}
          {analysisResult?.type==="image" && (
            <div style={{background:COLORS.card,borderRadius:16,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                <div style={{width:12,height:12,borderRadius:"50%",background:analysisResult.risk_level==="high"?COLORS.danger:analysisResult.risk_level==="medium"?"#FF9500":COLORS.success}}/>
                <div style={{fontSize:13,fontWeight:700}}>Risk: {(analysisResult.risk_level||"low").toUpperCase()}</div>
                <div style={{fontSize:12,color:COLORS.subtext,background:COLORS.bg,padding:"2px 8px",borderRadius:10}}>{analysisResult.type}</div>
              </div>
              <div style={{background:COLORS.bg,borderRadius:10,padding:12,marginBottom:12}}>
                <div style={{fontSize:13,color:COLORS.text,lineHeight:1.6}}>{analysisResult.description}</div>
              </div>
              {analysisResult.reply_suggestion && (
                <div onClick={()=>{navigator.clipboard.writeText(analysisResult.reply_suggestion);setCopied("image");setTimeout(()=>setCopied(""),2000);}}
                  style={{background:COLORS.bg,borderRadius:12,padding:14,cursor:"pointer",border:"2px solid "+(copied==="image"?COLORS.success:COLORS.border)}}>
                  <div style={{fontSize:11,fontWeight:700,color:COLORS.primary,marginBottom:4}}>{copied==="image"?"COPIED!":"SUGGESTED REPLY - TAP TO COPY"}</div>
                  <div style={{fontSize:14,color:COLORS.text}}>{analysisResult.reply_suggestion}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

        {/* Contact Name */}
        <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:8}}>CONTACT NAME (optional)</div>
          <input style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box",background:COLORS.bg,color:COLORS.text}}
            placeholder="Who sent this message?"
            value={contactName} onChange={e=>setContactName(e.target.value)}/>
        </div>

        {/* Tone Selector */}
        <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:10}}>REPLY TONE</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {tones.map(t=>(
              <button key={t.value} onClick={()=>{setTone(t.value);localStorage.setItem("jedichatai_tone",t.value);}}
                style={{padding:"6px 14px",borderRadius:20,border:"2px solid "+(tone===t.value?COLORS.primary:COLORS.border),background:tone===t.value?COLORS.primary:"transparent",color:tone===t.value?"#fff":COLORS.text,fontSize:12,fontWeight:600,cursor:"pointer"}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Message Input */}
        <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:8}}>PASTE MESSAGE</div>
          <textarea style={{width:"100%",minHeight:120,padding:"12px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,resize:"vertical",outline:"none",fontFamily:"inherit",boxSizing:"border-box",background:COLORS.bg,color:COLORS.text}}
            placeholder="Paste the WhatsApp message you received here..."
            value={inputMessage} onChange={e=>setInputMessage(e.target.value)}
            onKeyDown={e=>{if(e.ctrlKey&&e.key==="Enter")generateReplies();}}/>
          <div style={{display:"flex",gap:8,marginTop:10,alignItems:"center"}}>
            <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:COLORS.subtext,cursor:"pointer"}}>
              <input type="checkbox" checked={liberianMode} onChange={e=>setLiberianMode(e.target.checked)} style={{width:14,height:14}}/>
              Liberian Mode
            </label>
            <div style={{flex:1}}/>
            <button onClick={()=>setInputMessage("")} style={{background:"none",color:COLORS.subtext,border:"1px solid "+COLORS.border,borderRadius:8,padding:"8px 14px",fontSize:12,cursor:"pointer"}}>Clear</button>
            <button onClick={generateReplies} disabled={loading||!inputMessage.trim()}
              style={{background:loading||!inputMessage.trim()?"#ccc":COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px 20px",fontSize:14,fontWeight:700,cursor:loading||!inputMessage.trim()?"not-allowed":"pointer"}}>
              {loading ? "Generating..." : "Generate"}
            </button>
          </div>
          <div style={{fontSize:11,color:COLORS.subtext,marginTop:6}}>Tip: Press Ctrl+Enter to generate</div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{background:COLORS.card,borderRadius:16,padding:32,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:12}}>
              <Keyboard size={40} color={COLORS.primary}/>
            </div>
            <div style={{fontSize:14,color:COLORS.subtext}}>AI is analyzing the message and generating smart replies...</div>
          </div>
        )}

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div style={{background:COLORS.card,borderRadius:16,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext}}>AI SUGGESTIONS</div>
              <div style={{fontSize:11,color:COLORS.subtext}}>Tap to copy</div>
            </div>
            {suggestions.map((s,i)=>(
              <div key={i} onClick={()=>copyToClipboard(s.text)}
                style={{background:copied===s.text?"#E8F5E9":COLORS.bg,borderRadius:12,padding:14,marginBottom:8,cursor:"pointer",border:"2px solid "+(copied===s.text?COLORS.success:"transparent"),transition:"all 0.2s"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <div style={{fontSize:11,fontWeight:700,color:copied===s.text?COLORS.success:COLORS.primary,background:copied===s.text?"#E8F5E9":"#F0F7FF",padding:"2px 8px",borderRadius:10}}>
                    {copied===s.text?"COPIED!" : s.tone||"Reply "+(i+1)}
                  </div>
                  {copied===s.text ? <CheckCircle size={16} color={COLORS.success}/> : <Eye size={16} color={COLORS.subtext}/>}
                </div>
                <div style={{fontSize:14,lineHeight:1.6,color:COLORS.text}}>{s.text}</div>
              </div>
            ))}
            <div style={{marginTop:12,padding:"10px 14px",background:"#F0F7FF",borderRadius:10,fontSize:12,color:COLORS.primary,textAlign:"center"}}>
              Copy a reply, open WhatsApp and paste it
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && suggestions.length===0 && (
          <div style={{background:COLORS.card,borderRadius:16,padding:40,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"center",marginBottom:16}}>
              <Keyboard size={48} color={COLORS.border}/>
            </div>
            <div style={{fontSize:16,fontWeight:700,marginBottom:8}}>AI Keyboard Ready</div>
            <div style={{fontSize:13,color:COLORS.subtext,lineHeight:1.6}}>Paste any WhatsApp message above and get 3 smart AI replies instantly in your chosen tone!</div>
          </div>
        )}

      </div>
    </div>
  );
}















function WABAPage() {
  const [health, setHealth] = React.useState<any>(null);
  const [templates, setTemplates] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [testTo, setTestTo] = React.useState("");
  const [testMsg, setTestMsg] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [sendResult, setSendResult] = React.useState("");

  React.useEffect(() => {
    Promise.all([
      api.get("/api/v1/waba/health").catch(() => ({ data: { status: "unavailable" } })),
      api.get("/api/v1/waba/templates").catch(() => ({ data: { data: [] } }))
    ]).then(([h, t]) => {
      setHealth(h.data);
      setTemplates(t.data?.data || []);
      setLoading(false);
    });
  }, []);

  const sendTest = async () => {
    if (!testTo || !testMsg) { window.alert("Enter phone number and message"); return; }
    setSending(true);
    setSendResult("");
    try {
      const res = await api.post("/api/v1/waba/send", {
        to: testTo.replace(/\D/g, ""),
        message: testMsg
      });
      setSendResult("Message sent successfully!");
      setTestMsg("");
    } catch(e: any) {
      setSendResult("" + (e?.response?.data?.detail || "Send failed"));
    }
    setSending(false);
  };

  const sendHelloWorld = async () => {
    if (!testTo) { window.alert("Enter a phone number first"); return; }
    setSending(true);
    try {
      await api.post("/api/v1/waba/send-template", {
        to: testTo.replace(/\D/g, ""),
        template: "hello_world",
        lang: "en_US"
      });
      setSendResult("Hello World template sent!");
    } catch(e: any) {
      setSendResult("" + (e?.response?.data?.detail || "Send failed"));
    }
    setSending(false);
  };

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:"#25D366"}}>WhatsApp Business API</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Official Meta WABA integration</div>
      </div>
      <div style={{padding:16}}>

        {/* Status Card */}
        <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>📡 SERVICE STATUS</div>
          {loading ? (
            <div style={{color:COLORS.subtext,fontSize:13}}>Loading...</div>
          ) : (
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <div style={{width:12,height:12,borderRadius:6,background:health?.status==="healthy"?COLORS.success:COLORS.danger}}/>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:COLORS.text}}>
                  {health?.status==="healthy"?"WABA Service Online":"WABA Service Offline"}
                </div>
                {health?.phone_id && <div style={{fontSize:12,color:COLORS.subtext}}>Phone ID: {health.phone_id}</div>}
              </div>
            </div>
          )}
        </div>

        {/* Account Info */}
        <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>💼 ACCOUNT INFO</div>
          {[
            {label:"Business Account ID", value:"1630499704861791"},
            {label:"Phone Number", value:"+1 (555) 646-1073 (Test)"},
            {label:"App ID", value:"937072615783573"},
            {label:"Mode", value:"Development (Test only)"},
            {label:"Webhook", value:"https://app.jedichatai.com/waba/webhook"},
          ].map((item,i) => (
            <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid "+COLORS.border,flexWrap:"wrap" as const,gap:4}}>
              <span style={{fontSize:12,color:COLORS.subtext}}>{item.label}</span>
              <span style={{fontSize:12,fontWeight:600,color:COLORS.text}}>{item.value}</span>
            </div>
          ))}
        </div>

        {/* Send Test Message */}
        <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>📤 SEND TEST MESSAGE</div>
          <input value={testTo} onChange={e=>setTestTo(e.target.value)}
            placeholder="Recipient phone (e.g. 14384641730)"
            style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box" as const,marginBottom:8,color:COLORS.text,background:COLORS.bg}}/>
          <textarea value={testMsg} onChange={e=>setTestMsg(e.target.value)}
            placeholder="Message text..."
            style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box" as const,marginBottom:8,minHeight:80,color:COLORS.text,background:COLORS.bg,resize:"vertical" as const}}/>
          {sendResult && (
            <div style={{padding:"8px 12px",borderRadius:8,background:sendResult.startsWith("")?"#E8F5E9":"#FFF0F0",color:sendResult.startsWith("")?COLORS.success:COLORS.danger,fontSize:13,marginBottom:8}}>
              {sendResult}
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <button onClick={sendTest} disabled={sending}
              style={{flex:1,background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer",opacity:sending?0.6:1}}>
              {sending?"Sending...":"Send Text"}
            </button>
            <button onClick={sendHelloWorld} disabled={sending}
              style={{flex:1,background:"#25D366",color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer",opacity:sending?0.6:1}}>
              Hello World
            </button>
          </div>
        </div>

        {/* Templates */}
        <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>📋 MESSAGE TEMPLATES ({templates.length})</div>
          {templates.length === 0 ? (
            <div style={{fontSize:13,color:COLORS.subtext,textAlign:"center",padding:16}}>No templates yet. Create them in Meta Business Manager.</div>
          ) : templates.map((t:any, i:number) => (
            <div key={i} style={{padding:"10px 0",borderBottom:"1px solid "+COLORS.border}}>
              <div style={{fontSize:13,fontWeight:700,color:COLORS.text}}>{t.name}</div>
              <div style={{fontSize:11,color:COLORS.subtext}}>{t.status} • {t.language}</div>
            </div>
          ))}
        </div>

        {/* Next steps */}
        <div style={{background:"#FFF3E0",borderRadius:16,padding:16,border:"1px solid #FFB74D"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#E65100",marginBottom:8}}>⚠️ Production Requirements</div>
          {[
            "Complete Step 3: Business Verification on Meta",
            "Add a real phone number (not test number)",
            "Publish app to go live",
            "Create approved message templates",
          ].map((s,i) => (
            <div key={i} style={{fontSize:12,color:"#BF360C",padding:"4px 0"}}>• {s}</div>
          ))}
        </div>

      </div>
    </div>
  );
}

function AILearningPage() {
  const [samples, setSamples] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("jedichatai_style_samples")||"[]"); } catch(e) { return []; }
  });
  const [newSample, setNewSample] = React.useState("");
  const [styleProfile, setStyleProfile] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("jedichatai_style_profile")||"null") || {
      tone: "not analyzed yet", formality: "unknown", avgLength: 0, keywords: [], analyzed: false
    }; } catch(e) { return {tone:"not analyzed yet",formality:"unknown",avgLength:0,keywords:[],analyzed:false}; }
  });
  const [analyzing, setAnalyzing] = React.useState(false);
  const [trainingLevel, setTrainingLevel] = React.useState(() => parseInt(localStorage.getItem("jedichatai_training_level")||"0"));

  const saveSamples = (updated) => { setSamples(updated); localStorage.setItem("jedichatai_style_samples", JSON.stringify(updated)); };

  const addSample = () => {
    if (!newSample.trim()) return;
    const updated = [...samples, {id:Date.now(), text:newSample.trim(), date:new Date().toLocaleDateString()}];
    saveSamples(updated);
    setNewSample("");
    const level = Math.min(100, Math.floor(updated.length / 5 * 10));
    setTrainingLevel(level);
    localStorage.setItem("jedichatai_training_level", level.toString());
  };

  const deleteSample = (id) => saveSamples(samples.filter(s=>s.id!==id));

  const analyzeStyle = async () => {
    if (samples.length < 3) { alert("Add at least 3 message samples to analyze your style!"); return; }
    setAnalyzing(true);
    try {
      const token = localStorage.getItem("jedichatai_token");
      const url = window.location.hostname === "localhost" ? "http://localhost:8000/api/v1/keyboard/suggest" : "https://app.jedichatai.com/api/v1/keyboard/suggest";
      const sampleText = samples.map(s=>s.text).join(" | ");
      const res = await fetch(url, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({message: "Analyze the writing style of these messages and describe in one sentence the tone, formality level, and typical length. Messages: " + sampleText})
      });
      const data = await res.json();
      const analysis = data.suggestions?.[0]?.text || "Style analyzed successfully.";
      const words = sampleText.split(" ").length / samples.length;
      const profile = {
        tone: analysis,
        formality: words > 20 ? "Detailed & thorough" : words > 10 ? "Balanced" : "Brief & concise",
        avgLength: Math.round(words),
        keywords: [...new Set(sampleText.toLowerCase().split(/\W+/).filter(w=>w.length>4))].slice(0,8),
        analyzed: true,
        date: new Date().toLocaleDateString()
      };
      setStyleProfile(profile);
      localStorage.setItem("jedichatai_style_profile", JSON.stringify(profile));
    } catch(e) { alert("Could not analyze style."); }
    finally { setAnalyzing(false); }
  };

  const levelColor = trainingLevel < 30 ? COLORS.danger : trainingLevel < 70 ? "#FF9500" : COLORS.success;
  const levelLabel = trainingLevel < 30 ? "Beginner" : trainingLevel < 70 ? "Learning" : "Advanced";

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:"#fff",padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>AI Learning</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Teach AI your writing style</div>
      </div>

      <div style={{padding:16}}>
        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>{<Brain size={14}/>} TRAINING LEVEL</div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <span style={{fontSize:15,fontWeight:600}}>AI Style Training</span>
            <span style={{fontSize:14,fontWeight:700,color:levelColor}}>{trainingLevel}% {levelLabel}</span>
          </div>
          <div style={{height:12,background:"#F0F0F0",borderRadius:6,marginBottom:8}}>
            <div style={{height:12,borderRadius:6,background:levelColor,width:trainingLevel+"%",transition:"width 0.5s"}}/>
          </div>
          <div style={{fontSize:12,color:COLORS.subtext}}>{samples.length} samples added · Add {Math.max(0,5-samples.length%5)} more to level up</div>
        </div>

        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>✍️ ADD YOUR MESSAGE SAMPLES</div>
          <div style={{fontSize:12,color:COLORS.subtext,marginBottom:8}}>Add examples of how YOU write messages. The more you add, the better AI learns your style!</div>
          <textarea placeholder="Paste one of your typical WhatsApp messages here..." value={newSample} onChange={e=>setNewSample(e.target.value)}
            style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box",minHeight:80,resize:"vertical",marginBottom:8}}/>
          <button onClick={addSample} disabled={!newSample.trim()}
            style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:14,fontWeight:600,cursor:"pointer"}}>
            + Add Sample
          </button>
        </div>

        {samples.length > 0 && (
          <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext}}>📝 YOUR SAMPLES ({samples.length})</div>
              <button onClick={analyzeStyle} disabled={analyzing}
                style={{background:COLORS.purple||"#5856D6",color:"#fff",border:"none",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                {analyzing?"Analyzing...":"Analyze Style"}
              </button>
            </div>
            {samples.slice(-5).reverse().map(s=>(
              <div key={s.id} style={{padding:"8px 0",borderBottom:"1px solid "+COLORS.border,display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,color:COLORS.text,lineHeight:1.4}}>{s.text.substring(0,80)}{s.text.length>80?"...":""}</div>
                  <div style={{fontSize:11,color:COLORS.subtext,marginTop:2}}>{s.date}</div>
                </div>
                <button onClick={()=>deleteSample(s.id)} style={{background:"none",border:"none",color:COLORS.danger,fontSize:14,cursor:"pointer",flexShrink:0}}>✕</button>
              </div>
            ))}
          </div>
        )}

        {styleProfile.analyzed && (
          <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>{<Target size={14}/>} YOUR STYLE PROFILE</div>
            <div style={{background:"#F5F0FF",borderRadius:12,padding:12,marginBottom:10,border:"1px solid #C4B5FD"}}>
              <div style={{fontSize:12,fontWeight:600,color:"#5856D6",marginBottom:4}}>{<Brain size={14}/>} AI Analysis</div>
              <div style={{fontSize:13,lineHeight:1.5}}>{styleProfile.tone}</div>
            </div>
            {[
              {label:"Writing Style", value:styleProfile.formality},
              {label:"Avg Message Length", value:styleProfile.avgLength+" words"},
              {label:"Analyzed On", value:styleProfile.date},
            ].map(item=>(
              <div key={item.label} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid "+COLORS.border}}>
                <span style={{fontSize:13,color:COLORS.subtext}}>{item.label}</span>
                <span style={{fontSize:13,fontWeight:600,color:COLORS.text}}>{item.value}</span>
              </div>
            ))}
            {styleProfile.keywords.length > 0 && (
              <div style={{marginTop:10}}>
                <div style={{fontSize:12,color:COLORS.subtext,marginBottom:6}}>Common keywords in your messages:</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {styleProfile.keywords.map(k=>(
                    <span key={k} style={{background:COLORS.primary+"20",color:COLORS.primary,padding:"3px 10px",borderRadius:20,fontSize:12,fontWeight:600}}>{k}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{background:"linear-gradient(135deg,#5856D6,#007AFF)",borderRadius:16,padding:16,color:"#fff"}}>
          <div style={{fontSize:16,fontWeight:700,marginBottom:4}}>{<Rocket size={14}/>} Full AI Learning</div>
          <div style={{fontSize:13,opacity:0.9,marginBottom:12}}>Upgrade to Pro to have AI automatically learn from every approved reply and continuously improve its suggestions to match your exact style.</div>
          <button onClick={()=>window.open("https://jedichatai.com/#pricing")}
            style={{background:"#fff",color:"#5856D6",border:"none",borderRadius:10,padding:"10px 20px",fontSize:14,fontWeight:700,cursor:"pointer",width:"100%"}}>
            Upgrade to Pro: $9.99/mo
          </button>
        </div>
      </div>
    </div>
  );
}

function VoicePage() {
  const [transcript, setTranscript] = useState("");
  const [replySuggestion, setReplySuggestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recogRef, setRecogRef] = useState<any>(null);
  const [copied, setCopied] = useState("");
  const [tab, setTab] = useState("record");

  const startRecording = () => {
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) { window.alert("Speech recognition not supported. Try Chrome, or use the Upload tab."); return; }
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = "en-US";
    r.onresult = (e: any) => {
      const t = Array.from(e.results).map((x: any) => x[0].transcript).join(" ");
      setTranscript(t);
    };
    r.onend = () => { setRecording(false); if (transcript.trim()) transcribeText(transcript); };
    r.start();
    setRecogRef(r);
    setRecording(true);
  };

  const stopRecording = () => {
    if (recogRef) { recogRef.stop(); setRecogRef(null); }
    setRecording(false);
  };

  const transcribeText = async (text: string) => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const res = await api.post("/api/v1/keyboard/suggest", { message: "Voice note: " + text, tone: "friendly" });
      setReplySuggestion(res.data?.suggestions?.[0]?.text || "");
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const uploadAndTranscribe = async (file: File) => {
    setLoading(true);
    setTranscript("");
    setReplySuggestion("");
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = (reader.result as string).split(",")[1];
        const ext = file.name.split(".").pop() || "m4a";
        const res = await api.post("/api/v1/voice/transcribe", { audio_base64: b64, audio_format: ext, tz_offset_minutes: -new Date().getTimezoneOffset() });
        setTranscript(res.data.transcript || "");
        setReplySuggestion(res.data.reply_suggestion || "");
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch(e: any) {
      window.alert(e?.response?.data?.detail || "Transcription failed");
      setLoading(false);
    }
  };

  const copy = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 2000);
  };

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Voice Notes</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Transcribe and reply to voice messages</div>
      </div>

      <div style={{display:"flex",borderBottom:"1px solid "+COLORS.border,background:COLORS.card}}>
        {[{key:"record",label:"Record"},{key:"upload",label:"Upload File"}].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{flex:1,padding:"14px",border:"none",background:"none",borderBottom:tab===t.key?"2px solid "+COLORS.primary:"2px solid transparent",color:tab===t.key?COLORS.primary:COLORS.subtext,fontSize:13,fontWeight:600,cursor:"pointer"}}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{padding:16,maxWidth:640,margin:"0 auto"}}>
        {tab === "record" && (
          <div style={{background:COLORS.card,borderRadius:16,padding:24,marginBottom:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{textAlign:"center",marginBottom:20}}>
              <button onClick={recording ? stopRecording : startRecording} disabled={loading}
                style={{width:100,height:100,borderRadius:50,background:recording?"#FF3B3015":COLORS.primary+"15",border:"3px solid "+(recording?"#FF3B30":COLORS.primary),cursor:loading?"not-allowed":"pointer",fontSize:44,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px"}}>
                {loading ? "⏳" : recording ? "⏹" : ""}
              </button>
              <div style={{fontSize:15,fontWeight:700,color:COLORS.text,marginBottom:4}}>
                {loading ? "Transcribing..." : recording ? "Recording, click to stop & transcribe" : "Click to record"}
              </div>
              <div style={{fontSize:12,color:COLORS.subtext,marginBottom:12}}>
                {recording ? "Speak clearly into your microphone" : "Uses browser speech recognition (Chrome/Edge recommended)"}
              </div>
            </div>
            {!recording && !loading && (
              <div style={{background:COLORS.bg,borderRadius:10,padding:12,borderLeft:"3px solid "+COLORS.warning}}>
                <div style={{fontSize:12,fontWeight:700,color:COLORS.warning,marginBottom:4}}>⚠️ Browser Limitation</div>
                <div style={{fontSize:12,color:COLORS.subtext,lineHeight:1.6}}>
                  Browser recording works in Chrome and Edge only, and requires microphone permission.
                  For best results, use the <strong>Upload tab</strong>: record a voice note on your phone, save it, then upload here for AI transcription via Whisper.
                </div>
              </div>
            )}
            {recording && (
              <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:8}}>
                <div style={{width:8,height:8,borderRadius:4,background:"#FF3B30",animation:"pulse 1s infinite"}}/>
                <div style={{fontSize:13,color:"#FF3B30",fontWeight:600}}>Live: speak now</div>
              </div>
            )}
          </div>
        )}

        {tab === "upload" && (
          <div style={{background:COLORS.card,borderRadius:16,padding:24,textAlign:"center",marginBottom:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <input type="file" accept="audio/*" id="voice-upload" style={{display:"none"}}
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadAndTranscribe(f); e.target.value=""; }}/>
            <label htmlFor="voice-upload" style={{display:"block",cursor:loading?"not-allowed":"pointer"}}>
              <div style={{width:100,height:100,borderRadius:50,background:COLORS.primary+"15",border:"3px solid "+COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 16px",fontSize:44}}>
                {loading ? "⏳" : ""}
              </div>
              <div style={{fontSize:15,fontWeight:700,color:COLORS.text,marginBottom:4}}>
                {loading ? "Transcribing with Whisper AI..." : "Click to upload audio file"}
              </div>
              <div style={{fontSize:12,color:COLORS.subtext}}>Supports mp3, m4a, wav, ogg, webm</div>
            </label>
          </div>
        )}

        {transcript && (
          <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase"}}>Transcription</div>
              <button onClick={() => copy(transcript,"t")} style={{background:"none",border:"none",color:copied==="t"?COLORS.success:COLORS.primary,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                {copied==="t"?"Copied":"Copy"}
              </button>
            </div>
            <div style={{fontSize:14,color:COLORS.text,lineHeight:1.6}}>{transcript}</div>
          </div>
        )}

        {replySuggestion && (
          <div style={{background:"#F0F7FF",borderRadius:16,padding:16,marginBottom:12,border:"1px solid "+COLORS.primary+"30",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:12,fontWeight:700,color:COLORS.primary,textTransform:"uppercase"}}>AI Reply Suggestion</div>
              <button onClick={() => copy(replySuggestion,"r")} style={{background:"none",border:"none",color:copied==="r"?COLORS.success:COLORS.primary,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                {copied==="r"?"Copied":"Copy"}
              </button>
            </div>
            <div style={{fontSize:14,color:COLORS.text,lineHeight:1.6,marginBottom:12}}>{replySuggestion}</div>
          </div>
        )}

        {!transcript && !loading && (
          <div style={{background:COLORS.card,borderRadius:14,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:13,fontWeight:700,color:COLORS.text,marginBottom:8}}>How it works</div>
            <div style={{fontSize:12,color:COLORS.subtext,lineHeight:1.8}}>
              1. Record using your mic or upload an audio file<br/>
              2. AI transcribes it instantly using OpenAI Whisper<br/>
              3. Get an AI reply suggestion<br/>
              4. Copy and paste into WhatsApp
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function AccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    // Load connected accounts
    api.get("/api/v1/whatsapp/accounts").then(r => {
      setAccounts(r.data.accounts || []);
    }).catch(() => {
      // Fallback - show current session
      const _wemail = localStorage.getItem("jedichatai_email") || "default";
      api.get("/api/v1/whatsapp/status/" + encodeURIComponent(_wemail)).then(r => {
        if (r.data.status === "connected") {
          setAccounts([{id:"default", phone: r.data.phone || "Connected", status:"connected", is_active:true}]);
        }
      }).catch(() => {});
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>WhatsApp Accounts</div>
          <div style={{fontSize:13,color:COLORS.subtext}}>Manage connected numbers</div>
        </div>
      </div>
      <div style={{padding:16}}>
        {loading ? <div style={{textAlign:"center",padding:40,color:COLORS.subtext}}>Loading...</div> : (
          <>
            {accounts.length === 0 ? (
              <div style={{textAlign:"center",padding:48}}>
                <div style={{fontSize:64,marginBottom:16}}>📱</div>
                <div style={{fontSize:18,fontWeight:700,color:COLORS.text,marginBottom:8}}>No WhatsApp Connected</div>
                <div style={{fontSize:14,color:COLORS.subtext,marginBottom:24}}>Connect your WhatsApp to start using JediChatAI</div>
                <button onClick={()=>navigate("/")} style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:14,padding:"14px 32px",fontSize:15,fontWeight:700,cursor:"pointer"}}>
                  Connect WhatsApp
                </button>
              </div>
            ) : accounts.map((acc:any) => (
              <div key={acc.id} style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"2px solid "+(acc.is_active?COLORS.primary:COLORS.border)}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div style={{width:48,height:48,borderRadius:24,background:"#25D36620",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24}}>📱</div>
                  <div style={{flex:1}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <div style={{fontSize:15,fontWeight:700,color:COLORS.text}}>{acc.phone || "WhatsApp"}</div>
                      {acc.is_active && <span style={{background:COLORS.primary+"20",color:COLORS.primary,fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10}}>ACTIVE</span>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{width:8,height:8,borderRadius:4,background:acc.status==="connected"?COLORS.success:COLORS.danger}}/>
                      <div style={{fontSize:12,color:COLORS.subtext}}>{acc.status==="connected"?"Connected":"Disconnected"}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div style={{background:"#F0F7FF",borderRadius:16,padding:16,marginTop:8,border:"1px dashed "+COLORS.primary}}>
              <div style={{fontSize:14,fontWeight:700,color:COLORS.primary,marginBottom:4}}>{<Smartphone size={14}/>} Multiple Accounts (Coming Soon)</div>
              <div style={{fontSize:12,color:COLORS.subtext,lineHeight:1.6}}>Connect multiple WhatsApp numbers and switch between them. Each number gets its own AI settings, templates and contact groups.</div>
            </div>
          </>
        )}
      </div>
    </div>
  );
  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Multiple Accounts</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Manage multiple WhatsApp numbers</div>
      </div>
      <div style={{padding:24,maxWidth:520,margin:"0 auto"}}>
        <div style={{background:COLORS.card,borderRadius:20,padding:32,textAlign:"center",boxShadow:"0 4px 20px rgba(0,0,0,0.08)"}}>
          <div style={{fontSize:56,marginBottom:16}}>📱</div>
          <div style={{fontSize:22,fontWeight:800,marginBottom:8,color:COLORS.text}}>Multiple Accounts</div>
          <div style={{background:"#FF950020",borderRadius:10,padding:"8px 16px",display:"inline-block",marginBottom:16}}>
            <span style={{background:"#E8F5E9",color:"#34C759",fontWeight:700,fontSize:12,padding:"2px 8px",borderRadius:10}}>v2.0</span>
          </div>
          <div style={{fontSize:14,color:COLORS.subtext,lineHeight:1.6,marginBottom:24}}>Manage multiple WhatsApp numbers from one dashboard. Perfect for businesses with separate lines for sales, support, and operations.</div>
          {["Add unlimited WhatsApp numbers","Switch between accounts instantly","Separate AI settings per number","Unified conversation view","Per-account analytics","Team assignment per account"].map((f,i) => (
            <div key={i} style={{textAlign:"left",padding:"6px 0",fontSize:13,color:COLORS.text,borderBottom:"1px solid "+COLORS.border}}>{f}</div>
          ))}
          <a href="mailto:support@jedichatai.com?subject=Multiple%20Accounts%20Waitlist"
            style={{display:"block",marginTop:20,background:COLORS.primary,color:"#fff",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,textDecoration:"none",textAlign:"center"}}>
            Join the Waitlist
          </a>
        </div>
      </div>
    </div>
  );
}

function WeeklyReportPage() {
  const [email, setEmail] = React.useState(() => localStorage.getItem("jedichatai_report_email")||"");
  const [enabled, setEnabled] = React.useState(() => localStorage.getItem("jedichatai_report_enabled")==="true");
  const [day, setDay] = React.useState(() => localStorage.getItem("jedichatai_report_day")||"monday");
  const [saved, setSaved] = React.useState(false);

  const history = React.useMemo(() => { try { return JSON.parse(localStorage.getItem("jedichatai_history")||"[]"); } catch(e) { return []; } }, []);
  const sentiment = React.useMemo(() => { try { return JSON.parse(localStorage.getItem("jedichatai_sentiment")||"[]"); } catch(e) { return []; } }, []);

  const saveSettings = () => {
    localStorage.setItem("jedichatai_report_email", email);
    localStorage.setItem("jedichatai_report_enabled", enabled);
    localStorage.setItem("jedichatai_report_day", day);
    setSaved(true);
    setTimeout(()=>setSaved(false), 2000);
  };

  const sendPreview = () => {
    if (!email) { alert("Please enter your email first!"); return; }
    alert("Preview report sent to " + email + "!\n\nIn production this would send a real email with your weekly stats.");
  };

  const highRisk = sentiment.filter(s=>s.riskLevel==="high").length;
  const topEmotion = Object.entries(sentiment.reduce((acc,s)=>{acc[s.emotion]=(acc[s.emotion]||0)+1;return acc;},{})).sort((a,b)=>b[1]-a[1])[0];

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:"#fff",padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Weekly Reports</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Get weekly stats in your inbox</div>
      </div>

      <div style={{padding:16}}>
        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>{<Settings size={14}/>} REPORT SETTINGS</div>

          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid "+COLORS.border,marginBottom:12}}>
            <div>
              <div style={{fontSize:14,fontWeight:600}}>Enable Weekly Reports</div>
              <div style={{fontSize:12,color:COLORS.subtext}}>Receive stats every week</div>
            </div>
            <div onClick={()=>setEnabled(!enabled)} style={{width:44,height:26,borderRadius:13,background:enabled?COLORS.success:COLORS.subtext,cursor:"pointer",position:"relative",flexShrink:0}}>
              <div style={{width:20,height:20,borderRadius:10,background:"#fff",position:"absolute",top:3,left:enabled?21:3,transition:"left 0.2s"}}/>
            </div>
          </div>

          <div style={{marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:600,color:COLORS.subtext,marginBottom:6}}>{<Mail size={14}/>} Report Email</div>
            <input type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)}
              style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box"}}/>
          </div>

          <div style={{marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:600,color:COLORS.subtext,marginBottom:6}}>📅 Send Every</div>
            <select value={day} onChange={e=>setDay(e.target.value)}
              style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none"}}>
              <option value="monday">Monday</option>
              <option value="tuesday">Tuesday</option>
              <option value="wednesday">Wednesday</option>
              <option value="thursday">Thursday</option>
              <option value="friday">Friday</option>
              <option value="saturday">Saturday</option>
              <option value="sunday">Sunday</option>
            </select>
          </div>

          <div style={{display:"flex",gap:8}}>
            <button onClick={saveSettings}
              style={{flex:1,background:saved?COLORS.success:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:14,fontWeight:600,cursor:"pointer"}}>
              {saved?"Saved!":"Save Settings"}
            </button>
            <button onClick={sendPreview}
              style={{flex:1,background:"#f0f0f0",color:COLORS.text,border:"none",borderRadius:10,padding:"10px",fontSize:14,fontWeight:600,cursor:"pointer"}}>
              {<Mail size={14}/>} Send Preview
            </button>
          </div>
        </div>

        <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>{<BarChart2 size={14}/>} REPORT PREVIEW</div>
          <div style={{background:"linear-gradient(135deg,#007AFF,#5856D6)",borderRadius:12,padding:16,color:"#fff",marginBottom:12}}>
            <div style={{fontSize:11,opacity:0.8,marginBottom:4}}>JEDICHATAI WEEKLY REPORT</div>
            <div style={{fontSize:18,fontWeight:800}}>Your Week in Review</div>
            <div style={{fontSize:12,opacity:0.8,marginTop:2}}>{new Date().toLocaleDateString("en",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</div>
          </div>
          {[
            {icon:"", label:"Total Replies Sent", value:history.length},
            {icon:"", label:"High Risk Conversations", value:highRisk},
            {icon:"", label:"Top Emotion", value:topEmotion?topEmotion[0]:"None"},
            {icon:"", label:"Messages Analyzed", value:sentiment.length},
          ].map(item=>(
            <div key={item.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid "+COLORS.border}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:18}}>{item.icon}</span>
                <span style={{fontSize:13}}>{item.label}</span>
              </div>
              <span style={{fontSize:14,fontWeight:700,color:COLORS.primary,textTransform:"capitalize"}}>{item.value}</span>
            </div>
          ))}
          <div style={{marginTop:12,fontSize:12,color:COLORS.subtext,textAlign:"center"}}>
            This is how your weekly email report will look
          </div>
        </div>

        <div style={{background:"#FFF3CD",borderRadius:16,padding:16,border:"1px solid #FFE082"}}>
          <div style={{fontSize:14,fontWeight:700,color:"#856404",marginBottom:4}}>⚡ Coming Soon</div>
          <div style={{fontSize:13,color:"#856404"}}>Actual email delivery requires the Pro or Business plan. Upgrade to start receiving weekly reports automatically every {day}!</div>
          <button onClick={()=>window.open("https://jedichatai.com/#pricing")}
            style={{marginTop:10,width:"100%",background:"#F59E0B",color:"#fff",border:"none",borderRadius:8,padding:"10px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
            Upgrade to Pro: $9.99/mo
          </button>
        </div>
      </div>
    </div>
  );
}

function APIPage() {
  const [apiKeys, setApiKeys] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("jedichatai_apikeys")||"[]"); } catch(e) { return []; }
  });
  const [showCreate, setShowCreate] = React.useState(false);
  const [newKeyName, setNewKeyName] = React.useState("");
  const [newKeyPerms, setNewKeyPerms] = React.useState("read");
  const [copiedKey, setCopiedKey] = React.useState(null);
  const [showKey, setShowKey] = React.useState(null);

  const saveKeys = (updated) => { setApiKeys(updated); localStorage.setItem("jedichatai_apikeys", JSON.stringify(updated)); };

  const generateKey = () => {
    if (!newKeyName) { alert("Please enter a key name!"); return; }
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const key = "jck_" + Array.from({length:48}, ()=>chars[Math.floor(Math.random()*chars.length)]).join("");
    saveKeys([...apiKeys, {id:Date.now(), name:newKeyName, key, permissions:newKeyPerms, created:new Date().toLocaleDateString(), lastUsed:"Never", status:"active"}]);
    setNewKeyName(""); setShowCreate(false);
  };

  const deleteKey = (id) => { if(window.confirm("Delete this API key? This cannot be undone!")) saveKeys(apiKeys.filter(k=>k.id!==id)); };

  const copyKey = (key, id) => { navigator.clipboard.writeText(key); setCopiedKey(id); setTimeout(()=>setCopiedKey(null), 2000); };

  const permColors = { read:"#34C759", write:"#007AFF", full:"#FF9500" };

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:"#fff",padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>API Access</div>
          <div style={{fontSize:13,color:COLORS.subtext}}>Manage your API keys</div>
        </div>
        <button onClick={()=>setShowCreate(!showCreate)}
          style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"8px 16px",fontSize:14,fontWeight:600,cursor:"pointer"}}>
          + New Key
        </button>
      </div>

      {showCreate && (
        <div style={{background:"#fff",margin:16,borderRadius:16,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:14,fontWeight:700,marginBottom:12,color:COLORS.primary}}>🔑 Create API Key</div>
          <input placeholder="Key name (e.g. My App, Production)" value={newKeyName} onChange={e=>setNewKeyName(e.target.value)}
            style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box",marginBottom:8}}/>
          <select value={newKeyPerms} onChange={e=>setNewKeyPerms(e.target.value)}
            style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",marginBottom:8}}>
            <option value="read">👁️ Read: View conversations only</option>
            <option value="write">✏️ Write: Send replies</option>
            <option value="full">✏️ Full: Complete access</option>
          </select>
          <div style={{display:"flex",gap:8}}>
            <button onClick={generateKey} style={{flex:1,background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:14,fontWeight:600,cursor:"pointer"}}>Generate Key</button>
            <button onClick={()=>setShowCreate(false)} style={{flex:1,background:"#f0f0f0",color:COLORS.text,border:"none",borderRadius:10,padding:"10px",fontSize:14,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{padding:"8px 16px 4px",display:"flex",justifyContent:"flex-end"}}>
        <button onClick={() => {
          const rows = [["ID","Contact","Message","Scheduled At","Status","Sent At"],...messages.map(m=>[m.id,m.contact_name||m.contact_id,'"'+m.message_text.replace(/"/g,'""')+'"',m.scheduled_at,m.status,m.sent_at||""])];
          const csv = rows.map(r=>r.join(",")).join("\n");
          const a = document.createElement("a"); a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
          a.download = "scheduled_messages.csv"; a.click();
        }} style={{background:"none",border:"1px solid "+COLORS.border,borderRadius:8,padding:"6px 14px",fontSize:12,color:COLORS.subtext,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
          ⬇ Export CSV
        </button>
      </div>
      <div style={{padding:"0 16px 8px"}}>
        <div style={{background:"#EFF6FF",borderRadius:12,padding:12,marginBottom:12,border:"1px solid #BFDBFE"}}>
          <div style={{fontSize:13,fontWeight:600,color:COLORS.primary,marginBottom:4}}>📚 Base URL</div>
          <div style={{fontFamily:"monospace",fontSize:12,color:"#1e40af",background:"#fff",padding:"6px 10px",borderRadius:8}}>https://app.jedichatai.com/api/v1</div>
        </div>

        {apiKeys.length === 0 ? (
          <div style={{background:"#fff",borderRadius:16,padding:32,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:48,marginBottom:12}}>{<Key size={18}/>}</div>
            <div style={{fontSize:16,fontWeight:600}}>No API keys yet</div>
            <div style={{fontSize:13,color:COLORS.subtext,marginTop:8}}>Create an API key to integrate JediChatAI with your apps.</div>
          </div>
        ) : (
          apiKeys.map(k => (
            <div key={k.id} style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <div style={{fontSize:15,fontWeight:700}}>🔑 {k.name}</div>
                  <div style={{fontSize:11,color:COLORS.subtext,marginTop:2}}>Created {k.created} · Last used: {k.lastUsed}</div>
                </div>
                <div style={{display:"flex",gap:6,alignItems:"center"}}>
                  <span style={{fontSize:10,padding:"2px 8px",borderRadius:8,background:permColors[k.permissions]+"20",color:permColors[k.permissions],fontWeight:700,textTransform:"uppercase"}}>{k.permissions}</span>
                  <button onClick={()=>deleteKey(k.id)} style={{background:"none",border:"none",color:COLORS.danger,fontSize:16,cursor:"pointer"}}>🗑️</button>
                </div>
              </div>
              <div style={{background:"#F5F5F5",borderRadius:8,padding:"8px 12px",fontFamily:"monospace",fontSize:12,marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span>{showKey===k.id ? k.key : k.key.substring(0,12)+"••••••••••••••••••••••••••••••••••••"}</span>
                <button onClick={()=>setShowKey(showKey===k.id?null:k.id)} style={{background:"none",border:"none",color:COLORS.primary,fontSize:12,cursor:"pointer",marginLeft:8}}>{showKey===k.id?"Hide":"Show"}</button>
              </div>
              <button onClick={()=>copyKey(k.key, k.id)}
                style={{width:"100%",background:copiedKey===k.id?COLORS.success:COLORS.primary,color:"#fff",border:"none",borderRadius:8,padding:"8px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                {copiedKey===k.id?"Copied!":"Copy Key"}
              </button>
            </div>
          ))
        )}

        <div style={{background:"#fff",borderRadius:16,padding:16,marginTop:4,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>📖 QUICK START</div>
          {[
            {method:"GET", endpoint:"/conversations", desc:"Get all conversations"},
            {method:"POST", endpoint:"/keyboard/suggest", desc:"Generate AI replies"},
            {method:"POST", endpoint:"/conversations/approve", desc:"Approve a reply"},
            {method:"GET", endpoint:"/settings/auto-respond", desc:"Get auto-respond status"},
          ].map(e=>(
            <div key={e.endpoint} style={{padding:"8px 0",borderBottom:"1px solid "+COLORS.border}}>
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:2}}>
                <span style={{fontSize:10,padding:"2px 6px",borderRadius:4,background:e.method==="GET"?COLORS.success+"20":COLORS.primary+"20",color:e.method==="GET"?COLORS.success:COLORS.primary,fontWeight:700}}>{e.method}</span>
                <span style={{fontFamily:"monospace",fontSize:12,color:COLORS.text}}>{e.endpoint}</span>
              </div>
              <div style={{fontSize:12,color:COLORS.subtext}}>{e.desc}</div>
            </div>
          ))}
          <button onClick={()=>window.open("https://jedichatai.com/api-docs")}
            style={{width:"100%",background:"#f0f0f0",color:COLORS.text,border:"none",borderRadius:8,padding:"10px",fontSize:13,fontWeight:600,cursor:"pointer",marginTop:10}}>
            📚 Full API Documentation
          </button>
        </div>
      </div>
    </div>
  );
}

function AnalyticsPage() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const API = "https://app.jedichatai.com";
  const token = localStorage.getItem("jedichatai_token");

  React.useEffect(() => {
    loadAnalytics();
    const interval = setInterval(loadAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadAnalytics = async () => {
    try {
      const res = await fetch(API + "/api/v1/messages/analytics", {
        headers: { Authorization: "Bearer " + token }
      });
      const d = await res.json();
      setData(d);
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  const StatCard = ({icon, label, value, color, sub}) => (
    <div style={{background:COLORS.card,borderRadius:16,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
        <div style={{width:40,height:40,borderRadius:12,background:color+"20",display:"flex",alignItems:"center",justifyContent:"center",color:color}}>{icon}</div>
        <div>
          <div style={{fontSize:24,fontWeight:800,color:COLORS.text}}>{value}</div>
          <div style={{fontSize:12,color:COLORS.subtext}}>{label}</div>
        </div>
      </div>
      {sub && <div style={{fontSize:11,color:COLORS.subtext}}>{sub}</div>}
    </div>
  );

  if (loading) return (
    <div style={{display:"flex",justifyContent:"center",alignItems:"center",height:"60vh",flexDirection:"column",gap:16}}>
      <BarChart2 size={40} color={COLORS.primary}/>
      <div style={{color:COLORS.subtext}}>Loading analytics...</div>
    </div>
  );

  return (
    <div style={{paddingBottom:80,background:COLORS.bg,minHeight:"100vh"}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:24,fontWeight:800,color:COLORS.primary}}>Analytics</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Real-time performance overview</div>
      </div>
      <div style={{padding:16}}>

        <div style={{background:COLORS.card,borderRadius:14,padding:14,marginBottom:12,display:"flex",alignItems:"center",gap:12,borderLeft:"4px solid "+COLORS.primary}}>
          <div style={{fontSize:28}}>{<Calendar size={18}/>}</div>
          <div style={{flex:1}}>
            <div style={{fontSize:13,color:COLORS.subtext,marginBottom:2}}>Today</div>
            <div style={{display:"flex",gap:20}}>
              <div><span style={{fontSize:20,fontWeight:800,color:COLORS.primary}}>{data?.today_messages||0}</span><span style={{fontSize:11,color:COLORS.subtext,marginLeft:4}}>messages</span></div>
              <div><span style={{fontSize:20,fontWeight:800,color:COLORS.success}}>{data?.today_replies||0}</span><span style={{fontSize:11,color:COLORS.subtext,marginLeft:4}}>replies</span></div>
              <div><span style={{fontSize:20,fontWeight:800,color:COLORS.danger}}>{data?.today_risk||0}</span><span style={{fontSize:11,color:COLORS.subtext,marginLeft:4}}>risk alerts</span></div>
            </div>
          </div>
          <div style={{width:8,height:8,borderRadius:4,background:COLORS.success,animation:"pulse 2s infinite"}} title="Live"/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
          <StatCard icon={<MessageCircle size={20}/>} label="Total Messages" value={data?.total_messages||0} color={COLORS.primary}/>
          <StatCard icon={<CheckCircle size={20}/>} label="Replies Sent" value={data?.sent_replies||0} color={COLORS.success}/>
          <StatCard icon={<Zap size={20}/>} label="Auto Sent" value={data?.auto_sent||0} color="#FF9500"/>
          <StatCard icon={<Shield size={20}/>} label="High Risk Blocked" value={data?.high_risk||0} color={COLORS.danger}/>
        </div>

        {data?.top_emotions && data.top_emotions.length > 0 && (
          <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>TOP EMOTIONS DETECTED</div>
            {data.top_emotions.map((e, i) => (
              <div key={i} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:13,fontWeight:600,textTransform:"capitalize"}}>{e.emotion||"Unknown"}</span>
                  <span style={{fontSize:12,color:COLORS.subtext}}>{e.count} times</span>
                </div>
                <div style={{background:COLORS.border,borderRadius:10,height:6,overflow:"hidden"}}>
                  <div style={{background:COLORS.primary,height:"100%",borderRadius:10,width:Math.min(100,(e.count/data.top_emotions[0].count)*100)+"%",transition:"width 0.5s"}}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {data?.top_contacts && data.top_contacts.length > 0 && (
          <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>TOP CONTACTS</div>
            {data.top_contacts.map((c, i) => (
              <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+COLORS.border}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:36,height:36,borderRadius:"50%",background:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontWeight:700,fontSize:14}}>
                    {(c.contact_name||"?").charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{fontSize:13,fontWeight:600}}>{c.contact_name||c.contact_id||"Unknown"}</div>
                    <div style={{fontSize:11,color:COLORS.subtext}}>{c.count} messages</div>
                  </div>
                </div>
                <BarChart2 size={16} color={COLORS.subtext}/>
              </div>
            ))}
          </div>
        )}

        {(!data || (data.total_messages === 0)) && (
          <div style={{background:COLORS.card,borderRadius:16,padding:40,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <BarChart2 size={48} color={COLORS.border} style={{margin:"0 auto 16px"}}/>
            <div style={{fontSize:16,fontWeight:700,marginBottom:8}}>No data yet</div>
            <div style={{fontSize:13,color:COLORS.subtext}}>Connect WhatsApp and start receiving messages to see analytics here.</div>
          </div>
        )}

      </div>
    </div>
  );
}


function TeamPage() {
  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Team Mode</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Collaborative AI replies for your team</div>
      </div>
      <div style={{padding:24,maxWidth:520,margin:"0 auto"}}>
        <div style={{background:COLORS.card,borderRadius:20,padding:32,textAlign:"center",boxShadow:"0 4px 20px rgba(0,0,0,0.08)"}}>
          <div style={{width:80,height:80,borderRadius:40,background:COLORS.primary+"20",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 20px",fontSize:40}}>{<Users size={18}/>}</div>
          <div style={{fontSize:22,fontWeight:800,marginBottom:8,color:COLORS.text}}>Team Mode</div>
          <div style={{background:"#FF950020",borderRadius:10,padding:"8px 16px",display:"inline-block",marginBottom:16}}>
            <span style={{background:"#E8F5E9",color:"#34C759",fontWeight:700,fontSize:12,padding:"2px 8px",borderRadius:10}}>Available</span>
          </div>
          <div style={{fontSize:14,color:COLORS.subtext,lineHeight:1.6,marginBottom:24}}>Invite team members, assign conversations, collaborate on AI replies across your organization, all in one place.</div>
          {[
            "Invite unlimited team members",
            "Assign contacts to team members",
            "Shared templates library",
            "Team performance analytics",
            "Role-based permissions (Owner / Admin / Member)",
            "Real-time collaboration",
          ].map((f,i) => (
            <div key={i} style={{textAlign:"left",padding:"6px 0",fontSize:13,color:COLORS.text,borderBottom:"1px solid "+COLORS.border}}>{f}</div>
          ))}
          <div style={{marginTop:20,background:COLORS.primary,color:"#fff",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,textAlign:"center",cursor:"default",opacity:0.7}}>
            Business Plan Required
          </div>
        </div>
      </div>
    </div>
  );
}

function SchedulePage() {
  const [messages, setMessages] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("pending");
  const [form, setForm] = useState({contact_id:"", contact_name:"", message_text:"", scheduled_at:""});

  const load = async () => {
    try {
      const res = await api.get("/api/v1/schedule");
      setMessages(res.data.messages || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    api.get("/api/v1/contacts").then(r => setContacts(r.data.contacts || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.contact_id.trim() || !form.message_text.trim() || !form.scheduled_at.trim()) {
      window.alert("Please fill in all fields"); return;
    }
    setSaving(true);
    try {
      const cid = form.contact_id.includes("@c.us") ? form.contact_id : form.contact_id + "@c.us";
      await api.post("/api/v1/schedule", {...form, contact_id: cid, scheduled_at: new Date(form.scheduled_at).toISOString().slice(0,19)});
      setShowForm(false);
      setForm({contact_id:"", contact_name:"", message_text:"", scheduled_at:""});
      await load();
    } catch(e: any) { window.alert(e?.response?.data?.detail || "Could not schedule"); }
    setSaving(false);
  };

  const cancel = async (id: number) => {
    if (!window.confirm("Cancel this scheduled message?")) return;
    try { await api.delete("/api/v1/schedule/" + id); await load(); }
    catch(e) { window.alert("Could not cancel"); }
  };

  const pending = messages.filter(m => m.status === "pending");
  const sent = messages.filter(m => m.status === "sent");
  const other = messages.filter(m => m.status === "failed" || m.status === "cancelled");
  const displayed = tab === "pending" ? pending : tab === "sent" ? sent : other;

  const statusColor = (s: string) => s === "sent" ? COLORS.success : s === "failed" ? COLORS.danger : s === "cancelled" ? COLORS.subtext : COLORS.warning;
  const minDT = new Date(Date.now() + 60000).toISOString().slice(0,16);

  if (loading) return <div style={{padding:40,textAlign:"center",color:COLORS.subtext}}>Loading...</div>;

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Scheduled</div>
          <div style={{fontSize:13,color:COLORS.subtext}}>{pending.length} pending - {sent.length} sent</div>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"10px 18px",fontSize:14,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
          <Plus size={16}/> Schedule
        </button>
      </div>

      <div style={{display:"flex",borderBottom:"1px solid "+COLORS.border,background:COLORS.card}}>
        {[{k:"pending",l:"Pending " + pending.length},{k:"sent",l:"Sent " + sent.length},{k:"other",l:"Other " + other.length}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)}
            style={{flex:1,padding:"12px",border:"none",background:"none",borderBottom:tab===t.k?"2px solid "+COLORS.primary:"2px solid transparent",color:tab===t.k?COLORS.primary:COLORS.subtext,fontSize:13,fontWeight:600,cursor:"pointer"}}>
            {t.l}
          </button>
        ))}
      </div>

      {showForm && (
        <div style={{background:COLORS.card,margin:16,borderRadius:16,padding:20,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:15,fontWeight:700,color:COLORS.text,marginBottom:16}}>New Scheduled Message</div>
          <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:4}}>Recipient</div>
          <select value={form.contact_id} onChange={e => {
            const sel = contacts.find(c => c.contact_id === e.target.value);
            setForm(f => ({...f, contact_id: e.target.value, contact_name: sel?.contact_name || sel?.contact_id || e.target.value}));
          }} style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,marginBottom:8,background:COLORS.card,color:COLORS.text,boxSizing:"border-box"}}>
            <option value="">-- Pick from contacts --</option>
            {contacts.map(c => <option key={c.contact_id} value={c.contact_id}>{c.contact_name && c.contact_name !== c.contact_id ? c.contact_name + " · " + c.contact_id.replace("@c.us","") : c.contact_id.replace("@c.us","")} ({c.message_count} msgs)</option>)}
          </select>
          <div style={{fontSize:11,color:COLORS.subtext,marginBottom:4}}>Or type phone number directly:</div>
          <input value={form.contact_id} onChange={e => setForm(f => ({...f, contact_id: e.target.value, contact_name: e.target.value}))}
            placeholder="+1234567890" style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,marginBottom:12,background:COLORS.card,color:COLORS.text,boxSizing:"border-box"}}/>
          <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:4}}>Message</div>
          <textarea value={form.message_text} onChange={e => setForm(f => ({...f, message_text: e.target.value}))}
            placeholder="Type your message..." rows={4}
            style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,marginBottom:12,background:COLORS.card,color:COLORS.text,boxSizing:"border-box",fontFamily:"inherit",resize:"vertical"}}/>
          <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:4}}>Send Date & Time</div>
          <input type="datetime-local" value={form.scheduled_at} min={minDT}
            onChange={e => setForm(f => ({...f, scheduled_at: e.target.value}))}
            style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,marginBottom:12,background:COLORS.card,color:COLORS.text,boxSizing:"border-box"}}/>
          <div style={{display:"flex",gap:8}}>
            <button onClick={submit} disabled={saving}
              style={{flex:1,background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer",opacity:saving?0.6:1}}>
              {saving ? "Scheduling..." : "Save Schedule"}
            </button>
            <button onClick={() => setShowForm(false)}
              style={{flex:1,background:COLORS.card,color:COLORS.text,border:"1px solid "+COLORS.border,borderRadius:10,padding:"12px",fontSize:14,fontWeight:600,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={{padding:"8px 16px"}}>
        {displayed.length === 0 ? (
          <div style={{textAlign:"center",padding:48,color:COLORS.subtext}}>
            <Calendar size={48} style={{opacity:0.4,marginBottom:12}}/>
            <div style={{fontSize:16,fontWeight:700,marginBottom:6,color:COLORS.text}}>
              {tab === "pending" ? "No scheduled messages" : tab === "sent" ? "No sent messages" : "No cancelled messages"}
            </div>
            {tab === "pending" && <div style={{fontSize:13}}>Click Schedule to send a message at a future time</div>}
          </div>
        ) : displayed.map((msg: any) => (
          <div key={msg.id} style={{background:COLORS.card,borderRadius:14,padding:14,marginBottom:10,borderLeft:"3px solid "+statusColor(msg.status)}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <div style={{fontSize:15,fontWeight:700,color:COLORS.text}}>{msg.contact_name || msg.contact_id}</div>
              <span style={{fontSize:10,fontWeight:700,color:statusColor(msg.status),textTransform:"uppercase",background:statusColor(msg.status)+"15",padding:"2px 8px",borderRadius:6}}>{msg.status}</span>
            </div>
            <div style={{fontSize:13,color:COLORS.subtext,marginBottom:8,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{msg.message_text}</div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:11,color:COLORS.subtext}}>
                {msg.status === "sent" ? "Sent " + new Date(msg.sent_at).toLocaleString() : "Scheduled " + new Date(msg.scheduled_at).toLocaleString()}
              </div>
              {msg.status === "pending" && (
                <button onClick={() => cancel(msg.id)}
                  style={{background:"none",border:"none",color:COLORS.danger,fontWeight:700,fontSize:12,cursor:"pointer"}}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupsPage() {
  const [groups, setGroups] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [editor, setEditor] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const PALETTE = ["#FF3B30","#FF9500","#FFCC00","#34C759","#007AFF","#5856D6","#AF52DE","#FF2D55"];
  const ICONS = ["Users","Heart","Briefcase","Star","Home","Coffee","Phone","Clock"];
  const TONES = [
    {value:"", label:"Default"},
    {value:"professional", label:"Professional"},
    {value:"friendly", label:"Friendly"},
    {value:"casual", label:"Casual"},
    {value:"formal", label:"Formal"},
    {value:"empathetic", label:"Empathetic"},
  ];
  const AUTO_REPLIES = [
    {value:"", label:"Default"},
    {value:"on", label:"Always"},
    {value:"smart", label:"Smart (AI)"},
    {value:"off", label:"Never"},
  ];

  const load = async () => {
    try {
      const data = await getGroups();
      setGroups(data.groups || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startCreate = (type: string) => {
    setShowTypePicker(false);
    setEditor({ name:"", color:"#007AFF", icon:"Users", group_type:type, tone:"", auto_reply:"", custom_instructions:"" });
  };
  const openEdit = (g: any) => setEditor({...g});

  const saveEditor = async () => {
    if (!editor.name?.trim()) { window.alert("Group name required"); return; }
    setSaving(true);
    try {
      const payload: any = {
        name: editor.name,
        color: editor.color,
        icon: editor.icon,
        tone: editor.tone || null,
        auto_reply: editor.auto_reply || null,
        custom_instructions: editor.custom_instructions || null,
      };
      if (editor.id) {
        await updateGroup(editor.id, payload);
      } else {
        payload.group_type = editor.group_type;
        if (editor.group_type === "smart") payload.rule_json = editor.rule_json || {op:"AND", rules:[]};
        await createGroup(payload);
      }
      setEditor(null);
      await load();
    } catch(e: any) { window.alert(e?.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  const doDelete = async (g: any) => {
    if (!window.confirm('Delete "' + g.name + '" and all its members? This cannot be undone.')) return;
    try { await deleteGroup(g.id); setEditor(null); setDetail(null); await load(); }
    catch(e) { window.alert("Could not delete"); }
  };

  if (loading) return <div style={{padding:40, textAlign:"center", color:COLORS.subtext}}>Loading groups...</div>;

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Groups</div>
          <div style={{fontSize:13,color:COLORS.subtext}}>{groups.length} {groups.length===1?"group":"groups"} - manual + smart</div>
        </div>
        <button onClick={() => setShowTypePicker(true)}
          style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"10px 18px",fontSize:14,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
          <Plus size={16}/> New Group
        </button>
      </div>

      <div style={{padding:"16px"}}>
        {groups.length === 0 ? (
          <div style={{textAlign:"center",padding:48,color:COLORS.subtext}}>
            <Users size={48} style={{opacity:0.4,marginBottom:12}}/>
            <div style={{fontSize:16,fontWeight:700,marginBottom:6,color:COLORS.text}}>No groups yet</div>
            <div style={{fontSize:13}}>Click New Group to create your first one</div>
          </div>
        ) : groups.map((g:any) => (
          <div key={g.id} onClick={() => setDetail(g)}
            style={{background:COLORS.card,borderRadius:14,padding:14,marginBottom:10,cursor:"pointer",border:"1px solid "+COLORS.border,display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:44,height:44,borderRadius:22,background:(g.color||"#007AFF")+"25",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <Users size={20} color={g.color || COLORS.primary}/>
            </div>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                <div style={{fontSize:15,fontWeight:700,color:COLORS.text}}>{g.name}</div>
                <div style={{background: g.group_type==="smart"?"#FF950025":COLORS.primary+"15", padding:"2px 6px", borderRadius:6}}>
                  <span style={{fontSize:9,fontWeight:700,color:g.group_type==="smart"?"#FF9500":COLORS.primary,textTransform:"uppercase"}}>{g.group_type}</span>
                </div>
              </div>
              <div style={{fontSize:12,color:COLORS.subtext}}>
                {g.member_count||0} {(g.member_count||0)===1?"contact":"contacts"}
                {g.tone ? " - " + g.tone : ""}
              </div>
            </div>
            <ChevronRight size={18} color={COLORS.subtext}/>
          </div>
        ))}
      </div>

      {showTypePicker && (
        <div onClick={() => setShowTypePicker(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
          <div onClick={(e) => e.stopPropagation()} style={{background:COLORS.bg,borderRadius:16,maxWidth:480,width:"100%",padding:20}}>
            <div style={{fontSize:18,fontWeight:700,color:COLORS.text,marginBottom:12}}>New Group</div>
            <div style={{fontSize:13,color:COLORS.subtext,marginBottom:20}}>Pick a type. You can\'t change it later.</div>
            <div onClick={() => startCreate("manual")} style={{background:COLORS.card,borderRadius:12,padding:16,marginBottom:10,cursor:"pointer",border:"1px solid "+COLORS.border}}>
              <div style={{display:"flex",alignItems:"center",marginBottom:6}}>
                <Users size={20} color={COLORS.primary} style={{marginRight:10}}/>
                <span style={{fontWeight:700,color:COLORS.text,fontSize:15}}>Manual Group</span>
              </div>
              <div style={{fontSize:12,color:COLORS.subtext,marginLeft:30}}>You pick which contacts belong</div>
            </div>
            <div onClick={() => startCreate("smart")} style={{background:COLORS.card,borderRadius:12,padding:16,cursor:"pointer",border:"1px solid "+COLORS.border}}>
              <div style={{display:"flex",alignItems:"center",marginBottom:6}}>
                <Zap size={20} color="#FF9500" style={{marginRight:10}}/>
                <span style={{fontWeight:700,color:COLORS.text,fontSize:15}}>Smart Group</span>
              </div>
              <div style={{fontSize:12,color:COLORS.subtext,marginLeft:30}}>Auto-populated by rules</div>
            </div>
          </div>
        </div>
      )}

      {editor && (
        <div onClick={() => setEditor(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
          <div onClick={(e) => e.stopPropagation()} style={{background:COLORS.bg,borderRadius:16,maxWidth:560,width:"100%",maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:18,fontWeight:700,color:COLORS.text}}>{editor.id?"Edit Group":"New " + (editor.group_type==="smart"?"Smart":"Manual") + " Group"}</div>
              <button onClick={() => setEditor(null)} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.text}}><X size={22}/></button>
            </div>
            <div style={{padding:20,overflowY:"auto",flex:1}}>
              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Name</div>
              <input value={editor.name} onChange={e => setEditor({...editor, name:e.target.value})} placeholder="e.g. Family, VIP Clients" maxLength={100}
                style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,marginBottom:16,background:COLORS.card,color:COLORS.text,boxSizing:"border-box"}}/>
              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Color</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:16}}>
                {PALETTE.map(col => (
                  <button key={col} onClick={() => setEditor({...editor, color:col})}
                    style={{width:40,height:40,borderRadius:20,background:col,border:editor.color===col?"3px solid #fff":"none",boxShadow:editor.color===col?"0 0 0 2px "+col:"none",cursor:"pointer"}}/>
                ))}
              </div>
              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Reply Tone</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
                {TONES.map(t => {
                  const sel = (editor.tone||"") === t.value;
                  return (
                    <button key={t.value||"none"} onClick={() => setEditor({...editor, tone:t.value})}
                      style={{padding:"6px 12px",borderRadius:14,border:"1px solid "+(sel?COLORS.primary:COLORS.border),background:sel?COLORS.primary:COLORS.card,color:sel?"#fff":COLORS.text,fontSize:13,cursor:"pointer"}}>{t.label}</button>
                  );
                })}
              </div>
              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Auto-Reply</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
                {AUTO_REPLIES.map(a => {
                  const sel = (editor.auto_reply||"") === a.value;
                  return (
                    <button key={a.value||"none"} onClick={() => setEditor({...editor, auto_reply:a.value})}
                      style={{padding:"6px 12px",borderRadius:14,border:"1px solid "+(sel?COLORS.primary:COLORS.border),background:sel?COLORS.primary:COLORS.card,color:sel?"#fff":COLORS.text,fontSize:13,cursor:"pointer"}}>{a.label}</button>
                  );
                })}
              </div>
              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Custom Instructions (optional)</div>
              <textarea value={editor.custom_instructions||""} onChange={e => setEditor({...editor, custom_instructions:e.target.value})} placeholder="e.g. Keep replies short and warm" rows={3}
                style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,marginBottom:8,background:COLORS.card,color:COLORS.text,boxSizing:"border-box",fontFamily:"inherit",resize:"vertical"}}/>
            </div>
            <div style={{padding:"12px 20px",borderTop:"1px solid "+COLORS.border,display:"flex",justifyContent:"space-between"}}>
              <div>
                {editor.id && <button onClick={() => doDelete(editor)} style={{background:"none",color:COLORS.danger,border:"1px solid "+COLORS.danger,borderRadius:10,padding:"8px 16px",fontSize:14,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}><Trash2 size={14}/> Delete</button>}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={() => setEditor(null)} style={{background:COLORS.card,color:COLORS.text,border:"1px solid "+COLORS.border,borderRadius:10,padding:"8px 18px",fontSize:14,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                <button onClick={saveEditor} disabled={saving} style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"8px 20px",fontSize:14,fontWeight:700,cursor:"pointer",opacity:saving?0.6:1}}>{saving?"Saving...":"Save"}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detail && <GroupDetailModal group={detail} onClose={() => setDetail(null)} onEdit={() => { setEditor(detail); setDetail(null); }} onChange={load}/>}
    </div>
  );
}

// ── Web Rule Builder ──────────────────────────────────────────────────────────
const WEB_RULE_TYPES = [
  {value:"last_message_within_days",label:"Last message within N days",ops:["<=","<",">=",">"],vtype:"number",ph:"7"},
  {value:"message_count",label:"Message count",ops:[">=",">","<=","<","="],vtype:"number",ph:"10"},
  {value:"risk_level",label:"Risk level",ops:["in","not_in"],vtype:"multi",opts:["low","medium","high","critical"]},
  {value:"contact_name_contains",label:"Contact name contains",ops:["contains"],vtype:"text",ph:"search term"},
  {value:"phone_starts_with",label:"Phone starts with",ops:["starts_with"],vtype:"text",ph:"+1"},
];
const WEB_DEFAULT_LEAF = () => ({type:"last_message_within_days",operator:"<=",value:7});
const WEB_DEFAULT_BRANCH = () => ({op:"AND",rules:[WEB_DEFAULT_LEAF()]});

function WebRuleLeaf({node, onChange, onDelete}: any) {
  const spec = WEB_RULE_TYPES.find(r=>r.value===node.type)||WEB_RULE_TYPES[0];
  const upd = (field: string, val: any) => onChange({...node,[field]:val});
  return (
    <div style={{background:COLORS.bg,borderRadius:10,padding:12,marginBottom:8,border:"1px solid "+COLORS.border}}>
      <div style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
        <select value={node.type} onChange={e=>onChange({type:e.target.value,operator:WEB_RULE_TYPES.find(r=>r.value===e.target.value)?.ops[0]||"<=",value:e.target.value==="risk_level"?["high"]:7})}
          style={{flex:1,padding:"6px 10px",borderRadius:8,border:"1px solid "+COLORS.border,fontSize:13,background:COLORS.card,color:COLORS.text}}>
          {WEB_RULE_TYPES.map(r=><option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <button onClick={onDelete} style={{background:"#FF3B3015",border:"none",borderRadius:8,padding:"6px 10px",cursor:"pointer",color:COLORS.danger,fontWeight:700}}>✕</button>
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{display:"flex",gap:4}}>
          {spec.ops.map((op:string)=>(
            <button key={op} onClick={()=>upd("operator",op)}
              style={{padding:"4px 10px",borderRadius:6,border:"1px solid "+(node.operator===op?"#FF9500":COLORS.border),background:node.operator===op?"#FF9500":"transparent",color:node.operator===op?"#fff":COLORS.text,fontSize:12,cursor:"pointer",fontWeight:700}}>
              {op}
            </button>
          ))}
        </div>
        {spec.vtype==="number" && <input type="number" value={node.value||""} onChange={e=>upd("value",parseInt(e.target.value)||0)}
          style={{width:70,padding:"4px 8px",borderRadius:8,border:"1px solid "+COLORS.border,fontSize:13,textAlign:"center",background:COLORS.card,color:COLORS.text}} placeholder={spec.ph}/>}
        {spec.vtype==="text" && <input type="text" value={node.value||""} onChange={e=>upd("value",e.target.value)}
          style={{flex:1,padding:"4px 8px",borderRadius:8,border:"1px solid "+COLORS.border,fontSize:13,background:COLORS.card,color:COLORS.text}} placeholder={spec.ph}/>}
        {spec.vtype==="multi" && (spec.opts||[]).map((opt:string)=>{
          const sel=Array.isArray(node.value)&&node.value.includes(opt);
          return <button key={opt} onClick={()=>{const cur=Array.isArray(node.value)?node.value:[];upd("value",sel?cur.filter((x:string)=>x!==opt):[...cur,opt]);}}
            style={{padding:"4px 10px",borderRadius:6,border:"1px solid "+(sel?COLORS.danger:COLORS.border),background:sel?COLORS.danger:"transparent",color:sel?"#fff":COLORS.text,fontSize:12,cursor:"pointer",fontWeight:600}}>{opt}</button>;
        })}
      </div>
    </div>
  );
}

function WebRuleNode({node, onChange, onDelete, depth=0}: any) {
  const isBranch = node&&"op" in node&&Array.isArray(node.rules);
  if (!isBranch) return <WebRuleLeaf node={node} onChange={onChange} onDelete={onDelete}/>;
  const updateChild = (i: number, newNode: any) => { const r=[...node.rules]; r[i]=newNode; onChange({...node,rules:r}); };
  const deleteChild = (i: number) => { const r=node.rules.filter((_:any,idx:number)=>idx!==i); onChange({...node,rules:r}); };
  return (
    <div style={{borderLeft:"3px solid "+(node.op==="AND"?COLORS.primary:"#FF9500"),paddingLeft:12,marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
        {depth>0&&<button onClick={onDelete} style={{background:"#FF3B3015",border:"none",borderRadius:6,padding:"4px 8px",cursor:"pointer",color:COLORS.danger,fontSize:12}}>✕</button>}
        <button onClick={()=>onChange({...node,op:"AND"})}
          style={{padding:"4px 12px",borderRadius:6,border:"1px solid "+(node.op==="AND"?COLORS.primary:COLORS.border),background:node.op==="AND"?COLORS.primary:"transparent",color:node.op==="AND"?"#fff":COLORS.text,fontSize:12,cursor:"pointer",fontWeight:700}}>AND</button>
        <button onClick={()=>onChange({...node,op:"OR"})}
          style={{padding:"4px 12px",borderRadius:6,border:"1px solid "+(node.op==="OR"?"#FF9500":COLORS.border),background:node.op==="OR"?"#FF9500":"transparent",color:node.op==="OR"?"#fff":COLORS.text,fontSize:12,cursor:"pointer",fontWeight:700}}>OR</button>
        <span style={{fontSize:11,color:COLORS.subtext}}>{node.op==="AND"?"all must match":"any can match"}</span>
      </div>
      {node.rules.map((child:any,i:number)=>(
        <WebRuleNode key={i} node={child} onChange={(n:any)=>updateChild(i,n)} onDelete={()=>deleteChild(i)} depth={depth+1}/>
      ))}
      <div style={{display:"flex",gap:8,marginTop:4}}>
        <button onClick={()=>onChange({...node,rules:[...node.rules,WEB_DEFAULT_LEAF()]})}
          style={{flex:1,padding:"6px",borderRadius:8,border:"1px solid "+COLORS.primary,background:"transparent",color:COLORS.primary,fontSize:12,cursor:"pointer",fontWeight:600}}>+ Rule</button>
        {depth<2&&<button onClick={()=>onChange({...node,rules:[...node.rules,WEB_DEFAULT_BRANCH()]})}
          style={{flex:1,padding:"6px",borderRadius:8,border:"1px solid #FF9500",background:"transparent",color:"#FF9500",fontSize:12,cursor:"pointer",fontWeight:600}}>+ Group</button>}
      </div>
    </div>
  );
}

function WebRuleBuilder({group, onChange}: any) {
  const [ruleTree, setRuleTree] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (group.rule_json) {
      try {
        const parsed = typeof group.rule_json==="string"?JSON.parse(group.rule_json):group.rule_json;
        setRuleTree(Array.isArray(parsed)?{op:"AND",rules:parsed}:parsed);
      } catch(e) { setRuleTree(WEB_DEFAULT_BRANCH()); }
    } else { setRuleTree(WEB_DEFAULT_BRANCH()); }
  }, [group.id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/api/v1/groups/"+group.id, {rule_json: ruleTree});
      setDirty(false);
      onChange();
      window.alert("Rules saved. Use Re-evaluate on the Members tab to apply.");
    } catch(e) { window.alert("Could not save rules"); }
    setSaving(false);
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700,color:COLORS.text}}>Smart Rules</div>
        {dirty && <button onClick={save} disabled={saving}
          style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:8,padding:"8px 16px",fontSize:13,fontWeight:700,cursor:"pointer",opacity:saving?0.6:1}}>
          {saving?"Saving...":"Save Rules"}
        </button>}
      </div>
      <div style={{fontSize:12,color:COLORS.subtext,marginBottom:12,lineHeight:1.5}}>
        Build rules to auto-populate this group. AND = all must match. OR = any can match.
      </div>
      {ruleTree && <WebRuleNode node={ruleTree} onChange={(n:any)=>{setRuleTree(n);setDirty(true);}} onDelete={()=>{setRuleTree(WEB_DEFAULT_BRANCH());setDirty(true);}}/>}
    </div>
  );
}

function GroupDetailModal({ group, onClose, onEdit, onChange }: any) {
  const [tab, setTab] = useState("members");
  const [members, setMembers] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);

  const loadMembers = async () => {
    try { const d = await listGroupMembers(group.id); setMembers(d.members || []); } catch(e) {}
    setLoading(false);
  };
  useEffect(() => { loadMembers(); if (group.group_type === "manual") listContacts().then(d => setContacts(d.contacts || [])).catch(() => {}); }, []);

  const doAdd = async (c: any) => {
    try { await addGroupMember(group.id, {contact_id: c.contact_id, contact_name: c.contact_name}); setShowAdd(false); await loadMembers(); onChange(); }
    catch(e) { window.alert("Could not add"); }
  };
  const doRemove = async (m: any) => {
    if (!window.confirm("Remove " + (m.contact_name || m.contact_id) + "?")) return;
    try { await removeGroupMember(group.id, m.contact_id); await loadMembers(); onChange(); }
    catch(e) { window.alert("Could not remove"); }
  };
  const doEvaluate = async () => {
    setEvaluating(true);
    try { const r = await evaluateGroup(group.id); window.alert(r.matched + " contacts match. Added " + r.added + ", removed " + r.removed + "."); await loadMembers(); onChange(); }
    catch(e) { window.alert("Could not evaluate"); }
    setEvaluating(false);
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
      <div onClick={(e) => e.stopPropagation()} style={{background:COLORS.bg,borderRadius:16,maxWidth:600,width:"100%",maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>
        <div style={{padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",alignItems:"center",gap:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:18,fontWeight:700,color:COLORS.text}}>{group.name}</div>
            <div style={{fontSize:11,color:COLORS.subtext,marginTop:2}}>{group.group_type === "smart" ? "Smart group" : "Manual group"}</div>
          </div>
          <button onClick={onEdit} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.primary,padding:6}}><Edit size={20}/></button>
          <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.text}}><X size={22}/></button>
        </div>
        <div style={{display:"flex",borderBottom:"1px solid "+COLORS.border}}>
          {(group.group_type === "smart" ? ["members","settings","rules"] : ["members","settings"]).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{flex:1,padding:"12px",border:"none",background:"none",borderBottom:tab===t?"2px solid "+COLORS.primary:"2px solid transparent",color:tab===t?COLORS.primary:COLORS.subtext,fontSize:13,fontWeight:600,cursor:"pointer",textTransform:"capitalize"}}>{t}</button>
          ))}
        </div>
        <div style={{padding:16,overflowY:"auto",flex:1}}>
          {tab === "members" && (
            <>
              {group.group_type === "smart" && (
                <button onClick={doEvaluate} disabled={evaluating} style={{background:"#FF950025",color:"#FF9500",border:"none",borderRadius:10,padding:"10px",width:"100%",marginBottom:12,fontWeight:700,cursor:"pointer",opacity:evaluating?0.5:1,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  <RefreshCw size={16}/> {evaluating ? "Evaluating..." : "Re-evaluate now"}
                </button>
              )}
              {loading ? <div style={{textAlign:"center",color:COLORS.subtext,padding:32}}>Loading...</div> :
               members.length === 0 ? (
                <div style={{textAlign:"center",padding:32,color:COLORS.subtext}}>
                  <div>No members yet</div>
                  {group.group_type === "manual" && <button onClick={() => setShowAdd(true)} style={{marginTop:12,background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"8px 16px",fontWeight:700,cursor:"pointer"}}>+ Add contact</button>}
                </div>
              ) : members.map((m:any) => (
                <div key={m.contact_id} style={{background:COLORS.card,borderRadius:10,padding:12,marginBottom:8,display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:32,height:32,borderRadius:16,background:COLORS.primary+"20",color:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>{(m.contact_name || m.contact_id).charAt(0).toUpperCase()}</div>
                  <div style={{flex:1}}>
                    <div style={{color:COLORS.text,fontWeight:600,fontSize:14}}>{m.contact_name || "Unknown"}</div>
                    <div style={{color:COLORS.subtext,fontSize:11}}>{m.contact_id}</div>
                  </div>
                  <button onClick={() => doRemove(m)} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.subtext}}><X size={18}/></button>
                </div>
              ))}
              {group.group_type === "manual" && members.length > 0 && <button onClick={() => setShowAdd(true)} style={{marginTop:8,background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px",width:"100%",fontWeight:700,cursor:"pointer"}}>+ Add contact</button>}
            </>
          )}
          {tab === "settings" && (
            <>
              <div style={{background:COLORS.card,borderRadius:10,padding:12,marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:4}}>Tone</div>
                <div style={{color:COLORS.text,fontSize:14}}>{group.tone || "Default (use global)"}</div>
              </div>
              <div style={{background:COLORS.card,borderRadius:10,padding:12,marginBottom:8}}>
                <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:4}}>Auto-Reply</div>
                <div style={{color:COLORS.text,fontSize:14}}>{group.auto_reply || "Default (use global)"}</div>
              </div>
              {group.custom_instructions && (
                <div style={{background:COLORS.card,borderRadius:10,padding:12,marginBottom:8}}>
                  <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:4}}>Custom Instructions</div>
                  <div style={{color:COLORS.text,fontSize:13,lineHeight:1.5}}>{group.custom_instructions}</div>
                </div>
              )}
              <button onClick={onEdit} style={{marginTop:8,background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px",width:"100%",fontWeight:700,cursor:"pointer"}}>Edit Settings</button>
            </>
          )}
          {tab === "rules" && (
            <WebRuleBuilder group={group} onChange={onChange}/>
          )}
        </div>
      </div>

      {showAdd && (
        <div onClick={(e) => { e.stopPropagation(); setShowAdd(false); }} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1100,padding:20}}>
          <div onClick={(e) => e.stopPropagation()} style={{background:COLORS.bg,borderRadius:16,maxWidth:500,width:"100%",maxHeight:"80vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:17,fontWeight:700,color:COLORS.text}}>Add Contact</div>
              <button onClick={() => setShowAdd(false)} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.text}}><X size={22}/></button>
            </div>
            <div style={{padding:16,overflowY:"auto",flex:1}}>
              {contacts.length === 0 ? (
                <div style={{textAlign:"center",padding:32,color:COLORS.subtext}}>No contacts yet. Once WhatsApp messages start flowing, contacts will appear here.</div>
              ) : contacts.map((c:any) => {
                const already = members.some((m:any) => m.contact_id === c.contact_id);
                return (
                  <div key={c.contact_id} onClick={() => !already && doAdd(c)} style={{background:COLORS.card,borderRadius:10,padding:10,marginBottom:6,display:"flex",alignItems:"center",gap:10,cursor:already?"default":"pointer",opacity:already?0.5:1}}>
                    <div style={{width:32,height:32,borderRadius:16,background:COLORS.primary+"20",color:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700}}>{(c.contact_name || c.contact_id).charAt(0).toUpperCase()}</div>
                    <div style={{flex:1}}>
                      <div style={{color:COLORS.text,fontWeight:600,fontSize:14}}>{c.contact_name || "Unknown"}</div>
                      <div style={{color:COLORS.subtext,fontSize:11}}>{c.message_count} messages</div>
                    </div>
                    {already && <span style={{color:COLORS.success,fontSize:11,fontWeight:700}}>ADDED</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SentimentPage() {
  const [trends, setTrends] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("jedichatai_sentiment")||"[]"); } catch(e) { return []; }
  });

  const emotionColors = {
    joy: "#34C759", anger: "#FF3B30", sadness: "#5856D6",
    fear: "#FF9500", surprise: "#007AFF", neutral: "#8E8E93",
    love: "#FF2D55", disgust: "#AF52DE"
  };



  const counts = trends.reduce((acc, t) => {
    acc[t.emotion] = (acc[t.emotion]||0) + 1;
    return acc;
  }, {});

  const total = trends.length;
  const topEmotion = Object.entries(counts).sort((a,b)=>b[1]-a[1])[0];

  const contactTrends = trends.reduce((acc, t) => {
    if (!acc[t.contact]) acc[t.contact] = [];
    acc[t.contact].push(t);
    return acc;
  }, {});

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:"#fff",padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Sentiment Trends</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Emotion analysis across conversations</div>
      </div>

      <div style={{padding:16}}>
        {total === 0 ? (
          <div style={{background:"#fff",borderRadius:16,padding:32,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:48,marginBottom:12}}>{<BarChart2 size={18}/>}</div>
            <div style={{fontSize:16,fontWeight:600}}>No sentiment data yet</div>
            <div style={{fontSize:13,color:COLORS.subtext,marginTop:8}}>Sentiment data will appear here as you receive and process WhatsApp messages.</div>
          </div>
        ) : (
          <>
            <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
              <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>{<BarChart2 size={14}/>} EMOTION OVERVIEW</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
                <div style={{background:"#F0F7FF",borderRadius:12,padding:12,textAlign:"center"}}>
                  <div style={{fontSize:28}}>{topEmotion ? <EmotionDot emotion={topEmotion[0]} size={20}/> : null}</div>
                  <div style={{fontSize:12,fontWeight:700,color:COLORS.primary,marginTop:4}}>TOP EMOTION</div>
                  <div style={{fontSize:14,fontWeight:600,textTransform:"capitalize"}}>{topEmotion?.[0]||"None"}</div>
                </div>
                <div style={{background:"#F0FFF4",borderRadius:12,padding:12,textAlign:"center"}}>
                  <div style={{fontSize:28}}>{<Mail size={18}/>}</div>
                  <div style={{fontSize:12,fontWeight:700,color:COLORS.success,marginTop:4}}>TOTAL ANALYZED</div>
                  <div style={{fontSize:14,fontWeight:600}}>{total} messages</div>
                </div>
              </div>
              {Object.entries(counts).sort((a,b)=>b[1]-a[1]).map(([emotion, count]) => (
                <div key={emotion} style={{marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:13}}><EmotionDot emotion={emotion}/>{emotion}</span>
                    <span style={{fontSize:13,fontWeight:600}}>{count} ({Math.round(count/total*100)}%)</span>
                  </div>
                  <div style={{height:8,background:"#F0F0F0",borderRadius:4}}>
                    <div style={{height:8,borderRadius:4,background:emotionColors[emotion]||COLORS.primary,width:(count/total*100)+"%",transition:"width 0.5s"}}/>
                  </div>
                </div>
              ))}
            </div>

            <div style={{background:"#fff",borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
              <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>👥 BY CONTACT</div>
              {Object.entries(contactTrends).map(([contact, data]) => {
                const latest = data[data.length-1];
                const contactCounts = data.reduce((acc,t)=>{acc[t.emotion]=(acc[t.emotion]||0)+1;return acc;},{});
                const topE = Object.entries(contactCounts).sort((a,b)=>b[1]-a[1])[0];
                return (
                  <div key={contact} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+COLORS.border}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:36,height:36,borderRadius:18,background:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:14,fontWeight:700}}>
                        {contact[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontSize:14,fontWeight:600}}>{contact}</div>
                        <div style={{fontSize:11,color:COLORS.subtext}}>{data.length} messages analyzed</div>
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:20}}><EmotionDot emotion={topE?.[0]} size={14}/></div>
                      <div style={{fontSize:10,color:emotionColors[topE?.[0]]||COLORS.subtext,fontWeight:600,textTransform:"capitalize"}}>{topE?.[0]||"neutral"}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{background:"#fff",borderRadius:16,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
              <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>🕐 RECENT ACTIVITY</div>
              {trends.slice(-10).reverse().map((t, i) => (
                <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:"1px solid "+COLORS.border}}>
                  <span style={{fontSize:20}}><EmotionDot emotion={t.emotion} size={14}/></span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600}}>{t.contact}</div>
                    <div style={{fontSize:11,color:COLORS.subtext}}>{t.date}</div>
                  </div>
                  <span style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:(emotionColors[t.emotion]||COLORS.primary)+"20",color:emotionColors[t.emotion]||COLORS.primary,fontWeight:600,textTransform:"capitalize"}}>{t.emotion}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function HistoryPage() {
  const [history, setHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState<string|null>(null);
  const [deleting, setDeleting] = useState<number|null>(null);

  const load = async () => {
    try {
      const res = await api.get("/api/v1/messages/history");
      setHistory(res.data.messages || []);
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = history.filter(h =>
    !search || (h.contact_name||"").toLowerCase().includes(search.toLowerCase()) ||
    (h.message_text||"").toLowerCase().includes(search.toLowerCase()) ||
    (h.ai_reply||"").toLowerCase().includes(search.toLowerCase())
  );

  const copyReply = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const deleteItem = async (id: number) => {
    if (!window.confirm("Remove this item from history?")) return;
    setDeleting(id);
    try {
      await api.delete("/api/v1/messages/history/" + id);
      setHistory(h => h.filter((x:any) => x.id !== id));
    } catch(e) { setHistory(h => h.filter((x:any) => x.id !== id)); }
    setDeleting(null);
  };

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>History</div>
          <div style={{fontSize:13,color:COLORS.subtext}}>{filtered.length} of {history.length} messages</div>
        </div>
      </div>
      <div style={{padding:"12px 16px 8px",background:COLORS.card,borderBottom:"1px solid "+COLORS.border}}>
        <div style={{display:"flex",alignItems:"center",background:COLORS.bg,borderRadius:10,padding:"8px 12px",gap:8}}>
          <span>{<Search size={18}/>}</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by contact, message or reply..."
            style={{flex:1,border:"none",background:"transparent",fontSize:14,color:COLORS.text,outline:"none"}}/>
          {search && <button onClick={()=>setSearch("")} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.subtext,fontSize:16}}>×</button>}
        </div>
      </div>


      <div style={{padding:"0 16px"}}>
        {loading ? <div style={{textAlign:"center",padding:40,color:COLORS.subtext}}>Loading...</div> :
         filtered.length === 0 ? (
          <div style={{textAlign:"center",padding:48,color:COLORS.subtext}}>
            <div style={{fontSize:48,marginBottom:12}}>{<Inbox size={18}/>}</div>
            <div style={{fontSize:16,fontWeight:700,color:COLORS.text,marginBottom:6}}>{search ? "No results" : "No history yet"}</div>
          </div>
        ) : filtered.map((h: any) => (
          <div key={h.id||h.contact_id} style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:36,height:36,borderRadius:18,background:COLORS.primary+"20",color:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:14}}>
                  {(h.contact_name||h.contact_id||"?")[0].toUpperCase()}
                </div>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:COLORS.text}}>{h.contact_name||h.contact_id||"Unknown"}</div>
                  <div style={{fontSize:11,color:COLORS.subtext}}>{h.created_at?new Date(h.created_at).toLocaleString():""}</div>
                </div>
              </div>
              <button onClick={()=>h.id&&deleteItem(h.id)} disabled={deleting===h.id}
                style={{background:"none",border:"none",cursor:"pointer",color:COLORS.danger,fontSize:16,opacity:deleting===h.id?0.5:1,padding:4}}>🗑</button>
            </div>
            <div style={{fontSize:13,color:COLORS.subtext,marginBottom:8,padding:"8px 10px",background:COLORS.bg,borderRadius:8}}>{h.message_text}</div>
            {h.ai_reply && (
              <div style={{background:"#F0F7FF",borderRadius:10,padding:10}}>
                <div style={{fontSize:11,fontWeight:700,color:COLORS.primary,marginBottom:4}}>AI REPLY</div>
                <div style={{fontSize:13,color:COLORS.text,marginBottom:8}}>{h.ai_reply}</div>
                <button onClick={()=>copyReply(h.ai_reply, String(h.id))}
                  style={{background:copied===String(h.id)?"#E8F5E9":COLORS.card,border:"1px solid "+(copied===String(h.id)?COLORS.success:COLORS.border),borderRadius:8,padding:"4px 12px",fontSize:12,color:copied===String(h.id)?COLORS.success:COLORS.primary,cursor:"pointer"}}>
                  {copied===String(h.id)?"Copied":"Copy"}
                </button>
              </div>
            )}
          </div>
        ))}
        
      </div>
    </div>
  );
}

function TemplatesPage() {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [planInfo, setPlanInfo] = useState({count:0, limit:10, plan:"free"});
  const [filterCat, setFilterCat] = useState("All");
  const [editor, setEditor] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<number|null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");

  const toggleSelect = (id: number) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
  const selectAll = () => setSelected(templates.map(t=>t.id));
  const clearSelect = () => setSelected([]);

  const bulkDelete = async () => {
    if (!window.confirm("Delete " + selected.length + " templates? This cannot be undone.")) return;
    try {
      await Promise.all(selected.map(id => deleteTemplate(id)));
      setSelected([]);
      await load();
    } catch(e) { window.alert("Could not delete all"); }
  };

  const exportCSV = () => {
    const rows = [["ID","Title","Category","Body","Usage Count"],
      ...templates.filter(t=>selected.length===0||selected.includes(t.id))
        .map(t=>[t.id, '"'+t.title.replace(/"/g,'""')+'"', t.category, '"'+t.body.replace(/"/g,'""')+'"', t.usage_count||0])];
    const csv = rows.map(r=>r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = "templates.csv"; a.click();
  };

  const CATEGORIES = ["General","Greetings","Sales","Support","Personal","Business","Follow-up"];
  const ICONS = ["MessageCircle","ThumbsUp","Phone","Mail","Briefcase","Heart","Star","Clock","CheckCircle","Bell","Smile","Coffee"];

  const load = async () => {
    try {
      const data = await getTemplates();
      setTemplates(data.templates || []);
      setPlanInfo({count: data.count||0, limit: data.limit||10, plan: data.plan||"free"});
    } catch(e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    if (planInfo.count >= planInfo.limit) {
      window.alert("Template limit reached (" + planInfo.count + "/" + planInfo.limit + "). Upgrade your plan to add more.");
      return;
    }
    setEditor({title:"", body:"", category:"General", icon:"MessageCircle"});
  };
  const openEdit = (t: any) => setEditor({...t});

  const saveEditor = async () => {
    if (!editor.title?.trim() || !editor.body?.trim()) { window.alert("Title and body are required"); return; }
    setSaving(true);
    try {
      if (editor.id) {
        await updateTemplate(editor.id, {title: editor.title, body: editor.body, category: editor.category, icon: editor.icon});
      } else {
        await createTemplate({title: editor.title, body: editor.body, category: editor.category, icon: editor.icon});
      }
      setEditor(null);
      await load();
    } catch(e: any) {
      window.alert(e?.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  const doDelete = async (t: any) => {
    if (!window.confirm('Delete "' + t.title + '"? This cannot be undone.')) return;
    try { await deleteTemplate(t.id); setEditor(null); await load(); }
    catch(e) { window.alert("Could not delete"); }
  };

  const copyToClipboard = async (t: any) => {
    navigator.clipboard.writeText(t.body);
    setCopied(t.id);
    setTimeout(() => setCopied(null), 2000);
    useTemplate(t.id).catch(() => {});
  };

  const filtered = (filterCat === "All" ? templates : templates.filter((t:any) => t.category === filterCat)).filter((t:any) => search==="" || t.title.toLowerCase().includes(search.toLowerCase()) || t.body.toLowerCase().includes(search.toLowerCase()));
  const grouped: any = {};
  filtered.forEach((t:any) => { (grouped[t.category] = grouped[t.category] || []).push(t); });
  const groupKeys = Object.keys(grouped).sort();

  if (loading) return <div style={{padding:40, textAlign:"center", color:COLORS.subtext}}>Loading templates...</div>;

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Templates</div>
          <div style={{fontSize:13,color:COLORS.subtext}}>{planInfo.count}/{planInfo.limit===10000?"∞":planInfo.limit} on {planInfo.plan} plan</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {selected.length > 0 && <>
            <button onClick={bulkDelete} style={{background:COLORS.danger,color:"#fff",border:"none",borderRadius:10,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>🗑 Delete {selected.length}</button>
            <button onClick={exportCSV} style={{background:COLORS.success,color:"#fff",border:"none",borderRadius:10,padding:"8px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>⬇ Export</button>
            <button onClick={clearSelect} style={{background:"none",border:"1px solid "+COLORS.border,borderRadius:10,padding:"8px 12px",fontSize:13,cursor:"pointer",color:COLORS.subtext}}>✕</button>
          </>}
          {selected.length === 0 && <button onClick={exportCSV} style={{background:"none",border:"1px solid "+COLORS.border,borderRadius:10,padding:"8px 14px",fontSize:13,cursor:"pointer",color:COLORS.subtext}}>⬇ Export All</button>}
          <button onClick={selected.length===templates.length?clearSelect:selectAll}
            style={{background:"none",border:"1px solid "+COLORS.border,borderRadius:10,padding:"8px 12px",fontSize:13,cursor:"pointer",color:COLORS.subtext}}>
            {selected.length===templates.length?"Deselect All":"Select All"}
          </button>
          <button onClick={openCreate}
            style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"10px 18px",fontSize:14,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
            <Plus size={16}/> New Template
          </button>
        </div>
      </div>

      <div style={{padding:"12px 16px",display:"flex",gap:8,overflowX:"auto"}}>
        {["All", ...CATEGORIES].map(cat => (
          <button key={cat} onClick={() => setFilterCat(cat)}
            style={{padding:"6px 14px",borderRadius:16,border:"none",fontSize:13,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",background: filterCat===cat?COLORS.primary:COLORS.card, color: filterCat===cat?"#fff":COLORS.text}}>
            {cat}
          </button>
        ))}
      </div>
      <div style={{padding:"0 16px 12px",display:"flex",gap:8,alignItems:"center"}}>
        <div style={{flex:1,display:"flex",alignItems:"center",background:COLORS.card,borderRadius:10,padding:"8px 12px",gap:8,border:"1px solid "+COLORS.border}}>
          <span style={{color:COLORS.subtext}}>{<Search size={18}/>}</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search templates..."
            style={{flex:1,border:"none",background:"transparent",fontSize:14,color:COLORS.text,outline:"none"}}/>
          {search && <button onClick={()=>setSearch("")} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.subtext,fontSize:16}}>×</button>}
        </div>
      </div>

      <div style={{padding:"8px 16px"}}>
        {templates.length === 0 ? (
          <div style={{textAlign:"center",padding:48,color:COLORS.subtext}}>
            <FileText size={48} style={{opacity:0.4,marginBottom:12}}/>
            <div style={{fontSize:16,fontWeight:700,marginBottom:6,color:COLORS.text}}>No templates yet</div>
            <div style={{fontSize:13}}>Click "New Template" to create your first quick reply</div>
          </div>
        ) : groupKeys.length === 0 ? (
          <div style={{textAlign:"center",padding:32,color:COLORS.subtext}}>No templates in {filterCat}</div>
        ) : groupKeys.map(cat => (
          <div key={cat} style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",letterSpacing:1,marginBottom:8,paddingLeft:4}}>{cat}</div>
            {grouped[cat].map((t:any) => (
              <div key={t.id} style={{position:"relative"}}>
              <input type="checkbox" checked={selected.includes(t.id)} onChange={e=>{e.stopPropagation();toggleSelect(t.id);}}
                style={{position:"absolute",top:14,left:14,width:16,height:16,cursor:"pointer",zIndex:2,accentColor:COLORS.primary}}/>
              <div onClick={() => openEdit(t)}
                style={{background:COLORS.card,borderRadius:14,padding:14,paddingLeft:38,marginBottom:8,cursor:"pointer",border:"1px solid "+(selected.includes(t.id)?COLORS.primary:COLORS.border)}}>
                <div style={{display:"flex",alignItems:"center",marginBottom:6}}>
                  <div style={{width:32,height:32,borderRadius:8,background:COLORS.primary+"20",display:"flex",alignItems:"center",justifyContent:"center",marginRight:10,color:COLORS.primary}}>
                    <MessageCircle size={16}/>
                  </div>
                  <div style={{flex:1,fontSize:15,fontWeight:700,color:COLORS.text}}>{t.title}</div>
                  <div style={{fontSize:11,color:COLORS.subtext}}>Used {t.usage_count||0}x</div>
                </div>
                <div style={{fontSize:13,color:COLORS.subtext,lineHeight:1.5,marginBottom:10,overflow:"hidden",textOverflow:"ellipsis",display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical"}}>{t.body}</div>
                <button onClick={(e) => { e.stopPropagation(); copyToClipboard(t); }}
                  style={{background:copied===t.id?COLORS.success:COLORS.primary+"15",color:copied===t.id?"#fff":COLORS.primary,border:"none",borderRadius:8,padding:"6px 14px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                  {copied===t.id?"Copied":"Copy"}
                </button>
              </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {editor && (
        <div onClick={() => setEditor(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000,padding:20}}>
          <div onClick={(e) => e.stopPropagation()}
            style={{background:COLORS.bg,borderRadius:16,maxWidth:560,width:"100%",maxHeight:"90vh",overflow:"hidden",display:"flex",flexDirection:"column"}}>
            <div style={{padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{fontSize:18,fontWeight:700,color:COLORS.text}}>{editor.id?"Edit Template":"New Template"}</div>
              <button onClick={() => setEditor(null)} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.text}}><X size={22}/></button>
            </div>

            <div style={{padding:20,overflowY:"auto",flex:1}}>
              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Title</div>
              <input value={editor.title} onChange={e => setEditor({...editor, title: e.target.value})}
                placeholder="e.g. Thanks for reaching out" maxLength={100}
                style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box",marginBottom:16,background:COLORS.card,color:COLORS.text}}/>

              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Message Body</div>
              <textarea value={editor.body} onChange={e => setEditor({...editor, body: e.target.value})}
                placeholder="Type your template message..." rows={5}
                style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box",resize:"vertical",marginBottom:16,background:COLORS.card,color:COLORS.text,fontFamily:"inherit"}}/>

              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Category</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:16}}>
                {CATEGORIES.map(cat => (
                  <button key={cat} onClick={() => setEditor({...editor, category: cat})}
                    style={{padding:"6px 12px",borderRadius:14,border:"1px solid "+(editor.category===cat?COLORS.primary:COLORS.border),background:editor.category===cat?COLORS.primary:COLORS.card,color:editor.category===cat?"#fff":COLORS.text,fontSize:13,cursor:"pointer"}}>
                    {cat}
                  </button>
                ))}
              </div>

              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Icon</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {ICONS.map(ic => {
                  const selected = editor.icon === ic;
                  return (
                    <button key={ic} onClick={() => setEditor({...editor, icon: ic})}
                      title={ic}
                      style={{width:42,height:42,borderRadius:10,border:"1px solid "+(selected?COLORS.primary:COLORS.border),background:selected?COLORS.primary:COLORS.card,color:selected?"#fff":COLORS.text,display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",fontSize:11}}>
                      {ic.substring(0,2)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{padding:"12px 20px",borderTop:"1px solid "+COLORS.border,display:"flex",gap:8,justifyContent:"space-between"}}>
              <div>
                {editor.id && (
                  <button onClick={() => doDelete(editor)}
                    style={{background:"none",color:COLORS.danger,border:"1px solid "+COLORS.danger,borderRadius:10,padding:"8px 16px",fontSize:14,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
                    <Trash2 size={14}/> Delete
                  </button>
                )}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={() => setEditor(null)}
                  style={{background:COLORS.card,color:COLORS.text,border:"1px solid "+COLORS.border,borderRadius:10,padding:"8px 18px",fontSize:14,fontWeight:600,cursor:"pointer"}}>Cancel</button>
                <button onClick={saveEditor} disabled={saving}
                  style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"8px 20px",fontSize:14,fontWeight:700,cursor:"pointer",opacity:saving?0.6:1}}>
                  {saving?"Saving...":"Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const LANGUAGES = [
  {value:"en",label:"English",flag:"🇺🇸"},{value:"fr",label:"Français",flag:"🇫🇷"},{value:"es",label:"Español",flag:"🇪🇸"},
  {value:"pt",label:"Português",flag:"🇧🇷"},{value:"ar",label:"Arabic / العربية",flag:"🇸🇦"},{value:"zh",label:"Chinese / 中文",flag:"🇨🇳"},
  {value:"hi",label:"Hindi / हिन्दी",flag:"🇮🇳"},{value:"de",label:"Deutsch",flag:"🇩🇪"},{value:"it",label:"Italiano",flag:"🇮🇹"},
  {value:"ru",label:"Русский",flag:"🇷🇺"},{value:"ja",label:"日本語",flag:"🇯🇵"},{value:"ko",label:"한국어",flag:"🇰🇷"},
  {value:"nl",label:"Nederlands",flag:"🇳🇱"},{value:"pl",label:"Polski",flag:"🇵🇱"},{value:"tr",label:"Türkçe",flag:"🇹🇷"},
  {value:"vi",label:"Tiếng Việt",flag:"🇻🇳"},{value:"th",label:"ภาษาไทย",flag:"🇹🇭"},{value:"id",label:"Bahasa Indonesia",flag:"🇮🇩"},
  {value:"ms",label:"Bahasa Melayu",flag:"🇲🇾"},{value:"fa",label:"فارسی",flag:"🇮🇷"},{value:"ur",label:"اردو",flag:"🇵🇰"},
  {value:"bn",label:"বাংলা",flag:"🇧🇩"},{value:"ta",label:"தமிழ்",flag:"🇮🇳"},{value:"te",label:"తెలుగు",flag:"🇮🇳"},
  {value:"mr",label:"मराठी",flag:"🇮🇳"},{value:"gu",label:"ગુજરાતી",flag:"🇮🇳"},{value:"kn",label:"ಕನ್ನಡ",flag:"🇮🇳"},
  {value:"ml",label:"മലയാളം",flag:"🇮🇳"},{value:"pa",label:"ਪੰਜਾਬੀ",flag:"🇮🇳"},{value:"ro",label:"Română",flag:"🇷🇴"},
  {value:"hu",label:"Magyar",flag:"🇭🇺"},{value:"cs",label:"Čeština",flag:"🇨🇿"},{value:"sk",label:"Slovenčina",flag:"🇸🇰"},
  {value:"bg",label:"Български",flag:"🇧🇬"},{value:"hr",label:"Hrvatski",flag:"🇭🇷"},{value:"sr",label:"Српски",flag:"🇷🇸"},
  {value:"uk",label:"Українська",flag:"🇺🇦"},{value:"el",label:"Ελληνικά",flag:"🇬🇷"},{value:"da",label:"Dansk",flag:"🇩🇰"},
  {value:"fi",label:"Suomi",flag:"🇫🇮"},{value:"nb",label:"Norsk",flag:"🇳🇴"},{value:"sv",label:"Svenska",flag:"🇸🇪"},
  {value:"he",label:"עברית",flag:"🇮🇱"},{value:"ca",label:"Català",flag:"🇪🇸"},{value:"af",label:"Afrikaans",flag:"🇿🇦"},
  {value:"sq",label:"Shqip",flag:"🇦🇱"},{value:"hy",label:"Հայerեն",flag:"🇦🇲"},{value:"az",label:"Azərbaycan",flag:"🇦🇿"},
  {value:"eu",label:"Euskara",flag:"🇪🇸"},{value:"be",label:"Беларуская",flag:"🇧🇾"},{value:"bs",label:"Bosanski",flag:"🇧🇦"},
  {value:"my",label:"မြန်မာဘာသာ",flag:"🇲🇲"},{value:"km",label:"ភាសាខ្មែរ",flag:"🇰🇭"},{value:"et",label:"Eesti",flag:"🇪🇪"},
  {value:"ka",label:"ქართული",flag:"🇬🇪"},{value:"gl",label:"Galego",flag:"🇪🇸"},{value:"is",label:"Íslenska",flag:"🇮🇸"},
  {value:"kk",label:"Қазақша",flag:"🇰🇿"},{value:"lo",label:"ລາວ",flag:"🇱🇦"},{value:"lv",label:"Latviešu",flag:"🇱🇻"},
  {value:"lt",label:"Lietuvių",flag:"🇱🇹"},{value:"mk",label:"Македонски",flag:"🇲🇰"},{value:"mt",label:"Malti",flag:"🇲🇹"},
  {value:"mn",label:"Монгол",flag:"🇲🇳"},{value:"ne",label:"नेपाली",flag:"🇳🇵"},{value:"si",label:"සිංහල",flag:"🇱🇰"},
  {value:"sl",label:"Slovenščina",flag:"🇸🇮"},{value:"tl",label:"Filipino",flag:"🇵🇭"},{value:"uz",label:"Oʻzbek",flag:"🇺🇿"},
  {value:"cy",label:"Cymraeg",flag:"󠁧󠁢󠁷󠁬󠁳󠁿"},{value:"ky",label:"Кыргызча",flag:"🇰🇬"},{value:"tg",label:"Тоҷикӣ",flag:"🇹🇯"},
  {value:"tk",label:"Türkmençe",flag:"🇹🇲"},{value:"ku",label:"Kurdî",flag:"🇮🇶"},{value:"sd",label:"سنڌي",flag:"🇵🇰"},
  {value:"ps",label:"پښتو",flag:"🇦🇫"},{value:"ht",label:"Kreyòl ayisyen",flag:"🇭🇹"},{value:"lb",label:"Lëtzebuergesch",flag:"🇱🇺"},
  {value:"jv",label:"Basa Jawa",flag:"🇮🇩"},{value:"su",label:"Basa Sunda",flag:"🇮🇩"},{value:"ceb",label:"Cebuano",flag:"🇵🇭"},
  {value:"haw",label:"ʻŌlelo Hawaiʻi",flag:"🇺🇸"},{value:"sm",label:"Gagana Samoa",flag:"🇼🇸"},{value:"to",label:"Lea fakatonga",flag:"🇹🇴"},
  {value:"mi",label:"Te Reo Māori",flag:"🇳🇿"},{value:"fj",label:"Na Vosa Vakaviti",flag:"🇫🇯"},{value:"ty",label:"Reo Tahiti",flag:"🇵🇫"},
  {value:"sw",label:"Kiswahili",flag:"🇰🇪"},{value:"yo",label:"Yorùbá",flag:"🇳🇬"},{value:"ha",label:"Hausa",flag:"🇳🇬"},
  {value:"ig",label:"Igbo",flag:"🇳🇬"},{value:"am",label:"አማርኛ",flag:"🇪🇹"},{value:"om",label:"Oromoo",flag:"🇪🇹"},
  {value:"ti",label:"ትግርኛ",flag:"🇪🇹"},{value:"rw",label:"Kinyarwanda",flag:"🇷🇼"},{value:"lg",label:"Luganda",flag:"🇺🇬"},
  {value:"ln",label:"Lingála",flag:"🇨🇩"},{value:"sn",label:"chiShona",flag:"🇿🇼"},{value:"st",label:"Sesotho",flag:"🇿🇦"},
  {value:"tn",label:"Setswana",flag:"🇧🇼"},{value:"wo",label:"Wolof",flag:"🇸🇳"},{value:"ff",label:"Fulfulde",flag:"🇬🇳"},
  {value:"bm",label:"Bamanankan",flag:"🇲🇱"},{value:"tw",label:"Twi",flag:"🇬🇭"},{value:"ee",label:"Eʋegbe",flag:"🇬🇭"},
  {value:"ak",label:"Akan",flag:"🇬🇭"},{value:"ny",label:"Chichewa",flag:"🇲🇼"},{value:"so",label:"Soomaali",flag:"🇸🇴"},
  {value:"mg",label:"Malagasy",flag:"🇲🇬"},{value:"sg",label:"Sango",flag:"🇨🇫"},{value:"ts",label:"Xitsonga",flag:"🇿🇦"},
  {value:"ss",label:"Siswati",flag:"🇸🇿"},{value:"ve",label:"Tshivenda",flag:"🇿🇦"},{value:"nr",label:"isiNdebele",flag:"🇿🇦"},
  {value:"nso",label:"Sepedi",flag:"🇿🇦"},{value:"xh",label:"isiXhosa",flag:"🇿🇦"},{value:"zu",label:"isiZulu",flag:"🇿🇦"},
  {value:"kpe",label:"Kpelle",flag:"🇱🇷"},{value:"bss",label:"Bassa",flag:"🇱🇷"},{value:"grb",label:"Grebo",flag:"🇱🇷"},
  {value:"vai",label:"Vai",flag:"🇱🇷"},{value:"mfy",label:"Mandingo",flag:"🇱🇷"},{value:"loe",label:"Loma",flag:"🇱🇷"},
  {value:"mah",label:"Mano",flag:"🇱🇷"},{value:"dan",label:"Dan (Gio)",flag:"🇱🇷"},{value:"pcm",label:"Nigerian Pidgin",flag:"🇳🇬"},
  {value:"tiv",label:"Tiv",flag:"🇳🇬"},{value:"kan",label:"Kanuri",flag:"🇳🇬"},{value:"nus",label:"Nuer",flag:"🇸🇸"},
  {value:"din",label:"Dinka",flag:"🇸🇸"},{value:"luo",label:"Luo",flag:"🇰🇪"},{value:"kik",label:"Kikuyu",flag:"🇰🇪"},
  {value:"kam",label:"Kamba",flag:"🇰🇪"},{value:"mer",label:"Kimeru",flag:"🇰🇪"},{value:"ach",label:"Acholi",flag:"🇺🇬"},
  {value:"lgg",label:"Lugbara",flag:"🇺🇬"},{value:"nyn",label:"Runyankore",flag:"🇺🇬"},{value:"run",label:"Kirundi",flag:"🇧🇮"},
  {value:"ber",label:"Tamazight",flag:"🇲🇦"},{value:"kab",label:"Taqbaylit",flag:"🇩🇿"},{value:"ary",label:"Moroccan Arabic",flag:"🇲🇦"},
  {value:"arz",label:"Egyptian Arabic",flag:"🇪🇬"},{value:"kri",label:"Krio",flag:"🇸🇱"},{value:"men",label:"Mende",flag:"🇸🇱"},
  {value:"tem",label:"Temne",flag:"🇸🇱"},{value:"sus",label:"Susu",flag:"🇬🇳"},{value:"mos",label:"Mooré",flag:"🇧🇫"},
  {value:"ewe",label:"Ewe",flag:"🇹🇬"},{value:"fon",label:"Fon",flag:"🇧🇯"},{value:"dyu",label:"Dioula",flag:"🇨🇮"},
  {value:"gaa",label:"Ga",flag:"🇬🇭"},{value:"nqo",label:"N'Ko",flag:"🇬🇳"},
];

function LanguageSelector() {
  const [selected, setSelected] = React.useState(localStorage.getItem("i18nextLng")||"en");
  const [search, setSearch] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const current = LANGUAGES.find(l=>l.value===selected)||LANGUAGES[0];
  const filtered = LANGUAGES.filter(l=>l.label.toLowerCase().includes(search.toLowerCase())||l.flag.includes(search));
  return (
    <div style={{padding:"8px 0"}}>
      <div onClick={()=>setOpen(!open)} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderRadius:10,border:"1px solid #e5e5e5",cursor:"pointer",background:"#f8f9fa"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:24}}>{current.flag}</span>
          <span style={{fontSize:14,fontWeight:600}}>{current.label}</span>
        </div>
        <span style={{color:"#666"}}>{open?"▲":"▼"}</span>
      </div>
      {open && (
        <div style={{border:"1px solid #e5e5e5",borderRadius:10,marginTop:8,overflow:"hidden",boxShadow:"0 4px 20px rgba(0,0,0,0.1)"}}>
          <input placeholder="Search language..." value={search} onChange={e=>setSearch(e.target.value)}
            style={{width:"100%",padding:"10px 14px",border:"none",borderBottom:"1px solid #e5e5e5",fontSize:14,outline:"none",boxSizing:"border-box"}}/>
          <div style={{maxHeight:300,overflowY:"auto"}}>
            {filtered.map(lang=>(
              <div key={lang.value} onClick={()=>{setSelected(lang.value);localStorage.setItem("i18nextLng",lang.value);setOpen(false);}}
                style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",cursor:"pointer",background:selected===lang.value?"#F0F7FF":"#fff",borderBottom:"1px solid #f5f5f5"}}>
                <span style={{fontSize:22}}>{lang.flag}</span>
                <span style={{fontSize:14,color:selected===lang.value?"#007AFF":"#333",fontWeight:selected===lang.value?700:400}}>{lang.label}</span>
                {selected===lang.value && <span style={{marginLeft:"auto",color:"#007AFF"}}>✓</span>}
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{fontSize:11,color:"#999",marginTop:6}}>App reloads when language changes</div>
    </div>
  );
}


function TopNav() {
  const photo = localStorage.getItem("jedichatai_profile_photo");
  const name = localStorage.getItem("jedichatai_name") || "J";
  const initial = name.charAt(0).toUpperCase();
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 16px",background:"#fff",borderBottom:"1px solid "+COLORS.border,position:"sticky",top:0,zIndex:100}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <img src="/logo.png" alt="JediChatAI" style={{width:32,height:32,borderRadius:8,objectFit:"contain"}}/>
        <span style={{fontSize:16,fontWeight:800,color:COLORS.primary}}>JediChatAI</span>
      </div>
      <a href="/settings" style={{textDecoration:"none"}}>
        {photo ?
          <img src={photo} alt="Profile" style={{width:36,height:36,borderRadius:"50%",objectFit:"cover",border:"2px solid "+COLORS.primary}}/> :
          <div style={{width:36,height:36,borderRadius:"50%",background:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:"#fff",fontWeight:700}}>{initial}</div>
        }
      </a>
    </div>
  );
}


function SessionsManager() {
  const [sessions, setSessions] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [revoking, setRevoking] = React.useState<string|null>(null);
  const [revokingAll, setRevokingAll] = React.useState(false);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/v1/auth/sessions");
      setSessions(res.data.sessions || []);
    } catch(e) { setSessions([]); }
    setLoading(false);
  };

  React.useEffect(() => { loadSessions(); }, []);

  const revokeSession = async (id: string) => {
    if (!window.confirm("Sign out this device?")) return;
    setRevoking(id);
    try {
      await api.delete("/api/v1/auth/sessions/" + id);
      setSessions((prev:any[]) => prev.filter((s:any) => s.id !== id));
    } catch(e) { window.alert("Could not revoke session."); }
    setRevoking(null);
  };

  const revokeAll = async () => {
    if (!window.confirm("Sign out all other devices? You will stay logged in here.")) return;
    setRevokingAll(true);
    try {
      await api.delete("/api/v1/auth/sessions");
      await loadSessions();
    } catch(e) { window.alert("Error signing out other devices."); }
    setRevokingAll(false);
  };

  const getDeviceIcon = (s: any) => {
    const ua = (s.device_info || s.user_agent || "").toLowerCase();
    const type = (s.device_type || "").toLowerCase();
    if (type === "mobile" || ua.includes("android") || ua.includes("iphone")) return <Smartphone size={18}/>;
    if (type === "tablet" || ua.includes("ipad")) return <Smartphone size={18}/>;
    if (ua.includes("mac")) return <Laptop size={18}/>;
    if (ua.includes("windows")) return <Monitor size={18}/>;
    return <Globe size={18}/>;
  };

  const getDeviceName = (s: any) => {
    const ua = (s.device_info || s.user_agent || s.device_name || "").toLowerCase();
    if (ua.includes("android")) return "Android Device";
    if (ua.includes("iphone")) return "iPhone";
    if (ua.includes("ipad")) return "iPad";
    if (ua.includes("mac")) return "Mac";
    if (ua.includes("windows")) return "Windows PC";
    if (ua.includes("linux")) return "Linux Device";
    if (ua.includes("chrome")) return "Chrome Browser";
    if (ua.includes("firefox")) return "Firefox Browser";
    if (ua.includes("safari")) return "Safari Browser";
    return s.device_name || "Unknown Device";
  };

  const getBrowser = (s: any) => {
    const ua = (s.device_info || s.user_agent || "").toLowerCase();
    if (ua.includes("edg")) return "Edge";
    if (ua.includes("chrome")) return "Chrome";
    if (ua.includes("firefox")) return "Firefox";
    if (ua.includes("safari")) return "Safari";
    return "Browser";
  };

  const formatTime = (t: string) => {
    if (!t) return "Unknown";
    const diff = Math.floor((Date.now() - new Date(t).getTime()) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return Math.floor(diff/60) + "m ago";
    if (diff < 86400) return Math.floor(diff/3600) + "h ago";
    if (diff < 604800) return Math.floor(diff/86400) + "d ago";
    return new Date(t).toLocaleDateString();
  };

  if (loading) return <div style={{padding:"16px 0",textAlign:"center",color:COLORS.subtext,fontSize:13}}>Loading sessions...</div>;

  const sorted = [...sessions].sort((a:any,b:any) =>
    new Date(b.last_seen||b.updated_at||0).getTime() - new Date(a.last_seen||a.updated_at||0).getTime()
  );

  return (
    <div>
      <div style={{background:COLORS.bg,borderRadius:12,padding:"10px 14px",marginBottom:12,display:"flex",justifyContent:"space-between",alignItems:"center",border:"1px solid "+COLORS.border}}>
        <div style={{fontSize:13,color:COLORS.text}}>
          <span style={{fontWeight:700,color:COLORS.primary}}>{sessions.length}</span> active {sessions.length===1?"session":"sessions"}
          {sorted.length > 1 && <span style={{color:COLORS.warning}}> • {sorted.length-1} other</span>}
        </div>
        <button onClick={loadSessions} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.primary,fontSize:12,fontWeight:600}}>🔄 Refresh</button>
      </div>
      {sessions.length === 0 ? (
        <div style={{padding:"20px 0",textAlign:"center",color:COLORS.subtext,fontSize:13}}>No active sessions found.</div>
      ) : (
        <>
          {sorted.map((s:any, idx:number) => {
            const isCurrent = idx === 0;
            return (
              <div key={s.id} style={{background:isCurrent?"#F0F7FF":COLORS.card,borderRadius:12,padding:14,marginBottom:8,border:"1px solid "+(isCurrent?COLORS.primary:COLORS.border)}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:12,flex:1}}>
                    <div style={{fontSize:28,lineHeight:"1"}}>{getDeviceIcon(s)}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap" as const}}>
                        <span style={{fontSize:14,fontWeight:700,color:COLORS.text}}>{getDeviceName(s)}</span>
                        {isCurrent && <span style={{background:"#E8F5E9",color:COLORS.success,fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:10}}>✓ THIS DEVICE</span>}
                      </div>
                      <div style={{fontSize:12,color:COLORS.subtext,marginBottom:2}}>🌐 {getBrowser(s)} • {s.ip_address||"Unknown IP"}</div>
                      <div style={{fontSize:11,color:COLORS.subtext,marginBottom:1}}>🕐 Last active: {formatTime(s.last_seen||s.last_active||s.updated_at)}</div>
                      {s.created_at && <div style={{fontSize:11,color:COLORS.subtext}}>📅 Signed in: {new Date(s.created_at).toLocaleDateString()}</div>}
                    </div>
                  </div>
                  {!isCurrent && (
                    <button onClick={()=>revokeSession(s.id)} disabled={revoking===s.id}
                      style={{background:"#FF3B3010",border:"1px solid "+COLORS.danger,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer",color:COLORS.danger,flexShrink:0,opacity:revoking===s.id?0.5:1}}>
                      {revoking===s.id?"...":"Sign Out"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {sorted.length > 1 && (
            <button onClick={revokeAll} disabled={revokingAll}
              style={{width:"100%",background:"none",border:"1px solid "+COLORS.danger,borderRadius:12,padding:"12px",fontSize:13,fontWeight:700,cursor:"pointer",marginTop:8,color:COLORS.danger,opacity:revokingAll?0.5:1}}>
              {revokingAll?"Signing out...":"Sign Out All Other Devices ("+String(sorted.length-1)+")"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ============ HELP & FAQ PAGE ============
function HelpPage() {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<number|null>(null);
  const FAQS = [
    ["How do I connect WhatsApp?", "Go to the Conversations page and click 'Connect WhatsApp'. Scan the QR code with your WhatsApp app under Settings / Linked Devices / Link a Device. You'll be connected in under 2 minutes."],
    ["Why isn't the AI responding?", "Make sure WhatsApp is connected (green dot in Conversations). Also check that Auto-Respond is ON. If issues persist, disconnect and reconnect WhatsApp."],
    ["How do I enable Auto-Respond?", "On the Conversations page, toggle the Auto-Respond switch at the top. The AI will then send replies automatically without your approval."],
    ["What is the daily message limit?", "Free plan: 50 messages/day. Pro plan: 500 messages/day. Business plan: 5,000 messages/day. Usage resets at midnight UTC."],
    ["How do I upgrade my plan?", "Go to Settings / Subscription & Billing. Pro is $9.99 CAD/month and Business is $29.99 CAD/month. Cancel anytime."],
    ["Can I customize AI replies?", "Yes. Go to Settings / AI Preferences / Custom AI Instructions to add your preferred tone and style. You can also assign tones to contact groups."],
    ["How does risk detection work?", "The AI analyzes every message for financial scams, phishing links, and manipulation attempts. High-risk messages are flagged in red before you reply."],
    ["Is my WhatsApp data secure?", "Yes. All data is encrypted with AES-256. We never sell your data. Your WhatsApp session runs on a secure server accessible only by you."],
    ["How do I disconnect WhatsApp?", "Go to Conversations / Connect WhatsApp / Disconnect. You can also remove it from WhatsApp / Settings / Linked Devices."],
    ["What languages are supported?", "JediChatAI supports 181 languages. The AI auto-detects the language and replies in the same language as the contact."],
  ];
  const filtered = FAQS.filter(([q,a]) =>
    search === "" || q.toLowerCase().includes(search.toLowerCase()) || a.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Help & FAQ</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Find answers to common questions</div>
      </div>
      <div style={{padding:"12px 16px",background:COLORS.card,borderBottom:"1px solid "+COLORS.border}}>
        <div style={{display:"flex",alignItems:"center",background:COLORS.bg,borderRadius:12,padding:"8px 12px",gap:8,border:"1px solid "+COLORS.border}}>
          <span>{<Search size={18}/>}</span>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search help articles..."
            style={{flex:1,border:"none",background:"transparent",fontSize:14,color:COLORS.text,outline:"none"}}/>
          {search && <button onClick={()=>setSearch("")} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.subtext,fontSize:18}}>×</button>}
        </div>
      </div>
      <div style={{padding:16}}>
        <div style={{background:"#F0F7FF",borderRadius:12,padding:14,marginBottom:16,display:"flex",alignItems:"center",gap:12}}>
          <span style={{fontSize:24}}>✉️</span>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:COLORS.primary}}>Need more help?</div>
            <div style={{fontSize:12,color:COLORS.subtext}}>Email support@jedichatai.com. We respond within 24 hours.</div>
          </div>
        </div>
        {filtered.length === 0 ? (
          <div style={{textAlign:"center",padding:40,color:COLORS.subtext}}>No results for "{search}"</div>
        ) : filtered.map(([q,a],i) => (
          <div key={i} onClick={()=>setExpanded(expanded===i?null:i)}
            style={{background:COLORS.card,borderRadius:14,padding:16,marginBottom:8,cursor:"pointer",border:"1px solid "+(expanded===i?COLORS.primary:COLORS.border),boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontSize:14,fontWeight:700,color:expanded===i?COLORS.primary:COLORS.text,flex:1,marginRight:12}}>{q}</div>
              <span style={{color:COLORS.subtext,fontSize:18}}>{expanded===i?"▲":"▼"}</span>
            </div>
            {expanded===i && (
              <div style={{fontSize:13,color:COLORS.subtext,lineHeight:1.7,marginTop:12,paddingTop:12,borderTop:"1px solid "+COLORS.border}}>{a}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ FEEDBACK PAGE ============
function FeedbackPage() {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [category, setCategory] = useState("general");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const CATS = ["general","bug","feature","pricing","other"];

  const submit = async () => {
    if (rating === 0) { window.alert("Please select a star rating first"); return; }
    setSending(true);
    try {
      await api.post("/api/v1/feedback", { rating, comment, category });
      setSent(true);
    } catch(e) { setSent(true); }
    setSending(false);
  };

  if (sent) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",minHeight:"60vh",padding:32,textAlign:"center"}}>
      <div style={{fontSize:64,marginBottom:16}}>{<Star size={18}/>}</div>
      <div style={{fontSize:24,fontWeight:800,color:COLORS.primary,marginBottom:8}}>Thank You!</div>
      <div style={{fontSize:14,color:COLORS.subtext,lineHeight:1.6}}>Your feedback helps us improve JediChatAI for everyone. We read every submission.</div>
    </div>
  );

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Send Feedback</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Help us improve JediChatAI</div>
      </div>
      <div style={{padding:16}}>
        <div style={{background:COLORS.card,borderRadius:16,padding:20,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:15,fontWeight:700,color:COLORS.text,marginBottom:16,textAlign:"center"}}>How would you rate JediChatAI?</div>
          <div style={{display:"flex",justifyContent:"center",gap:12,marginBottom:8}}>
            {[1,2,3,4,5].map(star => (
              <span key={star} onClick={()=>setRating(star)} style={{fontSize:40,cursor:"pointer",filter:star<=rating?"none":"grayscale(1)",opacity:star<=rating?1:0.4}}>{<Star size={18}/>}</span>
            ))}
          </div>
          <div style={{textAlign:"center",fontSize:13,color:COLORS.subtext}}>
            {rating===0?"Tap to rate":rating===1?"Poor":rating===2?"Fair":rating===3?"Good":rating===4?"Great":"Excellent!"}
          </div>
        </div>
        <div style={{background:COLORS.card,borderRadius:16,padding:20,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:14,fontWeight:700,color:COLORS.text,marginBottom:10}}>Category</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {CATS.map(cat => (
              <button key={cat} onClick={()=>setCategory(cat)}
                style={{padding:"8px 16px",borderRadius:20,border:"1px solid "+(category===cat?COLORS.primary:COLORS.border),background:category===cat?COLORS.primary:"transparent",color:category===cat?"#fff":COLORS.text,fontSize:13,fontWeight:600,cursor:"pointer",textTransform:"capitalize"}}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div style={{background:COLORS.card,borderRadius:16,padding:20,marginBottom:24,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:14,fontWeight:700,color:COLORS.text,marginBottom:10}}>Comments (optional)</div>
          <textarea value={comment} onChange={e=>setComment(e.target.value)}
            placeholder="Tell us what you love, what could be better, or report a bug..."
            style={{width:"100%",minHeight:100,border:"1px solid "+COLORS.border,borderRadius:10,padding:12,fontSize:14,color:COLORS.text,resize:"vertical",outline:"none",boxSizing:"border-box",fontFamily:"inherit"}}/>
        </div>
        <button onClick={submit} disabled={sending||rating===0}
          style={{width:"100%",background:rating===0?COLORS.border:COLORS.primary,color:"#fff",border:"none",borderRadius:14,padding:16,fontSize:16,fontWeight:700,cursor:rating===0?"not-allowed":"pointer",opacity:sending?0.6:1}}>
          {sending?"Sending...":"Submit Feedback"}
        </button>
      </div>
    </div>
  );
}

// ============ TWO FACTOR PAGE ============
function TwoFactorPage() {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<"idle"|"sent"|"done">("idle");
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const email = localStorage.getItem("jedichatai_email") || "";

  useEffect(() => {
    api.get("/api/v1/auth/2fa-status").then(r => { setStatus(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const sendCode = async () => {
    setSending(true);
    try { await api.post("/api/v1/auth/enable-2fa", {}); setStep("sent"); }
    catch(e) { window.alert("Could not send code. Try again."); }
    setSending(false);
  };

  const verifyCode = async () => {
    if (code.length !== 6) { window.alert("Please enter the 6-digit code"); return; }
    setVerifying(true);
    try {
      await api.post("/api/v1/auth/verify-2fa", { email, otp: code });
      setStatus({...status, two_fa_enabled: true});
      setStep("done");
      setCode("");
    } catch(e: any) { window.alert(e?.response?.data?.detail || "Invalid code. Try again."); setCode(""); }
    setVerifying(false);
  };

  if (loading) return <div style={{display:"flex",justifyContent:"center",padding:40}}><div style={{color:COLORS.subtext}}>Loading...</div></div>;

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Two-Factor Auth</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Add extra security to your account</div>
      </div>
      <div style={{padding:16}}>
        <div style={{background:COLORS.card,borderRadius:16,padding:24,marginBottom:16,textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:64,marginBottom:12}}>{status?.two_fa_enabled?"":""}</div>
          <div style={{fontSize:18,fontWeight:700,color:COLORS.text,marginBottom:8}}>Two-Factor Authentication</div>
          <div style={{display:"inline-block",background:status?.two_fa_enabled?"#E8F5E9":"#FFF3E0",borderRadius:20,padding:"6px 16px",marginBottom:12}}>
            <span style={{fontSize:14,fontWeight:700,color:status?.two_fa_enabled?COLORS.success:COLORS.warning}}>
              {status?.two_fa_enabled?"Enabled":"Not Enabled"}
            </span>
          </div>
          <div style={{fontSize:13,color:COLORS.subtext,lineHeight:1.6}}>
            {status?.two_fa_enabled
              ? "Your account is protected. A code is sent to your email when signing in."
              : "A 6-digit code will be sent to your email each time you sign in."}
          </div>
        </div>

        {step === "idle" && !status?.two_fa_enabled && (
          <div style={{background:COLORS.card,borderRadius:16,padding:20,marginBottom:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{fontSize:14,fontWeight:700,color:COLORS.text,marginBottom:12}}>How it works</div>
            {["A 6-digit code is sent to your email","Enter the code every time you sign in","Even if someone has your password, they need the code","You can disable it anytime"].map((t,i) => (
              <div key={i} style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
                <div style={{width:22,height:22,borderRadius:11,background:COLORS.primary+"20",color:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:700,fontSize:12,flexShrink:0}}>{i+1}</div>
                <div style={{fontSize:13,color:COLORS.subtext,lineHeight:1.5}}>{t}</div>
              </div>
            ))}
            <button onClick={sendCode} disabled={sending}
              style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:14,fontSize:15,fontWeight:700,cursor:"pointer",marginTop:8,opacity:sending?0.6:1}}>
              {sending?"Sending code...":"Enable 2FA"}
            </button>
          </div>
        )}

        {step === "sent" && (
          <div style={{background:COLORS.card,borderRadius:16,padding:20,marginBottom:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{fontSize:48,marginBottom:12}}>{<Mail size={18}/>}</div>
              <div style={{fontSize:16,fontWeight:700,color:COLORS.text,marginBottom:6}}>Check your email</div>
              <div style={{fontSize:13,color:COLORS.subtext}}>We sent a 6-digit code to <strong>{email}</strong></div>
            </div>
            <input value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,"").substring(0,6))}
              placeholder="000000" maxLength={6}
              style={{width:"100%",padding:14,borderRadius:12,border:"2px solid "+(code.length===6?COLORS.primary:COLORS.border),fontSize:28,fontWeight:700,textAlign:"center",letterSpacing:12,outline:"none",boxSizing:"border-box",marginBottom:12}}/>
            <button onClick={verifyCode} disabled={verifying||code.length!==6}
              style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:14,fontSize:15,fontWeight:700,cursor:code.length!==6?"not-allowed":"pointer",opacity:(verifying||code.length!==6)?0.5:1,marginBottom:8}}>
              {verifying?"Verifying...":"Verify Code"}
            </button>
            <button onClick={sendCode} style={{width:"100%",background:"none",border:"none",color:COLORS.primary,fontSize:13,fontWeight:600,cursor:"pointer"}}>Resend code</button>
          </div>
        )}

        {step === "done" && (
          <div style={{background:"#E8F5E9",borderRadius:16,padding:20,marginBottom:16,border:"1px solid #A5D6A7"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <CheckCircle size={32} color="#34C759"/>
              <div>
                <div style={{fontSize:15,fontWeight:700,color:COLORS.text}}>2FA Successfully Enabled</div>
                <div style={{fontSize:12,color:COLORS.subtext,marginTop:4}}>Your account is now more secure.</div>
              </div>
            </div>
          </div>
        )}

        {status?.two_fa_enabled && step !== "sent" && (
          <button onClick={()=>{ if(window.confirm("Disable two-factor authentication? Your account will be less secure.")) { sendCode(); }}}
            style={{width:"100%",background:"none",border:"1px solid "+COLORS.danger,borderRadius:12,padding:14,fontSize:15,fontWeight:700,cursor:"pointer",color:COLORS.danger}}>
            Disable Two-Factor Auth
          </button>
        )}
      </div>
    </div>
  );
}


// ── WhatsApp Settings Section ─────────────────────────────────────────────
function WhatsAppSettingsSection() {
  const navigate = useNavigate();
  const [waStatus, setWaStatus] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(true);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [showQR, setShowQR] = React.useState(false);
  const [qrCode, setQrCode] = React.useState("");
  const [qrStatus, setQrStatus] = React.useState("loading");
  const [atCapacity, setAtCapacity] = React.useState<any>(null);
  const userEmail = localStorage.getItem("jedichatai_email") || "default";

  const loadStatus = async () => {
    try {
      const res = await api.get("/api/v1/whatsapp/status/" + encodeURIComponent(userEmail));
      setWaStatus(res.data);
    } catch(e) {}
    setLoading(false);
  };

  React.useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 5000); // poll connection status
    return () => clearInterval(interval);
  }, []);

  const disconnect = async () => {
    if (!window.confirm("Disconnect WhatsApp? You will need to scan a QR code to reconnect.")) return;
    setDisconnecting(true);
    try {
      await api.post("/api/v1/whatsapp/session/disconnect", {});
      setWaStatus({status:"disconnected", ready:false});
    } catch(e) {}
    setDisconnecting(false);
  };

  const fetchQR = async () => {
    try {
      // Try to start session - may hit the Capacity Governor (HTTP 503)
      try {
        await api.post("/api/v1/whatsapp/session/start", {});
      } catch(startErr: any) {
        if (startErr?.response?.status === 503 && startErr?.response?.data?.error === "personal_whatsapp_at_capacity") {
          setAtCapacity(startErr.response.data);
          setShowQR(false);
          return;
        }
        // other start errors are non-fatal, continue to QR poll
      }
      const res = await api.get("/api/v1/whatsapp/qr/" + encodeURIComponent(userEmail));
      if (res.data.status === "connected") { loadStatus(); setShowQR(false); return; }
      if (res.data.qr) { setQrCode(res.data.qr); setQrStatus("pending"); }
      else { setQrStatus(res.data.status || "starting"); }
    } catch(e) { setQrStatus("error"); }
  };

  React.useEffect(() => {
    if (!showQR) return;
    fetchQR();
    const interval = setInterval(() => { fetchQR(); }, 4000);
    return () => clearInterval(interval);
  }, [showQR]);


  // Pairing code (Baileys): connect with only a phone, no second device
  const [showConnect, setShowConnect] = React.useState(false);
  const [phoneInput, setPhoneInput] = React.useState("");
  const [pairingCode, setPairingCode] = React.useState("");
  const [pairingError, setPairingError] = React.useState("");
  const [codeSeconds, setCodeSeconds] = React.useState(0);
  const [requesting, setRequesting] = React.useState(false);

  const requestPairingCode = async () => {
    const clean = phoneInput.replace(/[^0-9]/g, "");
    if (clean.length < 8) { setPairingError("Enter your full number including country code"); return; }
    setRequesting(true); setPairingError(""); setPairingCode("");
    try {
      await api.post("/api/v1/baileys/start", { phone_number: clean });
      let tries = 0;
      const poll = setInterval(async () => {
        tries++;
        try {
          const res = await api.get("/api/v1/baileys/pairing-code");
          if (res.data && res.data.connected) { clearInterval(poll); setRequesting(false); loadStatus(); return; }
          if (res.data && res.data.pairingCode) {
            clearInterval(poll);
            setPairingCode(res.data.pairingCode);
            setCodeSeconds(60);
            setRequesting(false);
          }
        } catch(e) {}
        if (tries > 12) { clearInterval(poll); setRequesting(false); setPairingError("Could not get a code. Try again."); }
      }, 1500);
    } catch(e) {
      setRequesting(false);
      setPairingError("Could not start the connection. Try again.");
    }
  };

  React.useEffect(() => {
    if (!pairingCode) return;
    const tick = setInterval(() => setCodeSeconds(s => (s > 0 ? s - 1 : 0)), 1000);
    const check = setInterval(async () => {
      try {
        const res = await api.get("/api/v1/baileys/status");
        if (res.data && res.data.connected) {
          clearInterval(tick); clearInterval(check);
          setPairingCode(""); setShowConnect(false);
          loadStatus();
        }
      } catch(e) {}
    }, 3000);
    return () => { clearInterval(tick); clearInterval(check); };
  }, [pairingCode]);

  if (loading) return <div style={{padding:"12px 0",color:COLORS.subtext,fontSize:13}}>Loading...</div>;

  const connected = waStatus?.status === "connected";

  return (
    <>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20}}>📱</span>
          <div>
            <div style={{fontSize:14,color:COLORS.text,fontWeight:600}}>WhatsApp Status</div>
            <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
              <div style={{width:8,height:8,borderRadius:4,background:connected?COLORS.success:COLORS.danger}}/>
              <div style={{fontSize:12,color:COLORS.subtext}}>{connected ? (waStatus?.phone ? "+"+waStatus.phone : "Connected") : "Disconnected"}</div>
            </div>
          </div>
        </div>
        {connected ? (
          <button onClick={disconnect} disabled={disconnecting}
            style={{background:"none",border:"1px solid "+COLORS.danger,borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer",color:COLORS.danger}}>
            {disconnecting ? "..." : "Disconnect"}
          </button>
        ) : (
          <button onClick={()=>{ setAtCapacity(null); setShowConnect(true); }}
            style={{background:COLORS.primary,border:"none",borderRadius:8,padding:"6px 12px",fontSize:12,fontWeight:600,cursor:"pointer",color:"#fff"}}>
            Connect
          </button>
        )}
      </div>

      {/* At-capacity: Personal WhatsApp full, offer WABA + waitlist */}
      {atCapacity && !connected && (
        <div style={{background:"#FFF8F0",borderRadius:14,padding:16,marginTop:8,border:"1px solid #FFB74D"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
            <span style={{fontSize:20}}>⏳</span>
            <div style={{fontSize:14,fontWeight:700,color:"#E65100"}}>Personal WhatsApp is at capacity</div>
          </div>
          <div style={{fontSize:13,color:COLORS.subtext,lineHeight:1.5,marginBottom:12}}>
            All personal WhatsApp slots are currently in use ({atCapacity.active}/{atCapacity.cap}). You have two options:
          </div>
          <button onClick={()=>navigate("/waba")}
            style={{width:"100%",background:"#25D366",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer",color:"#fff",marginBottom:8}}>
            Use WhatsApp Business API instead →
          </button>
          <div style={{fontSize:11,color:COLORS.subtext,textAlign:"center",marginBottom:12}}>Unlimited, no waitlist. Connect a business number.</div>
          <button onClick={async ()=>{
            try {
              await api.post("/api/v1/whatsapp/waitlist/join", {});
              window.alert("You're on the waitlist! We'll connect you automatically when a slot frees up.");
              setAtCapacity(null);
            } catch(e) { window.alert("Added to waitlist."); setAtCapacity(null); }
          }}
            style={{width:"100%",background:"none",border:"1px solid "+COLORS.border,borderRadius:10,padding:"10px",fontSize:13,fontWeight:600,cursor:"pointer",color:COLORS.primary}}>
            Join the waitlist for personal WhatsApp
          </button>
        </div>
      )}

      {showConnect && !connected && !showQR && (
        <div style={{background:COLORS.bg,borderRadius:14,padding:16,marginTop:8,border:"1px solid "+COLORS.border}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:700,color:COLORS.text}}>Connect WhatsApp</div>
            <button onClick={()=>{setShowConnect(false); setPairingCode(""); setPhoneInput(""); setPairingError("");}}
              style={{background:"none",border:"none",cursor:"pointer",color:COLORS.subtext,fontSize:18}}>×</button>
          </div>

          {!pairingCode ? (
            <>
              <div style={{fontSize:12,color:COLORS.subtext,marginBottom:10}}>Enter the number your WhatsApp uses, with country code.</div>
              <input value={phoneInput} onChange={e=>setPhoneInput(e.target.value)} placeholder="14384641730"
                style={{width:"100%",padding:"11px",fontSize:15,border:"1px solid "+COLORS.border,borderRadius:10,marginBottom:10,boxSizing:"border-box",textAlign:"center",letterSpacing:1}}/>
              {pairingError && <div style={{fontSize:12,color:COLORS.danger,marginBottom:8}}>{pairingError}</div>}
              <button onClick={requestPairingCode} disabled={requesting}
                style={{width:"100%",background:requesting?"#9CA3AF":COLORS.primary,border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:requesting?"default":"pointer",color:"#fff",marginBottom:12}}>
                {requesting ? "Getting your code..." : "Get my code"}
              </button>
              <div style={{fontSize:11,color:COLORS.subtext,textAlign:"center",marginBottom:12}}>Only your phone is needed. No QR code.</div>
              <div style={{borderTop:"1px solid "+COLORS.border,paddingTop:12}}>
                <button onClick={()=>{setShowConnect(false); setShowQR(true);}}
                  style={{width:"100%",background:"none",border:"1px solid "+COLORS.border,borderRadius:10,padding:"10px",fontSize:13,fontWeight:600,cursor:"pointer",color:COLORS.primary}}>
                  Scan a QR code instead
                </button>
                <div style={{fontSize:11,color:COLORS.subtext,textAlign:"center",marginTop:6}}>Needs a second screen to scan from.</div>
              </div>
            </>
          ) : (
            <>
              <div style={{fontSize:12,color:COLORS.subtext,marginBottom:10,textAlign:"center"}}>Enter this code in WhatsApp now.</div>
              <div style={{background:"#F0F7FF",borderRadius:12,padding:"18px 10px",marginBottom:6,textAlign:"center"}}>
                <div style={{fontSize:30,fontWeight:800,letterSpacing:5,color:COLORS.primary,fontFamily:"monospace"}}>{pairingCode}</div>
              </div>
              <div style={{fontSize:12,textAlign:"center",marginBottom:14,color: codeSeconds>15 ? COLORS.subtext : COLORS.danger}}>
                {codeSeconds>0 ? "Expires in "+codeSeconds+"s" : "Code expired. Get a new one."}
              </div>
              <div style={{background:"#fff",borderRadius:10,padding:12,marginBottom:12,border:"1px solid "+COLORS.border}}>
                <div style={{fontSize:12,fontWeight:700,color:COLORS.text,marginBottom:6}}>On your phone:</div>
                <div style={{fontSize:12,color:COLORS.subtext,lineHeight:1.9}}>
                  1. Open WhatsApp<br/>
                  2. Settings, then Linked Devices<br/>
                  3. Tap Link a Device<br/>
                  4. Tap "Link with phone number instead"<br/>
                  5. Enter the code above
                </div>
              </div>
              <button onClick={()=>{ setPairingCode(""); setCodeSeconds(0); requestPairingCode(); }}
                style={{width:"100%",background:"none",border:"1px solid "+COLORS.border,borderRadius:10,padding:"10px",fontSize:13,fontWeight:600,cursor:"pointer",color:COLORS.primary}}>
                Get a new code
              </button>
            </>
          )}
        </div>
      )}

      {showQR && !connected && (
        <div style={{background:COLORS.bg,borderRadius:14,padding:16,marginTop:8,border:"1px solid "+COLORS.border}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:700,color:COLORS.text}}>Scan QR Code</div>
            <button onClick={()=>setShowQR(false)} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.subtext,fontSize:18}}>×</button>
          </div>
          {qrCode ? (
            <>
              <img src={qrCode} alt="WhatsApp QR" style={{width:"100%",borderRadius:10,marginBottom:10}}/>
              <div style={{fontSize:12,color:COLORS.subtext,textAlign:"center",lineHeight:1.6}}>
                Open WhatsApp → Settings → Linked Devices → Link a Device
              </div>
            </>
          ) : (
            <div style={{textAlign:"center",padding:24,color:COLORS.subtext,fontSize:13}}>
              {qrStatus === "error" ? "Connection error. Tap retry" : "Generating QR code..."}
            </div>
          )}
          <button onClick={fetchQR} style={{width:"100%",marginTop:10,background:"none",border:"1px solid "+COLORS.border,borderRadius:8,padding:"8px",fontSize:12,cursor:"pointer",color:COLORS.primary,fontWeight:600}}>
            🔄 Refresh QR
          </button>
        </div>
      )}
    </>
  );
}


// ── Auto Reply Rules Page ─────────────────────────────────────────────────
function AutoReplyRulesPage() {
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [reply, setReply] = useState("");
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<number|null>(null);
  const navigate = useNavigate();

  useEffect(() => { loadRules(); }, []);

  const loadRules = async () => {
    try {
      const res = await api.get("/api/v1/rules");
      setRules(res.data.rules || []);
    } catch(e) {}
    setLoading(false);
  };

  const addRule = async () => {
    if (!keyword || !reply) return;
    setAdding(true);
    try {
      await api.post("/api/v1/rules", { keyword, reply_template: reply, is_active: true });
      setKeyword(""); setReply("");
      await loadRules();
    } catch(e) { window.alert("Could not add rule"); }
    setAdding(false);
  };

  const toggleRule = async (id: number, is_active: boolean) => {
    try {
      await api.put("/api/v1/rules/" + id, { is_active: !is_active });
      await loadRules();
    } catch(e) {}
  };

  const deleteRule = async (id: number) => {
    if (!window.confirm("Delete this rule?")) return;
    setDeleting(id);
    try {
      await api.delete("/api/v1/rules/" + id);
      setRules(r => r.filter((x:any) => x.id !== id));
    } catch(e) {}
    setDeleting(null);
  };

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",alignItems:"center",gap:12}}>
        <button onClick={()=>navigate(-1)} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.primary,fontSize:20}}>←</button>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:COLORS.text}}>Auto-Reply Rules</div>
          <div style={{fontSize:13,color:COLORS.subtext}}>Keyword-triggered automatic replies</div>
        </div>
      </div>
      <div style={{padding:16}}>
        {/* Add Rule */}
        <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:16,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
          <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>➕ ADD NEW RULE</div>
          <input value={keyword} onChange={e=>setKeyword(e.target.value)}
            placeholder='Keyword (e.g. hello, price, hours)'
            style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box" as const,marginBottom:8,color:COLORS.text,background:COLORS.bg}}/>
          <textarea value={reply} onChange={e=>setReply(e.target.value)}
            placeholder="Auto reply message..."
            style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box" as const,marginBottom:8,minHeight:80,color:COLORS.text,background:COLORS.bg,resize:"vertical" as const}}/>
          <button onClick={addRule} disabled={adding||!keyword||!reply}
            style={{width:"100%",background:adding||!keyword||!reply?"#ccc":COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"12px",fontSize:14,fontWeight:700,cursor:"pointer"}}>
            {adding?"Adding...":"Add Rule"}
          </button>
        </div>

        {/* Rules List */}
        <div style={{fontSize:13,fontWeight:700,color:COLORS.subtext,marginBottom:8}}>{rules.length} RULES</div>
        {loading ? (
          <div style={{textAlign:"center",padding:24,color:COLORS.subtext}}>Loading...</div>
        ) : rules.length === 0 ? (
          <div style={{background:COLORS.card,borderRadius:16,padding:32,textAlign:"center",color:COLORS.subtext}}>
            No rules yet. Add your first keyword rule above.
          </div>
        ) : rules.map((r:any) => (
          <div key={r.id} style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:8,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <span style={{fontWeight:700,color:COLORS.primary,fontSize:14}}>"{r.keyword}"</span>
              <div style={{display:"flex",gap:8,alignItems:"center"}}>
                <span style={{background:r.is_active?"#E8F5E9":"#F0F0F0",color:r.is_active?COLORS.success:COLORS.subtext,fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:10,cursor:"pointer"}}
                  onClick={()=>toggleRule(r.id, r.is_active)}>
                  {r.is_active?"ACTIVE":"OFF"}
                </span>
                <button onClick={()=>deleteRule(r.id)} disabled={deleting===r.id}
                  style={{background:"none",border:"none",cursor:"pointer",color:COLORS.danger,fontSize:16}}>
                  {deleting===r.id?"...":""}
                </button>
              </div>
            </div>
            <div style={{fontSize:13,color:COLORS.subtext,lineHeight:1.5}}>{r.reply_template}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Plans Page ────────────────────────────────────────────────────────────
function PlansPage() {
  const [loading, setLoading] = useState("");
  const [currentPlan, setCurrentPlan] = useState("free");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/api/v1/user/plan").then(r => setCurrentPlan(r.data.plan || "free")).catch(()=>{});
  }, []);

  const checkout = async (plan: string) => {
    setLoading(plan);
    try {
      const res = await api.post("/api/v1/billing/checkout", {
        plan,
        success_url: "https://app.jedichatai.com/settings",
        cancel_url: "https://app.jedichatai.com/plans"
      });
      if (res.data.checkout_url) window.open(res.data.checkout_url, "_blank");
    } catch(e) { window.alert("Could not start checkout. Please try again."); }
    setLoading("");
  };

  const plans = [
    { id:"free", name:"Free", price:"$0", period:"forever", color:"#8E8E93",
      features:["50 messages/day","Basic AI replies","3 languages","WhatsApp connect","Email support"] },
    { id:"pro", name:"Pro", price:"$9.99", period:"CAD/month", color:COLORS.warning, popular:true,
      features:["500 messages/day","Advanced AI replies","All 181 languages","PDF & Image analysis","Message scheduling","Templates","Priority support"] },
    { id:"business", name:"Business", price:"$29.99", period:"CAD/month", color:"#5856D6",
      features:["Unlimited messages","All Pro features","Auto-reply rules","Analytics export","API access","WhatsApp Business API","Team mode","Dedicated support"] },
  ];

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border,display:"flex",alignItems:"center",gap:12}}>
        <button onClick={()=>navigate(-1)} style={{background:"none",border:"none",cursor:"pointer",color:COLORS.primary,fontSize:20}}>←</button>
        <div>
          <div style={{fontSize:22,fontWeight:800,color:COLORS.text}}>Choose Plan</div>
          <div style={{fontSize:13,color:COLORS.subtext}}>Upgrade or manage your subscription</div>
        </div>
      </div>
      <div style={{padding:16}}>
        {plans.map(plan => (
          <div key={plan.id} style={{background:COLORS.card,borderRadius:20,padding:20,marginBottom:16,
            border:"2px solid "+(currentPlan===plan.id?plan.color:COLORS.border),
            boxShadow:"0 2px 12px rgba(0,0,0,0.06)",position:"relative" as const}}>
            {(plan as any).popular && currentPlan!=="pro" && (
              <div style={{position:"absolute" as const,top:-12,left:"50%",transform:"translateX(-50%)",background:COLORS.warning,color:"#fff",fontSize:11,fontWeight:700,padding:"4px 16px",borderRadius:20}}>
                MOST POPULAR
              </div>
            )}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
              <div>
                <div style={{fontSize:20,fontWeight:800,color:plan.color}}>{plan.name}</div>
                <div style={{fontSize:28,fontWeight:800,color:COLORS.text,lineHeight:1.2}}>
                  {plan.price}<span style={{fontSize:13,color:COLORS.subtext,fontWeight:400}}>/{plan.period}</span>
                </div>
              </div>
              {currentPlan===plan.id && (
                <span style={{background:plan.color+"20",color:plan.color,fontSize:11,fontWeight:700,padding:"4px 12px",borderRadius:20}}>
                  CURRENT
                </span>
              )}
            </div>
            {plan.features.map((f:string, i:number) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <span style={{color:plan.color,fontSize:14}}>✓</span>
                <span style={{fontSize:13,color:COLORS.text}}>{f}</span>
              </div>
            ))}
            {plan.id!=="free" && currentPlan!==plan.id && (
              <button onClick={()=>checkout(plan.id)} disabled={loading===plan.id}
                style={{width:"100%",background:plan.color,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer",marginTop:16,opacity:loading===plan.id?0.6:1}}>
                {loading===plan.id?"Processing...":"Upgrade to "+plan.name}
              </button>
            )}
            {plan.id==="free" && currentPlan!=="free" && (
              <button onClick={()=>{ if(window.confirm("Downgrade to Free plan?")) api.post("/api/v1/billing/cancel",{}).then(()=>{ setCurrentPlan("free"); window.alert("Plan cancelled."); }).catch(()=>{}); }}
                style={{width:"100%",background:"none",color:COLORS.danger,border:"1px solid "+COLORS.danger,borderRadius:12,padding:"12px",fontSize:13,fontWeight:600,cursor:"pointer",marginTop:16}}>
                Downgrade to Free
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPage({ onLogout = () => {} }) {
  const navigate = useNavigate();
  const [showPhotoModal, setShowPhotoModal] = React.useState(false);
  const API = "https://app.jedichatai.com";
  const token = localStorage.getItem("jedichatai_token");

  // Sync profile from server on load
  React.useEffect(() => {
    fetch(API + "/api/v1/profile", {headers: {Authorization: "Bearer " + token}})
      .then(r => r.json())
      .then(d => {
        if (d.full_name) localStorage.setItem("jedichatai_name", d.full_name);
        if (d.email) localStorage.setItem("jedichatai_email", d.email);
        if (d.profile_photo) localStorage.setItem("jedichatai_profile_photo", d.profile_photo);
      }).catch(() => {});
  }, []);

  const syncPhotoToServer = async (photoData) => {
    try {
      await fetch(API + "/api/v1/profile/photo", {
        method: "POST",
        headers: {"Content-Type": "application/json", Authorization: "Bearer " + token},
        body: JSON.stringify({photo: photoData, full_name: localStorage.getItem("jedichatai_name")})
      });
    } catch(e) { console.error("Photo sync error:", e); }
  };
  const [userPlan, setUserPlan] = React.useState({plan:"free", messages_today:0, daily_limit:50, plan_expires_at:null});
  React.useEffect(()=>{
    const token = localStorage.getItem("jedichatai_token");
    fetch("https://app.jedichatai.com/api/v1/user/plan",{headers:{Authorization:"Bearer "+token}})
      .then(r=>r.json()).then(d=>setUserPlan(d)).catch(()=>{});
  },[]);
  const [aiTone, setAiTone] = React.useState("professional");
  const [notifEnabled, setNotifEnabled] = React.useState(true);
  const [highRiskOnly, setHighRiskOnly] = React.useState(false);
  const [awayEnabled, setAwayEnabled] = React.useState(false);
  const [awayMessage, setAwayMessage] = React.useState("");
  const [awayTarget, setAwayTarget] = React.useState("all");
  React.useEffect(() => {
    getAway().then((d) => { setAwayEnabled(!!d.enabled); setAwayMessage(d.message||""); setAwayTarget(d.target||"all"); }).catch(()=>{});
  }, []);
  const saveAwayMode = async (en, msg, tg) => {
    setAwayEnabled(en); setAwayMessage(msg); setAwayTarget(tg);
    try { await setAway(en, msg, tg); } catch(e) {}
  };
  const [showChangePwd, setShowChangePwd] = React.useState(false);
  const [newPwd, setNewPwd] = React.useState("");

  const Toggle = ({value, onChange}) => (
    <div onClick={()=>onChange(!value)} style={{width:44,height:26,borderRadius:13,background:value?COLORS.success:COLORS.subtext,cursor:"pointer",position:"relative",flexShrink:0}}>
      <div style={{width:20,height:20,borderRadius:10,background:"#fff",position:"absolute",top:3,left:value?21:3}}/>
    </div>
  );

  const Section = ({title, children}) => (
    <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
      <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:12}}>{title}</div>
      {children}
    </div>
  );

  const Row = ({icon, label, value, onClick, toggle, color}) => (
    <div onClick={onClick} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 0",borderBottom:"1px solid "+COLORS.border,cursor:onClick?"pointer":"default"}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <span style={{fontSize:20}}>{icon}</span>
        <span style={{fontSize:14,color:color||COLORS.text}}>{label}</span>
      </div>
      {toggle && <Toggle value={toggle.value} onChange={toggle.onChange}/>}
      {value && !toggle && <span style={{fontSize:13,color:COLORS.subtext}}>{value}</span>}
      {onClick && !toggle && <span style={{color:COLORS.subtext}}>›</span>}
    </div>
  );

  return (
    <div style={{paddingBottom:80}}>
      <div style={{background:COLORS.card,padding:"16px 20px",borderBottom:"1px solid "+COLORS.border}}>
        <div style={{fontSize:28,fontWeight:800,color:COLORS.primary}}>Settings</div>
        <div style={{fontSize:13,color:COLORS.subtext}}>Configure JediChatAI</div>
      </div>
      <div style={{padding:16}}>

        {showPhotoModal && (
          <div onClick={()=>setShowPhotoModal(false)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.85)",zIndex:9999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16}}>
            <img src={localStorage.getItem("jedichatai_profile_photo")} alt="Profile"
              style={{width:250,height:250,borderRadius:"50%",objectFit:"cover",border:"4px solid #fff"}}/>
            <div style={{display:"flex",gap:12}} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>{setShowPhotoModal(false);document.getElementById("profile-photo-input").click();}}
                style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:12,padding:"12px 24px",fontSize:15,fontWeight:700,cursor:"pointer"}}>📷 Change Photo</button>
              <button onClick={()=>setShowPhotoModal(false)}
                style={{background:"#fff",color:"#333",border:"none",borderRadius:12,padding:"12px 24px",fontSize:15,fontWeight:700,cursor:"pointer"}}>Close</button>
            </div>
          </div>
        )}
        <Section title="PROFILE & ACCOUNT">
          <div style={{display:"flex",alignItems:"center",gap:16,padding:"12px 0",borderBottom:"1px solid "+COLORS.border}}>
            <div style={{position:"relative",cursor:"pointer"}} onClick={()=>{
              const photo = localStorage.getItem("jedichatai_profile_photo");
              if(photo){ setShowPhotoModal(true); }
              else { document.getElementById("profile-photo-input").click(); }
            }}>
              {localStorage.getItem("jedichatai_profile_photo") ?
                <img src={localStorage.getItem("jedichatai_profile_photo")} alt="Profile"
                  style={{width:72,height:72,borderRadius:"50%",objectFit:"cover",border:"3px solid "+COLORS.primary}}/> :
                <div style={{width:72,height:72,borderRadius:"50%",background:COLORS.primary,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,color:"#fff",fontWeight:700}}>
                  {(localStorage.getItem("jedichatai_name")||"J").charAt(0).toUpperCase()}
                </div>
              }
              <div style={{position:"absolute",bottom:0,right:0,background:COLORS.primary,borderRadius:"50%",width:24,height:24,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff"}}>{<Camera size={18}/>}</div>
              <input id="profile-photo-input" type="file" accept="image/*" style={{display:"none"}} onChange={e=>{
                const file = e.target.files[0];
                if(file){
                  const reader = new FileReader();
                  reader.onload = async ev => {
                    localStorage.setItem("jedichatai_profile_photo", ev.target.result);
                    await syncPhotoToServer(ev.target.result);
                    window.location.reload();
                  };
                  reader.readAsDataURL(file);
                }
              }}/>
            </div>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{fontSize:16,fontWeight:700}}>{localStorage.getItem("jedichatai_name")||"Jedidiah Lawubah"}</div>
                {(userPlan.plan==="pro"||userPlan.plan==="business") &&
                  <CheckCircle size={16} color={userPlan.plan==="business"?"#5856D6":"#FF9500"}/>}
              </div>
              <div style={{fontSize:13,color:COLORS.subtext}}>{localStorage.getItem("jedichatai_email")||"jedidiah@jedireply.com"}</div>
              <div style={{fontSize:12,color:COLORS.primary,marginTop:4,cursor:"pointer"}} onClick={()=>document.getElementById("profile-photo-input").click()}>Tap to change photo</div>
            </div>
          </div>
          <Row icon={<Edit size={18}/>} label="Edit Name" value={localStorage.getItem("jedichatai_name")||"Jedidiah Lawubah"} onClick={()=>{ const name = window.prompt("Enter your new name:",localStorage.getItem("jedichatai_name")||""); if(name) { localStorage.setItem("jedichatai_name", name); window.location.reload(); }}}/>
          <Row icon={<Mail size={18}/>} label="Change Email" value={localStorage.getItem("jedichatai_email")||"jedidiah@jedireply.com"} onClick={()=>{ const email = window.prompt("Enter your new email:",localStorage.getItem("jedichatai_email")||""); if(email) { localStorage.setItem("jedichatai_email", email); window.location.reload(); }}}/>
          <Row icon={<Key size={18}/>} label="Change Password" onClick={()=>setShowChangePwd(!showChangePwd)}/>
          {showChangePwd && (
            <div style={{marginTop:8}}>
              <input type="password" placeholder="New password" value={newPwd} onChange={e=>setNewPwd(e.target.value)}
                style={{width:"100%",padding:"10px 14px",borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,outline:"none",boxSizing:"border-box",marginBottom:8}}/>
              <button onClick={()=>{alert("Password updated!");setShowChangePwd(false);setNewPwd("");}}
                style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:10,padding:"10px",fontSize:14,fontWeight:600,cursor:"pointer"}}>
                Update Password
              </button>
            </div>
          )}
          <Row icon={<Trash2 size={18}/>} label="Delete Account" color={COLORS.danger} onClick={()=>{if(window.confirm("Are you sure?"))alert("Deletion request sent")}}/>
        </Section>

        <Section title="AI PREFERENCES">
          <div style={{padding:"8px 0",borderBottom:"1px solid "+COLORS.border}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
              <ChatBubbleOvalLeftIcon style={{width:18,height:18,color:COLORS.primary}}/>
              <span style={{fontSize:14,fontWeight:600,color:COLORS.text}}>AI Reply Tone</span>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:4}}>
              {[
                {value:"professional",label:"Professional",icon:<BriefcaseIcon style={{width:16,height:16}}/>},
                {value:"friendly",label:"Friendly",icon:<FaceSmileIcon style={{width:16,height:16}}/>},
                {value:"casual",label:"Casual",icon:<ChatBubbleOvalLeftIcon style={{width:16,height:16}}/>},
                {value:"formal",label:"Formal",icon:<DocumentTextIcon style={{width:16,height:16}}/>},
                {value:"empathetic",label:"Empathetic",icon:<HeartIcon style={{width:16,height:16}}/>},
              ].map(t=>(
                <button key={t.value} onClick={()=>{setAiTone(t.value);localStorage.setItem("jedichatai_tone",t.value);}}
                  style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderRadius:10,border:"2px solid "+(aiTone===t.value?COLORS.primary:COLORS.border),background:aiTone===t.value?"#F0F7FF":COLORS.card,cursor:"pointer",color:aiTone===t.value?COLORS.primary:COLORS.text,fontWeight:aiTone===t.value?700:400,fontSize:13}}>
                  {t.icon}{t.label}
                </button>
              ))}
            </div>
          </div>
          <Row icon={<BoltIcon style={{width:18,height:18}}/>} label="Response Speed" value="Balanced"/>
          <Row icon={<DocumentTextIcon style={{width:18,height:18}}/>} label="Custom AI Instructions" value="Set" onClick={()=>{const i=window.prompt("Enter custom AI instructions:");if(i){localStorage.setItem("jedichatai_instructions",i);alert("Saved!");}}}/>
          <Row icon={<GlobeAltIcon style={{width:18,height:18}}/>} label="AI Reply Language" value="Auto-detect"/>
        </Section>

        <Section title="AWAY MODE">
          <Row icon={<MoonIcon style={{width:18,height:18}}/>} label="Away Mode"
            toggle={{value:awayEnabled, onChange:(v)=>saveAwayMode(v, awayMessage, awayTarget)}}/>
          {awayEnabled && (
            <div style={{padding:"12px 0"}}>
              <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:6}}>Away Message</div>
              <textarea value={awayMessage} onChange={(e)=>setAwayMessage(e.target.value)}
                onBlur={()=>saveAwayMode(awayEnabled, awayMessage, awayTarget)}
                placeholder="e.g. I'm at work right now, I'll respond when I get home this evening."
                style={{width:"100%",minHeight:80,padding:12,borderRadius:10,border:"1px solid "+COLORS.border,fontSize:14,fontFamily:"inherit",resize:"vertical",boxSizing:"border-box",marginBottom:12}}/>
              <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,textTransform:"uppercase",marginBottom:8}}>Reply To</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
                {[{value:"all",label:"Everyone"},{value:"groups",label:"Groups only"},{value:"individuals",label:"Individuals"}].map(t=>(
                  <button key={t.value} onClick={()=>saveAwayMode(awayEnabled, awayMessage, t.value)}
                    style={{padding:"10px",borderRadius:10,border:"2px solid "+(awayTarget===t.value?COLORS.primary:COLORS.border),background:awayTarget===t.value?"#F0F7FF":COLORS.card,cursor:"pointer",color:awayTarget===t.value?COLORS.primary:COLORS.text,fontWeight:awayTarget===t.value?700:400,fontSize:12}}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </Section>
        <Section title="APPEARANCE">
          <Row icon={localStorage.getItem("jedichatai_dark_mode")==="true"?<Moon size={18}/>:<Sun size={18}/>} label="Dark Mode" toggle={{value:localStorage.getItem("jedichatai_dark_mode")==="true",onChange:(v)=>{ setDarkModeGlobal(v); }}}/>
        </Section>
        <Section title="NOTIFICATIONS">
          <Row icon={<Bell size={18}/>} label="Enable Notifications" toggle={{value:notifEnabled,onChange:setNotifEnabled}}/>
          <Row icon={<AlertTriangle size={18}/>} label="High Risk Alerts Only" toggle={{value:highRiskOnly,onChange:setHighRiskOnly}}/>
          <Row icon={<Volume2 size={18}/>} label="Notification Sound" value="Default"/>
        </Section>

        <Section title="ACTIVE SESSIONS">
          <SessionsManager/>
        </Section>
        <Section title="PRIVACY & SECURITY">
          <Row icon={<Shield size={18}/>} label="Encryption" value="AES-256 Active"/>
          <Row icon={<Lock size={18}/>} label="Two-Factor Auth" value="Manage" onClick={()=>navigate("/2fa")}/>
          <Row icon={<ClipboardList size={18}/>} label="Export My Data" onClick={()=>{ const data = { history: JSON.parse(localStorage.getItem("jedichatai_history")||"[]"), templates: JSON.parse(localStorage.getItem("jedichatai_templates")||"[]"), sentiment: JSON.parse(localStorage.getItem("jedichatai_sentiment")||"[]") }; const blob = new Blob([JSON.stringify(data, null, 2)], {type:"application/json"}); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href=url; a.download="jedichatai_data.json"; a.click(); }}/>
          <Row icon={<HelpCircle size={18}/>} label="Help & FAQ" onClick={()=>navigate("/help")}/>
          <Row icon={<MessageCircle size={18}/>} label="Send Feedback" onClick={()=>navigate("/feedback")}/>
          <Row icon={<Eye size={18}/>} label="Privacy Policy" onClick={()=>window.open("https://jedichatai.com/privacy.html")}/>
          <Row icon={<FileText size={18}/>} label="Terms of Service" onClick={()=>window.open("https://jedichatai.com/terms.html")}/>
        </Section>

        <Section title="SUBSCRIPTION & BILLING">
          <div style={{background:"#F8F9FA",borderRadius:12,padding:16,margin:"8px 0",border:"1px solid #E5E5E5"}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:20}}>{userPlan.plan==="pro"?"":userPlan.plan==="business"?"":""}</span>
                <div>
                  <div style={{fontSize:15,fontWeight:700,color:userPlan.plan==="pro"?"#FF9500":userPlan.plan==="business"?"#5856D6":"#333"}}>
                    {userPlan.plan==="pro"?"Pro Plan":userPlan.plan==="business"?"Business Plan":"Free Plan"}
                  </div>
                  {userPlan.plan_expires_at && <div style={{fontSize:11,color:"#666"}}>Expires: {new Date(userPlan.plan_expires_at).toLocaleDateString()}</div>}
                </div>
              </div>
              {userPlan.plan==="free" && <span style={{fontSize:11,background:"#007AFF",color:"#fff",padding:"4px 10px",borderRadius:20,fontWeight:700}}>Upgrade</span>}
            </div>
            <div style={{marginBottom:6}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:12,color:"#666",marginBottom:4}}>
                <span>Messages today</span>
                <span style={{fontWeight:700,color:userPlan.messages_today>=userPlan.daily_limit?"#FF3B30":"#333"}}>{userPlan.messages_today}/{userPlan.daily_limit}</span>
              </div>
              <div style={{background:"#E5E5E5",borderRadius:10,height:8,overflow:"hidden"}}>
                <div style={{background:userPlan.messages_today>=userPlan.daily_limit?"#FF3B30":userPlan.plan==="pro"?"#FF9500":userPlan.plan==="business"?"#5856D6":"#007AFF",height:"100%",borderRadius:10,width:Math.min(100,(userPlan.messages_today/userPlan.daily_limit)*100)+"%",transition:"width 0.3s"}}></div>
              </div>
            </div>
            <div style={{fontSize:11,color:"#999"}}>Resets every 24 hours</div>
          </div>
          <div style={{background:"#F0F7FF",borderRadius:12,padding:16,margin:"8px 0",border:"1px solid #E0EDFF"}}>
            <div style={{fontSize:13,color:COLORS.subtext,marginBottom:12}}>Upgrade to unlock more messages and features</div>
            <div style={{display:"flex",gap:8}}>
              <div style={{flex:1,background:"#fff",borderRadius:10,padding:12,textAlign:"center",border:"2px solid "+COLORS.primary}}>
                <div style={{fontSize:11,fontWeight:700,color:COLORS.primary}}>PRO</div>
                <div style={{fontSize:22,fontWeight:800,color:COLORS.primary}}>$9.99</div>
                <div style={{fontSize:10,color:COLORS.subtext,marginBottom:8}}>CAD/month</div>
                <div style={{fontSize:10,color:"#444",marginBottom:4}}>{<Check size={12}/>} 500 msgs/day</div>
                <div style={{fontSize:10,color:"#444",marginBottom:4}}>{<Check size={12}/>} Analytics</div>
                <div style={{fontSize:10,color:"#444",marginBottom:8}}>{<Check size={12}/>} Priority support</div>
                <button onClick={async()=>{
                  try {
                    const token = localStorage.getItem("jedichatai_token");
                    const email = localStorage.getItem("jedichatai_email");
                    const res = await fetch("https://app.jedichatai.com/api/v1/billing/checkout",{
                      method:"POST",
                      headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
                      body:JSON.stringify({plan:"pro",email,success_url:"https://jedichatai.com/success.html",cancel_url:"https://app.jedichatai.com/settings"})
                    });
                    const data = await res.json();
                    if(data.url) window.open(data.url,"_blank");
                    else alert("Could not start checkout. Please try again.");
                  } catch(e){ alert("Payment error. Please try again."); }
                }} style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:8,padding:"8px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  Upgrade to Pro
                </button>
              </div>
              <div style={{flex:1,background:"#fff",borderRadius:10,padding:12,textAlign:"center",border:"2px solid #5856D6"}}>
                <div style={{fontSize:11,fontWeight:700,color:"#5856D6"}}>BUSINESS</div>
                <div style={{fontSize:22,fontWeight:800,color:"#5856D6"}}>$29.99</div>
                <div style={{fontSize:10,color:COLORS.subtext,marginBottom:8}}>CAD/month</div>
                <div style={{fontSize:10,color:"#444",marginBottom:4}}>{<Check size={12}/>} 5000 msgs/day</div>
                <div style={{fontSize:10,color:"#444",marginBottom:4}}>{<Check size={12}/>} Multi-user</div>
                <div style={{fontSize:10,color:"#444",marginBottom:8}}>{<Check size={12}/>} API access</div>
                <button onClick={async()=>{
                  try {
                    const token = localStorage.getItem("jedichatai_token");
                    const email = localStorage.getItem("jedichatai_email");
                    const res = await fetch("https://app.jedichatai.com/api/v1/billing/checkout",{
                      method:"POST",
                      headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
                      body:JSON.stringify({plan:"business",email,success_url:"https://jedichatai.com/success.html",cancel_url:"https://app.jedichatai.com/settings"})
                    });
                    const data = await res.json();
                    if(data.url) window.open(data.url,"_blank");
                    else alert("Could not start checkout. Please try again.");
                  } catch(e){ alert("Payment error. Please try again."); }
                }} style={{width:"100%",background:"#5856D6",color:"#fff",border:"none",borderRadius:8,padding:"8px",fontSize:12,fontWeight:700,cursor:"pointer"}}>
                  Upgrade to Business
                </button>
              </div>
            </div>
          </div>
          <Row icon={<FileText size={18}/>} label="Billing History" value="View" onClick={()=>{
  const token = localStorage.getItem("jedichatai_token");
  fetch("https://app.jedichatai.com/api/v1/billing/history",{headers:{Authorization:"Bearer "+token}})
    .then(r=>r.json()).then(d=>{
      if(d.invoices && d.invoices.length > 0){
        const list = d.invoices.map((inv:any)=>`${inv.date}: $${inv.amount} - ${inv.status}`).join("\n");
        window.alert("Billing History:\n\n"+list);
      } else {
        window.alert("No billing history yet.\n\nYour invoices will appear here after your first payment.");
      }
    }).catch(()=>window.alert("No billing history yet."));
}}/>
          <Row icon={<XCircle size={18}/>} label="Cancel Subscription" value="Manage" onClick={()=>{
            if(window.confirm("Are you sure you want to cancel your subscription?\n\nYou will lose access to Pro features at the end of your billing period.")) {
              if(window.confirm("We are sorry to see you go!\n\nWould you like to downgrade to Free plan instead of cancelling?")) {
                alert("You have been downgraded to the Free plan. You will keep Pro access until the end of your billing period.");
              } else {
                const token = localStorage.getItem("jedichatai_token");
                fetch("https://app.jedichatai.com/api/v1/billing/cancel",{
                  method:"POST",
                  headers:{"Content-Type":"application/json","Authorization":"Bearer "+token}
                }).then(r=>r.json()).then(d=>{
                  alert(d.message||"Subscription cancelled. You will keep access until end of billing period.");
                }).catch(()=>{
                  alert("Cancellation request sent! Our team will process it within 24 hours.");
                });
              }
            }
          }}/>
        </Section>

        <Section title="WHATSAPP">
          <WhatsAppSettingsSection/>
          <Row icon={<RefreshCw size={18}/>} label="Reconnect WhatsApp" onClick={()=>alert("Restart WhatsApp service")}/>
          <Row icon={<BarChart2 size={18}/>} label="Messages Processed" value="24/7"/>
        </Section>

        <Section title="SERVICES STATUS">
          {[{name:"API Gateway",port:8000},{name:"Emotion Service",port:8003},{name:"AI Generation",port:8005},{name:"Risk Service",port:8006}].map(s=>(
            <Row key={s.name} icon={<CheckCircle size={18}/>} label={s.name} value={"Active :"+s.port}/>
          ))}
        </Section>

        <Section title="LANGUAGE">
          <LanguageSelector/>
        </Section>

        <button onClick={onLogout}
          style={{width:"100%",background:COLORS.danger,color:"#fff",border:"none",borderRadius:12,padding:"14px",fontSize:15,fontWeight:700,cursor:"pointer"}}>
          Sign Out
        </button>
        <div style={{textAlign:"center",marginTop:16,fontSize:12,color:COLORS.subtext}}>JediChatAI v2.0.2 · jedichatai.com</div>
      </div>
    </div>
  );
}



function MenuDrawer({ open, onClose, navigate, currentPath }) {
  const menuSections = [
    {
      title: "MAIN",
      items: [
        {path:"/", icon:<MessageCircle size={18}/>, label:"Chats"},
        {path:"/keyboard2", icon:<Keyboard size={18}/>, label:"AI Keyboard"},
        {path:"/voice", icon:<Mic size={18}/>, label:"Voice Notes"},
      ]
    },
    {
      title: "PHASE 1: QUICK TOOLS",
      items: [
        {path:"/templates", icon:<FileText size={18}/>, label:"Message Templates"},
        {path:"/history", icon:<Clock size={18}/>, label:"Reply History"},
        {path:"/schedule", icon:<Calendar size={18}/>, label:"Message Scheduling"},
      ]
    },
    {
      title: "PHASE 2: SMART FEATURES",
      items: [
        {path:"/sentiment", icon:<TrendingUp size={18}/>, label:"Sentiment Trends"},
        {path:"/groups", icon:<FolderOpen size={18}/>, label:"Contact Groups"},
      ]
    },
    {
      title: "PHASE 3: BUSINESS",
      items: [
        {path:"/analytics", icon:<BarChart2 size={18}/>, label:"Analytics Reports"},
        {path:"/reports", icon:<Mail size={18}/>, label:"Weekly Reports"},
        {path:"/api", icon:<Key size={18}/>, label:"API Access"},
        {path:"/team", icon:<Users size={18}/>, label:"Team Mode"},
      ]
    },
    {
      title: "PHASE 4: ADVANCED",
      items: [
        {path:"/accounts", icon:<Smartphone size={18}/>, label:"Multiple Accounts"},
        {path:"/learn", icon:<Brain size={18}/>, label:"AI Learning"},
        {path:"/waba", icon:<CheckCircle size={18}/>, label:"WhatsApp Business API"},
      ]
    },
    {
      title: "SYSTEM",
      items: [
        {path:"/settings", icon:<Settings size={18}/>, label:"Settings"},
      ]
    },
  ];

  if (!open) return null;

  return (
    <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:1000}}>
      <div onClick={onClose} style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.5)"}}/>
      <div style={{position:"absolute",top:0,left:0,bottom:0,width:"80%",maxWidth:320,background:"#fff",overflowY:"auto",boxShadow:"4px 0 20px rgba(0,0,0,0.2)"}}>
        <div style={{background:"linear-gradient(135deg,#007AFF,#5856D6)",padding:"48px 20px 20px"}}>
          <img src="/chat.png" alt="logo" style={{width:48,height:48,borderRadius:12,marginBottom:8}}/>
          <div style={{fontSize:22,fontWeight:800,color:"#fff"}}>JediChatAI</div>
          <div style={{fontSize:13,color:"rgba(255,255,255,0.8)"}}>AI WhatsApp Assistant</div>
        </div>
        <div style={{padding:"8px 0"}}>
          {menuSections.map(section=>(
            <div key={section.title}>
              <div style={{fontSize:11,fontWeight:700,color:COLORS.subtext,padding:"12px 16px 4px",letterSpacing:0.5}}>{section.title}</div>
              {section.items.map(item=>(
                <div key={item.path} onClick={()=>{navigate(item.path);onClose();}}
                  style={{display:"flex",alignItems:"center",gap:12,padding:"11px 16px",cursor:"pointer",background:currentPath===item.path?"#F0F7FF":"transparent",borderLeft:currentPath===item.path?"3px solid "+COLORS.primary:"3px solid transparent"}}>
                  <span style={{fontSize:20,width:28,textAlign:"center"}}>{item.icon}</span>
                  <span style={{fontSize:14,fontWeight:currentPath===item.path?700:400,color:currentPath===item.path?COLORS.primary:COLORS.text}}>{item.label}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div style={{padding:16,borderTop:"1px solid "+COLORS.border}}>
          <div style={{fontSize:11,color:COLORS.subtext,textAlign:"center"}}>JediChatAI v2.0.2 · jedichatai.com</div>
        </div>
      </div>
    </div>
  );
}

function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const quickTabs = [
    {path:"/", icon:<MessageCircle size={24}/>, label:"Chats"},
    {path:"/keyboard2", icon:<Keyboard size={24}/>, label:"AI Keys"},
    {path:"/analytics", icon:<BarChart2 size={24}/>, label:"Stats"},
    {path:"/settings", icon:<Settings size={24}/>, label:"Settings"},
  ];
  const doHaptic = () => { if ("vibrate" in navigator) navigator.vibrate(10); };
  return (
    <>
      <MenuDrawer open={menuOpen} onClose={()=>setMenuOpen(false)} navigate={navigate} currentPath={location.pathname}/>
      <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:480,background:COLORS.card,borderTop:"1px solid "+COLORS.border,display:"flex",paddingBottom:"env(safe-area-inset-bottom)",boxShadow:"0 -4px 20px rgba(0,0,0,0.08)",zIndex:100}}>
        <button onClick={()=>{ doHaptic(); setMenuOpen(true); }}
          style={{flex:1,background:"none",border:"none",cursor:"pointer",padding:"10px 0 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:4,WebkitTapHighlightColor:"transparent"}}>
          <Menu size={24} color={COLORS.subtext}/>
          <span style={{fontSize:10,color:COLORS.subtext}}>More</span>
        </button>
        {quickTabs.map(t=>{
          const active = location.pathname === t.path;
          return (
            <button key={t.path} onClick={()=>{ doHaptic(); navigate(t.path); }}
              style={{flex:1,background:"none",border:"none",cursor:"pointer",padding:"10px 0 8px",display:"flex",flexDirection:"column",alignItems:"center",gap:4,position:"relative",WebkitTapHighlightColor:"transparent"}}>
              {active && <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:32,height:3,borderRadius:2,background:COLORS.primary}}/>}
              <span style={{color:active?COLORS.primary:COLORS.subtext,display:"inline-block",transform:active?"scale(1.1)":"scale(1)",transition:"transform 0.15s"}}>{t.icon}</span>
              <span style={{fontSize:10,color:active?COLORS.primary:COLORS.subtext,fontWeight:active?700:400}}>{t.label}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}


export default 

function App() {

  const [loggedIn, setLoggedIn] = useState(!!localStorage.getItem("jedichatai_token"));
  const [whatsappConnected, setWhatsappConnected] = useState(true); // Always show app, check status inside
  const [onboarded, setOnboarded] = useState(!!localStorage.getItem("jedichatai_onboarded") || !!localStorage.getItem("jedichatai_token"));
  const darkModeState = useDarkModeState(); // triggers re-render on dark mode change

  // Offline detection
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  React.useEffect(() => {
    injectShimmer();
    registerSW();
    registerInstallPrompt();
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => { window.removeEventListener("online", handleOnline); window.removeEventListener("offline", handleOffline); };
  }, []);

  if (!loggedIn) return <LoginPage onLogin={() => setLoggedIn(true)} />;
  // Offline banner injected into app
  const OfflineBanner = () => !isOnline ? (
    <div style={{position:"fixed",top:0,left:0,right:0,background:"#FF3B30",color:"#fff",padding:"10px 16px",textAlign:"center",fontSize:13,fontWeight:700,zIndex:99999,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
      <span>📵</span> You are offline. Some features may not work
    </div>
  ) : null;
  if (!onboarded) return <OnboardingPage onComplete={() => { localStorage.setItem("jedichatai_onboarded", "true"); setOnboarded(true); }}/>;
  if (!whatsappConnected) return <WhatsAppConnectPage onConnected={() => { localStorage.setItem("whatsapp_connected", "true"); setWhatsappConnected(true); }} />;

  return (
    <BrowserRouter>
      <OfflineBanner/>
      <div style={{maxWidth:430,margin:"0 auto",minHeight:"100vh",background:COLORS.bg}}>
        <TopNav/>
        <Routes>
          <Route path="/" element={<ConversationsPage/>}/>
          <Route path="/keyboard" element={<KeyboardPage/>}/>
          <Route path="/templates" element={<TemplatesPage/>}/>
          <Route path="/history" element={<HistoryPage/>}/>
          <Route path="/sentiment" element={<SentimentPage/>}/>
          <Route path="/groups" element={<GroupsPage/>}/>
          <Route path="/schedule" element={<SchedulePage/>}/>
          <Route path="/team" element={<TeamPage/>}/>
          <Route path="/analytics" element={<AnalyticsPage/>}/>
          <Route path="/api" element={<APIPage/>}/>
          <Route path="/reports" element={<WeeklyReportPage/>}/>
          <Route path="/voice" element={<VoicePage/>}/>
          <Route path="/keyboard2" element={<ChatKeyboardPage/>}/>
          <Route path="/accounts" element={<AccountsPage/>}/>
          <Route path="/learn" element={<AILearningPage/>}/>
          <Route path="/waba" element={<WABAPage/>}/>
          <Route path="/templates" element={<TemplatesPage/>}/>
          <Route path="/history" element={<HistoryPage/>}/>
          <Route path="/sentiment" element={<SentimentPage/>}/>
          <Route path="/groups" element={<GroupsPage/>}/>
          <Route path="/schedule" element={<SchedulePage/>}/>
          <Route path="/team" element={<TeamPage/>}/>
          <Route path="/analytics" element={<AnalyticsPage/>}/>
          <Route path="/api" element={<APIPage/>}/>
          <Route path="/reports" element={<WeeklyReportPage/>}/>
          <Route path="/settings" element={<SettingsPage onLogout={() => { localStorage.clear(); setLoggedIn(false); }}/>}/>
          <Route path="/help" element={<HelpPage/>}/>
          <Route path="/feedback" element={<FeedbackPage/>}/>
          <Route path="/2fa" element={<TwoFactorPage/>}/>
          <Route path="/rules" element={<AutoReplyRulesPage/>}/>
          <Route path="/plans" element={<PlansPage/>}/>
              <Route path="*" element={<Navigate to="/" replace/>}/>
          <Route path="*" element={<Navigate to="/" replace/>}/>
        </Routes>
        <BottomNav/>
      </div>
    </BrowserRouter>
  );
}


// ============ VOICE CHAT PANEL (v3) ============
function VoiceChatPanel({ onTurn }) {
  const API = "https://app.jedichatai.com";
  const [connected, setConnected] = React.useState(false);
  const [status, setStatus] = React.useState("");
  const [left, setLeft] = React.useState(60);
  const [sessionsLeft, setSessionsLeft] = React.useState(null);
  const [lines, setLines] = React.useState([]);
  const pc = React.useRef(null);
  const mic = React.useRef(null);
  const timer = React.useRef(null);
  const audioEl = React.useRef(null);

  const stop = React.useCallback((why) => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    try { mic.current && mic.current.getTracks().forEach(t => t.stop()); } catch (e) {}
    try { pc.current && pc.current.close(); } catch (e) {}
    pc.current = null; mic.current = null;
    setConnected(false);
    setStatus(why || "");
  }, []);

  React.useEffect(() => { return () => stop(); }, [stop]);

  const start = async () => {
    try {
      setStatus("Connecting"); setLines([]);
      const token = localStorage.getItem("jedichatai_token");
      const res = await fetch(API + "/api/v1/voice/session", {
        method: "POST", headers: { Authorization: "Bearer " + token }
      });
      const data = await res.json();
      if (!res.ok) { setStatus(data.detail || "Could not start voice chat"); return; }
      const secret = data.value;
      if (typeof data.sessions_left === "number") setSessionsLeft(data.sessions_left);

      const peer = new RTCPeerConnection();
      pc.current = peer;
      peer.ontrack = (e) => {
        if (audioEl.current) { audioEl.current.srcObject = e.streams[0]; audioEl.current.play().catch(()=>{}); }
      };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mic.current = stream;
      stream.getTracks().forEach(t => peer.addTrack(t, stream));

      const ch = peer.createDataChannel("oai-events");
      // If the server opens its own channel instead of using ours, our
      // onmessage never fires. Attach the same handler to whichever arrives.
      peer.ondatachannel = (ev) => {
        console.log("[voice] server opened channel", ev.channel.label);
        ev.channel.onmessage = ch.onmessage;
      };
      ch.onclose = () => console.log("[voice] channel CLOSED");
      ch.onerror = (e) => console.log("[voice] channel ERROR", e);
      peer.onconnectionstatechange = () => console.log("[voice] connection:", peer.connectionState);
      ch.onopen = () => {
        console.log("[voice] channel OPEN, state:", ch.readyState);
        setConnected(true); setStatus("Listening");
        ch.send(JSON.stringify({ type: "session.update", session: {
          modalities: ["text", "audio"],
          output_modalities: ["text", "audio"],
          audio: { input: { transcription: { model: "whisper-1" } } },
          instructions: "You are the JediChatAI voice assistant. Be warm, helpful and concise. Sessions last about a minute, so keep answers brief unless asked for more."
        }}));
      };
      ch.onmessage = (e) => {
        const d = JSON.parse(e.data);
        console.log("[voice]", d.type, d);
        if (d.type === "response.output_audio_transcript.delta" || d.type === "response.audio_transcript.delta") {
          setLines(prev => {
            const i = prev.findIndex(m => m.role === "ai" && m.id === d.item_id);
            if (i >= 0) {
              const copy = prev.slice();
              copy[i] = { ...copy[i], text: copy[i].text + d.delta };
              return copy;
            }
            return prev.concat({ role:"ai", text: d.delta, id: d.item_id });
          });
        }
        if (d.type === "conversation.item.done" && d.item?.role === "user") {
          const t = (d.item.content || []).map(c => c.transcript || c.text || "").join(" ").trim();
          if (t) { setLines(prev => prev.concat({ role:"you", text: t })); if (onTurn) onTurn("you", t); }
        }
        if (d.type === "conversation.item.input_audio_transcription.completed" && d.transcript) {
          setLines(prev => prev.concat({ role:"you", text: d.transcript })); if (onTurn) onTurn("you", d.transcript);
        }
        if (d.type === "input_audio_buffer.speech_started") setStatus("Listening");
        if (d.type === "response.created") setStatus("Speaking");
        if (d.type === "response.output_audio_transcript.done" && d.item_id) {
          // Hand THIS item's finished text to the chat thread, so interleaved
          // turns cannot send the wrong one.
          setLines(prev => {
            const m = prev.find(x => x.role === "ai" && x.id === d.item_id);
            if (m && m.text && onTurn) onTurn("ai", m.text);
            return prev;
          });
        }
        if (d.type === "response.done") setStatus("Listening");
      };

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      const sdp = await fetch("https://api.openai.com/v1/realtime/calls", {
        method: "POST",
        headers: { Authorization: "Bearer " + secret, "Content-Type": "application/sdp" },
        body: offer.sdp
      }).then(r => { if (!r.ok) throw new Error("connect failed"); return r.text(); });
      await peer.setRemoteDescription({ type: "answer", sdp });

      setLeft(data.session_seconds || 60);
      timer.current = setInterval(() => {
        setLeft(v => { if (v <= 1) { stop("Session ended"); return 0; } return v - 1; });
      }, 1000);
    } catch (err) {
      console.error(err);
      stop("Could not connect. Check microphone permission.");
    }
  };

  return (
    <div style={{background:COLORS.card,borderRadius:16,padding:16,marginBottom:12,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
      <div style={{fontSize:12,fontWeight:700,color:COLORS.subtext,marginBottom:10}}>VOICE CHAT</div>
      <div style={{display:"flex",alignItems:"center",gap:12}}>
        <button onClick={() => connected ? stop("") : start()}
          style={{border:0,borderRadius:999,padding:"12px 22px",fontSize:14,fontWeight:700,
            cursor:"pointer",color:"#fff",background:connected?"#8E8E93":COLORS.primary}}>
          {connected ? "Stop" : "Start voice chat"}
        </button>
        <div style={{fontSize:13,color:left<=10&&connected?COLORS.danger:COLORS.subtext}}>
          {status}{connected ? "  ·  " + left + "s" : ""}
        </div>
      </div>
      {sessionsLeft !== null && (
        <div style={{fontSize:11,color:COLORS.subtext,marginTop:8}}>
          {sessionsLeft} session{sessionsLeft === 1 ? "" : "s"} left today · voice uses your daily limit faster
        </div>
      )}
      <audio ref={audioEl} autoPlay/>
      {lines.length > 0 && (
        <div style={{marginTop:12,background:COLORS.bg,borderRadius:12,padding:12,maxHeight:200,overflowY:"auto"}}>
          {lines.map((m,i)=>(
            <p key={i} style={{margin:"6px 0",fontSize:13,lineHeight:1.5}}>
              <b style={{color:m.role==="ai"?COLORS.primary:COLORS.text}}>{m.role==="ai"?"AI":"You"}:</b> {m.text}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}


// ============ CHAT-STYLE KEYBOARD (v3) ============
function ChatKeyboardPage() {
  const API = "https://app.jedichatai.com";
  const [thread, setThread] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem("jedichatai_chat_thread") || "[]"); }
    catch (e) { return []; }
  });
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [tone, setTone] = React.useState(localStorage.getItem("jedichatai_tone")||"friendly");
  const [showTone, setShowTone] = React.useState(false);
  const [copied, setCopied] = React.useState("");
  const endRef = React.useRef(null);
  const token = localStorage.getItem("jedichatai_token");
  const tones = ["professional","friendly","casual","formal","empathetic"];

  React.useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({behavior:"smooth"});
  }, [thread, busy]);

  // Keep the last 100 so the page reopens where you left off without
  // the stored blob growing forever.
  React.useEffect(() => {
    try { localStorage.setItem("jedichatai_chat_thread", JSON.stringify(thread.slice(-100))); }
    catch (e) {}
  }, [thread]);

  const push = (role, text, extra) => setThread(t => t.concat(Object.assign({role, text}, extra||{})));

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput(""); push("you", text); setBusy(true);
    const hasLink = /(https?:\/\/|www\.)[^\s]+/i.test(text);
    try {
      await getToken();
      if (hasLink) {
        push("note", "Link found. Checking it for fraud.");
        const lr = await fetch(API + "/api/v1/analyze/link", {
          method:"POST",
          headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
          body: JSON.stringify({text})
        }).then(r=>r.json());
        push("ai", lr.reply_suggestion || "I checked the link.", {risk: lr.overall_risk, results: lr.results});
      }
      // Send the thread so far so follow-ups like "make it shorter" work.
      // Only the user's own turns: feeding the model its own output back
      // made it start discussing the instructions instead of replying.
      const history = thread.filter(m => m.role === "you").slice(-6).map(m => ({
        who: "You", text: m.text
      }));
      const res = await fetch(API + "/api/v1/keyboard/suggest", {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify({message: text, tone, whatsapp_thread: history})
      });
      const d = await res.json();
      if (!res.ok) { push("note", d.detail || "Something went wrong."); }
      else {
        const list = d.suggestions || [];
        if (list.length === 0) push("note", "No reply generated. Try again.");
        else list.forEach(sg => push("ai", typeof sg === "string" ? sg : sg.text, {tone: sg.tone}));
      }
    } catch (e) {
      push("note", "Could not reach the server.");
    }
    setBusy(false);
  };

  const attach = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
    push("you", (isPdf ? "PDF: " : "Image: ") + file.name);
    push("note", isPdf ? "Reading the PDF." : "Analysing the image.");
    try {
      await getToken();
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = e => res(e.target.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const url = isPdf ? "/api/v1/analyze/pdf" : "/api/v1/analyze/image";
      const payload = isPdf ? {pdf_base64: b64} : {image_base64: b64, media_type: file.type||"image/jpeg"};
      const d = await fetch(API + url, {
        method:"POST",
        headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body: JSON.stringify(payload)
      }).then(r=>r.json());
      if (d.summary) push("ai", d.summary);
      if (d.description) push("ai", d.description);
      if (d.reply_suggestion) push("ai", d.reply_suggestion, {tone:"suggested reply"});
      if (!d.summary && !d.description && !d.reply_suggestion) push("note", "Nothing came back for that file.");
    } catch (e) {
      push("note", "Could not read that file.");
    }
    setBusy(false);
  };

  const bubble = (m, i) => {
    if (m.role === "note") return (
      <div key={i} style={{textAlign:"center",fontSize:12,color:COLORS.subtext,margin:"10px 0"}}>{m.text}</div>
    );
    const mine = m.role === "you";
    return (
      <div key={i} style={{display:"flex",justifyContent:mine?"flex-end":"flex-start",marginBottom:10}}>
        <div onClick={()=>{ if(!mine){navigator.clipboard.writeText(m.text);setCopied(m.text);setTimeout(()=>setCopied(""),1500);} }}
          style={{maxWidth:"78%",padding:"12px 14px",borderRadius:16,cursor:mine?"default":"pointer",
            background: mine ? COLORS.primary : COLORS.card,
            color: mine ? "#fff" : COLORS.text,
            border: mine ? "none" : "1px solid "+COLORS.border,
            boxShadow: mine ? "none" : "0 1px 4px rgba(0,0,0,0.05)"}}>
          {m.tone && <div style={{fontSize:10,fontWeight:700,opacity:.7,marginBottom:4,textTransform:"uppercase"}}>{m.tone}</div>}
          <div style={{fontSize:14,lineHeight:1.55,whiteSpace:"pre-wrap"}}>{m.text}</div>
          {m.risk && <div style={{fontSize:11,marginTop:6,fontWeight:700,
            color: m.risk==="high"?COLORS.danger:m.risk==="medium"?"#FF9500":COLORS.success}}>
            {m.risk==="high"?"HIGH RISK - likely fraud":m.risk==="medium"?"MEDIUM RISK - be careful":"Link looks safe"}</div>}
          {!mine && copied===m.text && <div style={{fontSize:10,color:COLORS.success,marginTop:4,fontWeight:700}}>COPIED</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100vh",background:COLORS.bg}}>
      <div style={{background:COLORS.card,padding:"14px 18px",borderBottom:"1px solid "+COLORS.border,
        display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{fontSize:18,fontWeight:800,color:COLORS.primary}}>AI Keyboard</div>
        <button onClick={()=>{setThread([]);localStorage.removeItem("jedichatai_chat_thread");}} style={{border:0,background:"none",color:COLORS.subtext,
          fontSize:12,cursor:"pointer"}}>Clear</button>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"16px 14px 8px"}}>
        {thread.length===0 && (
          <div style={{textAlign:"center",color:COLORS.subtext,marginTop:60,padding:"0 30px"}}>
            <div style={{fontSize:17,fontWeight:700,color:COLORS.text,marginBottom:8}}>What can I help with?</div>
            <div style={{fontSize:13,lineHeight:1.6}}>Paste a message to get a reply, share a link to check it,
            or attach a PDF or image. Ask me to change anything afterwards.</div>
          </div>
        )}
        {thread.map(bubble)}
        {busy && <div style={{textAlign:"center",fontSize:12,color:COLORS.subtext,margin:"8px 0"}}>Thinking...</div>}
        <div ref={endRef}/>
      </div>

      {showTone && (
        <div style={{display:"flex",gap:6,padding:"0 14px 8px",flexWrap:"wrap"}}>
          {tones.map(t=>(
            <button key={t} onClick={()=>{setTone(t);localStorage.setItem("jedichatai_tone",t);setShowTone(false);}}
              style={{border:"1px solid "+(t===tone?COLORS.primary:COLORS.border),background:t===tone?COLORS.primary:COLORS.card,
                color:t===tone?"#fff":COLORS.text,borderRadius:999,padding:"6px 12px",fontSize:12,cursor:"pointer"}}>
              {t.charAt(0).toUpperCase()+t.slice(1)}
            </button>
          ))}
        </div>
      )}

      <div style={{padding:"0 14px"}}><VoiceChatPanel onTurn={(role,text)=>push(role,text,{via:"voice"})}/></div>
      <div style={{padding:"10px 14px calc(76px + env(safe-area-inset-bottom))",background:COLORS.bg}}>
        <div style={{background:COLORS.card,border:"1px solid "+COLORS.border,borderRadius:22,
          padding:"10px 12px",boxShadow:"0 2px 10px rgba(0,0,0,0.06)"}}>
          <textarea value={input} onChange={e=>setInput(e.target.value)}
            onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}}
            placeholder="Paste a message, or ask me anything"
            style={{width:"100%",border:"none",outline:"none",resize:"none",minHeight:44,maxHeight:140,
              fontSize:14,fontFamily:"inherit",background:"transparent",color:COLORS.text,lineHeight:1.5}}/>
          <div style={{display:"flex",alignItems:"center",gap:8,marginTop:4}}>
            <label style={{cursor:"pointer",fontSize:12,color:COLORS.subtext,border:"1px solid "+COLORS.border,
              borderRadius:999,padding:"5px 11px"}}>
              Attach
              <input type="file" accept=".pdf,image/*" style={{display:"none"}}
                onChange={e=>e.target.files[0]&&attach(e.target.files[0])}/>
            </label>
            <button onClick={()=>setShowTone(v=>!v)} style={{cursor:"pointer",fontSize:12,color:COLORS.subtext,
              border:"1px solid "+COLORS.border,borderRadius:999,padding:"5px 11px",background:COLORS.card}}>
              {tone.charAt(0).toUpperCase()+tone.slice(1)}
            </button>
            <div style={{flex:1}}/>
            <button onClick={send} disabled={busy||!input.trim()}
              style={{border:0,borderRadius:"50%",width:36,height:36,cursor:"pointer",fontSize:16,
                background: (busy||!input.trim()) ? COLORS.border : COLORS.primary, color:"#fff"}}>↑</button>
          </div>
        </div>
      </div>
    </div>
  );
}
